import { R, Toy, type Ramp } from "./toy.ts";
import type { SpeciesId } from "../data/species.ts";
import { FRONTIER_PAINT, type FrontierId } from "./frontierIndex.ts";
import { UNDERNET_PAINT } from "./undernet.ts";
import { SIDECHAIN_PAINT, type SidechainId } from "./sidechain.ts";

/**
 * Creature painters on the vinyl-toy rig (toy.ts): rounded, key-lit volumes with rim light,
 * glossy eyes and each species' signature accessory, in the locked 32-colour LCD master.
 * Frames are 32×32 with feet on row 29; fronts face the camera, backs are the same volumes
 * seen from behind. Original IP only.
 */
export type Pose = "front" | "back";
type Paint = (t: Toy, back: boolean) => void;

import { INK, WHITE, eyeBig, eyeBead, eyeHollow, eyeDot, eyeSleepy, brow, visor, nose, smile, frown, grin, teeth, earPoint, paws, crown, flame, arc, hoop, eyeGlow, slat, keyhole, blade, bubble, rivet } from "./parts.ts";
// ---------------------------------------------------------------------------
// species
// ---------------------------------------------------------------------------

const bogfiend: Paint = (t, back) => {
  for (const x of [10, 22]) t.ell(x, 28, 4, 2, R.green, { spec: false, shade: 1 });
  t.ell(16, 20, 12, 8, R.cream);
  for (let x = 5; x <= 27; x += 2) t.px(x, 28, R.cream, 3);
  if (back) {
    t.vline(16, 14, 13, R.cream, 3);
    t.ring(16, 21, 4, 3, R.cream, 3);
    t.px(16, 21, R.cream, 4);
    t.ell(16, 11, 9, 6.5, R.green);
    for (const x of [10.5, 21.5]) t.ell(x, 6.5, 3.5, 3, R.green);
    t.ell(16, 8, 4, 2, R.green, { shade: 1, occ: false, rim: false, spec: false });
    return;
  }
  t.ell(16, 22, 6, 6, R.green, { shade: -1 });
  t.poly([[5, 15], [11, 17], [9, 28], [4, 26]], R.cream, { grad: true });
  t.poly([[27, 15], [21, 17], [23, 28], [28, 26]], R.cream, { grad: true });
  t.rect(6, 21, 3, 3, R.paper, 1);
  t.hline(6, 22, 2, INK, 1);
  t.px(6, 21, WHITE);
  for (const x of [9, 23]) t.cap(x, 18, x, 22, 1.5, R.green, { spec: false });
  t.ell(16, 11, 9, 6.5, R.green);
  for (const x of [10.5, 21.5]) t.ell(x, 6.5, 3.5, 3, R.green);
  eyeBead(t, 9, 5);
  eyeBead(t, 20, 5);
  t.px(14, 11, INK, 1);
  t.px(17, 11, INK, 1);
  t.px(10, 13, INK, 1);
  t.hline(11, 14, 10, INK, 1);
  t.px(21, 13, INK, 1);
  t.lighten(8, 12);
  t.lighten(24, 12);
};

const bogbaron: Paint = (t, back) => {
  for (const x of [9, 23]) t.ell(x, 28, 4.5, 2, R.green, { spec: false, shade: 1 });
  t.vline(27, 8, 21, R.bark, 1);
  t.px(27, 7, R.gold, 0);
  t.px(27, 8, R.gold, 1);
  t.px(27, 11, WHITE);
  t.ell(16, 21, 13, 9, R.cream);
  for (let x = 4; x <= 28; x += 2) { t.px(x, 27, R.cream, 3); t.px(x + 1, 29, R.cream, 3); }
  if (back) {
    t.vline(16, 13, 14, R.cream, 3);
    t.ring(16, 21, 5, 4, R.cream, 3);
    t.ell(16, 21, 2, 1.5, R.green, { flat: 1 });
    t.ell(16, 11, 10, 7, R.green);
    for (const x of [10, 22]) t.ell(x, 6, 4, 3.5, R.green);
  } else {
    t.ell(16, 22, 7, 6, R.green, { shade: -1 });
    t.poly([[4, 15], [11, 17], [9, 29], [3, 27]], R.cream, { grad: true });
    t.poly([[28, 15], [21, 17], [23, 29], [29, 27]], R.cream, { grad: true });
    t.rect(6, 20, 3, 3, R.gold, 1);
    t.px(7, 21, R.gold, 0);
    t.px(6, 20, WHITE);
    for (const x of [9, 23]) t.cap(x, 18, x, 23, 1.6, R.green, { spec: false });
    t.ell(16, 11, 10, 7, R.green);
    for (const x of [10, 22]) t.ell(x, 6, 4, 3.5, R.green);
    eyeBig(t, 8, 4);
    eyeBig(t, 20, 4);
    t.px(14, 12, INK, 1);
    t.px(18, 12, INK, 1);
    t.px(9, 14, INK, 1);
    t.hline(10, 15, 12, INK, 1);
    t.px(22, 14, INK, 1);
    t.lighten(7, 13);
    t.lighten(25, 13);
  }
  for (const x of [10, 13, 16, 19, 22]) {
    t.vline(x, 2, 3, R.moss, 1);
    t.px(x, 1, R.orange, 1);
  }
};

const shibonk: Paint = (t, back) => {
  if (back) {
    t.ell(16, 23, 8, 6, R.orange);
    t.cap(8, 17, 24, 25, 1, R.bark, { spec: false });
    t.px(16, 21, R.gold, 0);
    t.px(17, 21, R.gold, 0);
    paws(t, 16, 5, 3, R.orange);
    t.ell(25, 21, 4, 4, R.orange);
    t.ell(25.5, 21.5, 2, 2, R.cream, { flat: 1, occ: false });
    t.ell(16, 12, 9, 8, R.orange);
    earPoint(t, 11, 9, 11, 1, R.orange, R.orange, 3);
    earPoint(t, 21, 9, 21, 1, R.orange, R.orange, 3);
    t.hline(9, 20, 14, R.orange, 3);
    return;
  }
  t.ell(25, 20, 3.5, 3.5, R.orange);
  t.ell(25.5, 20.5, 1.5, 1.5, R.cream, { flat: 1, occ: false });
  t.ell(16, 23, 8, 6, R.orange);
  t.ell(16, 24, 5, 4.5, R.cream, { dome: 0.5, rim: false, spec: false });
  paws(t, 16, 5, 3, R.orange);
  t.cap(9, 26, 23, 18, 1, R.bark, { spec: false });
  t.px(16, 22, R.gold, 0);
  t.px(17, 21, R.gold, 0);
  t.px(16, 21, WHITE);
  t.ell(16, 12, 9, 8, R.orange);
  earPoint(t, 11, 9, 11, 1, R.orange, R.cream, 3);
  earPoint(t, 21, 9, 21, 1, R.orange, R.cream, 3);
  t.ell(16, 15.5, 5, 3.5, R.cream, { dome: 0.5, rim: false, spec: false });
  nose(t, 15, 14);
  smile(t, 14, 17, 5);
  eyeBig(t, 10, 9);
  eyeBig(t, 18, 9);
};

const shibonkx: Paint = (t, back) => {
  if (!back) {
    t.cap(5, 19, 12, 6, 1.2, R.bark, { spec: false });
    t.cap(27, 19, 20, 6, 1.2, R.bark, { spec: false });
    t.px(12, 5, R.gold, 0); t.px(20, 5, R.gold, 0);
    t.px(12, 6, WHITE); t.px(20, 6, WHITE);
  }
  t.ell(16, 22, 9, 7, R.orange);
  if (back) {
    t.cap(6, 26, 14, 12, 1.2, R.bark, { spec: false });
    t.cap(26, 26, 18, 12, 1.2, R.bark, { spec: false });
    t.px(14, 11, R.gold, 0); t.px(18, 11, R.gold, 0);
  } else {
    t.ell(16, 23, 5.5, 5, R.cream, { dome: 0.5, rim: false, spec: false });
  }
  paws(t, 16, 5.5, 3.2, R.orange);
  t.ell(26, 19, 4, 4, R.orange);
  if (back) t.ell(26.5, 19.5, 2, 2, R.cream, { flat: 1, occ: false });
  t.ell(16, 11, 10, 8.5, R.orange);
  t.hline(11, 18, 10, R.slate, 1);
  t.px(16, 19, R.gold, 0);
  earPoint(t, 10, 8, 9, 1, R.orange, back ? R.orange : R.cream, 3);
  earPoint(t, 22, 8, 23, 1, R.orange, back ? R.orange : R.cream, 3);
  t.px(9, 1, INK, 1); t.px(23, 1, INK, 1);
  if (back) return;
  t.ell(16, 14.5, 5.5, 3.8, R.cream, { dome: 0.5, rim: false, spec: false });
  nose(t, 15, 13);
  smile(t, 14, 16, 5);
  eyeBig(t, 9, 8);
  eyeBig(t, 19, 8);
  brow(t, 9, 8, 1);
  brow(t, 19, 8, -1);
};

const wojakle: Paint = (t, back) => {
  paws(t, 16, 4.5, 3, R.navy);
  t.ell(16, 23, 8, 7, R.navy);
  if (back) {
    t.ell(16, 11, 9, 8, R.navy);
    t.vline(16, 4, 8, R.navy, 3);
    t.px(16, 3, R.rose, 1);
    t.px(20, 8, R.ice, 1);
    for (const x of [9, 23]) t.cap(x, 20, x, 25, 1.8, R.cream, { spec: false });
    return;
  }
  t.ell(16, 11, 9, 8, R.navy);
  t.rect(10, 4, 12, 3, R.navy, 2);
  t.hline(11, 4, 10, R.navy, 1);
  t.px(16, 3, R.rose, 1);
  t.px(16, 2, R.rose, 0);
  t.ell(16, 12, 6.5, 6, R.cream, { dome: 0.6, spec: false });
  t.hline(11, 9, 3, INK, 1);
  t.hline(18, 9, 3, INK, 1);
  eyeBead(t, 11, 10);
  eyeBead(t, 18, 10);
  t.px(12, 14, R.ice, 1);
  t.px(12, 15, R.ice, 2);
  frown(t, 14, 17, 4);
  t.cap(10, 21, 10, 25, 1.8, R.cream, { spec: false });
  t.cap(22, 21, 22, 24, 1.8, R.cream, { spec: false });
  t.rect(20, 19, 4, 6, R.slate, 1);
  t.rect(21, 20, 2, 4, R.paper, 0);
  t.px(21, 22, R.rose, 1);
  t.px(22, 21, R.rose, 1);
  t.px(21, 20, WHITE);
};

const wojaked: Paint = (t, back) => {
  paws(t, 16, 5, 3.2, R.navy);
  t.ell(16, 22, 9, 7, R.navy);
  t.ring(16, 22, 6, 4, R.slate, 0);
  for (const x of [8, 24]) t.ell(x, 17, 3, 2.5, R.slate, { spec: false });
  if (back) {
    t.ell(16, 10, 10, 8.5, R.navy);
    t.ring(16, 10, 10, 8.5, R.slate, 0);
    t.vline(16, 3, 8, R.navy, 3);
    t.px(16, 2, R.rose, 1);
    t.rect(22, 21, 3, 5, R.ice, 1);
    return;
  }
  t.rect(10, 2, 12, 3, R.navy, 2);
  t.px(16, 1, R.rose, 1);
  t.px(16, 0, R.rose, 0);
  t.ell(16, 10, 10, 8.5, R.navy);
  arc(t, 16, 10, 10, 8.5, Math.PI * 1.05, Math.PI * 1.95, R.slate, 0);
  t.ell(16, 12, 6.5, 6, R.cream, { dome: 0.6, spec: false });
  t.hline(11, 9, 3, INK, 1);
  t.hline(18, 9, 3, INK, 1);
  eyeBead(t, 11, 10);
  eyeBead(t, 18, 10);
  t.px(12, 14, R.ice, 1);
  t.px(12, 15, R.ice, 2);
  frown(t, 14, 17, 4);
  t.rect(22, 21, 3, 5, R.ice, 1);
  t.px(23, 20, R.orange, 1);
  t.px(23, 23, R.ice, 0);
  t.px(22, 21, WHITE);
  t.cap(9, 21, 9, 25, 1.8, R.cream, { spec: false });
};

const gigachomp: Paint = (t, back) => {
  paws(t, 16, 6.5, 3.5, R.brown);
  // Boulder shoulders, then the head block whose jaw is wider than its crown.
  t.ell(16, 24, 12, 6.5, R.brown);
  for (const x of [8, 24]) t.ell(x, 20, 4, 3, R.brown, { spec: false });
  if (back) {
    // Seen from behind this is still a stack, so it has to read as layers rather than one brown
    // mass: the crown bun, the filling squeezed out between, the base bun, and the seam down it.
    t.ell(16, 10, 9, 7, R.brown);
    t.ell(16, 15.5, 12, 4.5, R.brown, { shade: 1 });
    t.hline(5, 14, 22, R.orange, 2);
    // Seeds on the crown and a frill of leaf squeezed out at the back of the stack, because the
    // only things this toy shows you from behind are the two it is made of.
    for (const [x, y] of [[11, 6], [16, 5], [20, 7], [13, 9], [22, 10]] as const) {
      t.px(x, y, R.cream, 1);
      t.darken(x, y + 1);
    }
    for (let x = 5; x < 27; x += 3) t.px(x, 13, R.moss, x % 2 ? 1 : 0);
    for (const x of [6, 26]) t.ell(x, 9, 2, 2.5, R.brown, { spec: false });
    t.vline(16, 19, 9, R.brown, 4);
    t.px(15, 19, R.brown, 1);
    for (const x of [10, 22]) t.vline(x, 20, 6, R.brown, 3);
    return;
  }
  for (const x of [11, 21]) t.ell(x, 22, 4, 2.5, R.brown, { shade: -1, spec: false });
  t.ell(16, 10, 9, 7, R.brown);
  for (const x of [6, 26]) {
    t.ell(x, 9, 2, 2.5, R.brown, { spec: false });
    t.px(x, 9, R.orange, 1);
  }
  t.ell(16, 15.5, 12, 4.5, R.orange, { shade: -1 });
  t.hline(9, 7, 14, INK, 1);
  for (let x = 10; x <= 22; x++) t.darken(x, 6);
  eyeSleepy(t, 10, 8);
  eyeSleepy(t, 19, 8);
  t.vline(16, 10, 3, R.orange, 2);
  t.hline(15, 13, 3, R.orange, 3);
  teeth(t, 9, 16, 14);
  t.px(16, 15, WHITE);
  t.darken(16, 18);
  t.darken(16, 19);
  t.hline(11, 19, 3, R.orange, 3);
  t.hline(18, 19, 3, R.orange, 3);
};

const laserpup: Paint = (t, back) => {
  t.cap(23, 21, 26, 18, 1.5, R.cream, { spec: false });
  paws(t, 16, 5, 3, R.cream);
  t.ell(16, 24, 7, 5.5, R.cream);
  t.hline(11, 19, 10, R.navy, 2);
  t.px(16, 20, R.gold, 0);
  t.ell(16, 13, 8.5, 7.5, R.cream);
  earPoint(t, 10, 8, 9, 2, R.cream, back ? R.paper : R.rose, 3);
  earPoint(t, 22, 8, 23, 2, R.cream, back ? R.paper : R.rose, 3);
  t.vline(16, 2, 4, R.slate, 1);
  t.px(16, 2, R.rose, 1);
  t.px(16, 1, WHITE);
  if (back) {
    // The back was the front with the face left off, so head and body fused into one cream mass.
    // A nape crease puts the head back on top of the shoulders, the spine gives the mass a
    // centre, and the ear shells go dark because from here you are looking at their outsides.
    t.cap(10, 19, 22, 19, 1.6, R.cream, { shade: 1, spec: false });
    t.vline(16, 20, 7, R.cream, 3);
    for (const x of [10, 22]) t.ell(x, 7, 2.2, 2.6, R.cream, { flat: 3, occ: false });
    return;
  }
  visor(t, 9, 11, 14);
  nose(t, 15, 16);
  smile(t, 14, 18, 5);
};

const laserhound: Paint = (t, back) => {
  t.cap(23, 20, 28, 15, 1.5, R.ice, { spec: false });
  for (const x of [11, 21]) t.cap(x, 23, x, 28, 2.2, R.ice, { spec: false });
  t.ell(16, 21, 7, 7, R.ice);
  t.ell(16, 10, 8, 7, R.ice);
  t.cap(15, 4, 24, 1, 1.2, R.navy, { spec: false });
  earPoint(t, 10, 6, 8, 1, R.ice, undefined, 2.5);
  earPoint(t, 22, 6, 24, 1, R.ice, undefined, 2.5);
  t.px(8, 1, R.navy, 2); t.px(24, 1, R.navy, 2);
  const collar: Ramp[] = [R.rose, R.gold, R.blue, R.ice];
  for (let x = 10; x <= 22; x++) t.px(x, 16, collar[x % 4]!, x % 4 === 2 ? 1 : 0);
  t.px(13, 16, WHITE);
  if (back) return;
  visor(t, 9, 8, 14);
  nose(t, 15, 13);
  smile(t, 14, 15, 5);
};

const whalord: Paint = (t, back) => {
  for (const x of [11, 21]) t.ell(x, 28, 4, 1.8, R.blue, { spec: false });
  t.poly([[6, 16], [3, 19], [7, 21]], R.blue, { grad: true });
  t.poly([[26, 16], [29, 19], [25, 21]], R.blue, { grad: true });
  t.ell(16, 17, 10, 11, R.blue);
  t.px(8, 12, R.blue, 0); t.px(24, 10, R.blue, 0); t.px(9, 22, R.blue, 0);
  if (back) {
    t.vline(16, 7, 16, R.blue, 3);
    t.px(22, 14, R.blue, 0);
  } else {
    t.ell(16, 20, 6, 8, R.ice, { dome: 0.4, rim: false, spec: false });
    for (let y = 16; y <= 26; y += 3) for (let x = 12; x <= 20; x++) t.darken(x, y);
    eyeBig(t, 9, 12);
    eyeBig(t, 19, 12);
    t.px(11, 18, INK, 1);
    t.hline(12, 19, 8, INK, 1);
    t.px(20, 18, INK, 1);
  }
  crown(t, 16, 1, 8);
  t.px(16, 1, WHITE);
};

const apeling: Paint = (t, back) => {
  paws(t, 16, 5, 3.5, R.brown);
  t.cap(9, 19, 3, 14, 2.2, R.brown, { spec: false });
  t.cap(23, 19, 28, 15, 2.2, R.brown, { spec: false });
  t.ell(16, 22, 8, 7, R.brown);
  if (!back) t.ell(16, 23, 5, 5, R.orange, { dome: 0.5, rim: false, spec: false });
  t.ell(3, 13, 2.5, 2.5, R.brown, { spec: false });
  t.cap(2, 16, 5, 9, 1.2, R.gold, { spec: false });
  t.px(5, 9, R.bark, 2);
  t.px(4, 10, R.gold, 0);
  t.px(3, 13, WHITE);
  t.ell(28, 15, 2.5, 3, back ? R.brown : R.orange, { spec: false });
  if (!back) for (const y of [13, 15, 17]) t.darken(27, y);
  t.ell(16, 11, 8.5, 7.5, R.brown);
  for (const x of [7, 25]) {
    t.ell(x, 11, 2.5, 3, R.brown, { spec: false });
    if (!back) t.ell(x, 11, 1.2, 1.5, R.orange, { flat: 1, occ: false });
  }
  if (back) return;
  t.ell(16, 13, 6, 5.5, R.orange, { dome: 0.5, spec: false });
  for (let x = 10; x <= 22; x++) t.darken(x, 8);
  eyeBig(t, 11, 9);
  eyeBig(t, 18, 9);
  grin(t, 13, 16, 6, 4);
};

const apemajor: Paint = (t, back) => {
  paws(t, 16, 6, 4, R.brown);
  t.cap(8, 18, 4, 23, 2.6, R.brown, { spec: false });
  t.cap(24, 18, 28, 23, 2.6, R.brown, { spec: false });
  t.ell(16, 21, 10, 8, R.brown);
  t.cap(6, 15, 10, 13, 1.5, R.gold, { spec: false });
  t.cap(26, 15, 22, 13, 1.5, R.gold, { spec: false });
  t.ell(16, 10, 9, 8, R.brown);
  for (const x of [7, 25]) t.ell(x, 10, 2.5, 3, R.brown, { spec: false });
  if (back) return;
  t.ell(16, 22, 5.5, 5.5, R.orange, { dome: 0.5, rim: false, spec: false });
  t.px(16, 18, R.gold, 0);
  t.ring(16, 18, 1, 1, R.gold, 1);
  t.ell(16, 12, 6.5, 6, R.orange, { dome: 0.5, spec: false });
  for (let x = 10; x <= 22; x++) t.darken(x, 7);
  eyeBig(t, 10, 8);
  eyeBig(t, 18, 8);
  brow(t, 10, 8, 1);
  brow(t, 18, 8, -1);
  t.hline(13, 16, 6, INK, 1);
  t.px(14, 17, WHITE);
  t.px(18, 17, WHITE);
};

const rugfox: Paint = (t, back) => {
  t.cap(22, 24, 27, 17, 2.5, R.red, { spec: false });
  t.ell(27, 16, 2.2, 2.2, R.cream, { spec: false });
  t.rect(26, 14, 3, 3, R.violet, 1);
  t.px(26, 13, R.slate, 1);
  t.px(28, 13, R.slate, 1);
  t.px(27, 14, R.gold, 0);
  t.px(26, 14, WHITE);
  paws(t, 16, 5, 3, R.red, { shade: 1 });
  t.ell(16, 23, 7.5, 6, R.red);
  if (!back) t.ell(16, 24, 4.5, 4, R.cream, { dome: 0.5, rim: false, spec: false });
  t.ell(16, 12, 8.5, 7, R.red);
  earPoint(t, 11, 8, 10, 1, R.red, back ? R.red : R.cream, 3);
  earPoint(t, 21, 8, 22, 1, R.red, back ? R.red : R.cream, 3);
  t.px(10, 1, INK, 1); t.px(22, 1, INK, 1);
  t.cap(9, 18, 23, 18, 2, R.cream, { spec: false });
  if (back) {
    t.ell(16, 18, 2.5, 2, R.cream, { shade: 1, spec: false });
    return;
  }
  t.cap(21, 19, 24, 25, 1.5, R.cream, { spec: false });
  t.px(24, 27, R.cream, 3);
  t.ell(16, 15, 5, 3.2, R.cream, { dome: 0.5, rim: false, spec: false });
  nose(t, 15, 14);
  t.px(13, 17, INK, 1);
  t.hline(14, 18, 4, INK, 1);
  t.px(18, 17, INK, 1);
  t.px(19, 16, INK, 1);
  eyeSleepy(t, 10, 9);
  eyeSleepy(t, 19, 9);
};

const rugwolf: Paint = (t, back) => {
  t.cap(22, 24, 28, 16, 2.6, R.red, { spec: false });
  t.rect(26, 13, 3, 3, R.violet, 1);
  for (const x of [26, 27, 28]) t.px(x, 12, R.slate, 1);
  t.px(26, 13, WHITE);
  paws(t, 16, 6, 3.5, R.red, { shade: 1 });
  t.ell(16, 22, 9, 7, R.red);
  if (!back) t.ell(16, 23, 5, 5, R.cream, { dome: 0.5, rim: false, spec: false });
  t.ell(16, 11, 9.5, 8, R.red);
  earPoint(t, 10, 7, 8, 0.5, R.red, back ? R.red : R.cream, 3);
  earPoint(t, 22, 7, 24, 0.5, R.red, back ? R.red : R.cream, 3);
  // Woven mane: a checked rug collar.
  for (let x = 7; x <= 25; x++) for (let y = 16; y <= 19; y++) {
    if ((x - 7) * (x - 25) > -12 && y === 16) continue;
    t.px(x, y, (x + y) % 2 ? R.red : R.orange, (x + y) % 2 ? 2 : 1);
  }
  if (back) {
    t.rect(11, 20, 10, 6, R.orange, 1);
    for (let x = 11; x < 21; x++) for (let y = 20; y < 26; y++) if ((x + y) % 2) t.px(x, y, R.red, 2);
    return;
  }
  t.ell(16, 14, 5.5, 3.5, R.cream, { dome: 0.5, rim: false, spec: false });
  nose(t, 15, 13);
  teeth(t, 12, 17, 8);
  eyeSleepy(t, 9, 8);
  eyeSleepy(t, 20, 8);
  brow(t, 9, 8, 1);
  brow(t, 20, 8, -1);
};

const moonling: Paint = (t, back) => {
  t.ring(16, 19, 13, 4, R.gold, 1);
  for (const x of [12, 20]) t.ell(x, 27.5, 2.5, 1.8, R.stone, { spec: false, shade: 1 });
  t.ell(16, 16, 9.5, 9.5, R.stone);
  for (const [x, y, rx, ry] of back ? [[10, 14, 2, 1.5], [20, 11, 1.5, 1], [17, 21, 2, 1.5], [12, 22, 1, 1]] : [[10, 20, 1.5, 1], [22, 12, 1.2, 1], [20, 22, 1, 1]]) {
    t.ell(x!, y!, rx!, ry!, R.stone, { flat: 2, occ: false });
    t.px(x!, y! - 1, R.stone, 3);
  }
  arc(t, 16, 19, 13, 4, 0, Math.PI, R.gold, 0);
  if (back) return;
  eyeBig(t, 11, 12);
  eyeBig(t, 18, 12);
  smile(t, 14, 20, 5);
  t.px(19, 19, INK, 1);
  t.vline(24, 7, 4, R.bark, 1);
  t.rect(25, 7, 3, 2, R.rose, 1);
  t.px(26, 7, WHITE);
};

const moonlord: Paint = (t, back) => {
  t.ring(16, 18, 13, 5, R.gold, 1);
  for (const x of [12, 20]) t.ell(x, 27.5, 3, 2, R.stone, { spec: false, shade: 1 });
  t.ell(16, 15, 11, 11, R.stone);
  for (const [x, y, rx, ry] of back ? [[9, 13, 2.5, 1.8], [21, 9, 1.8, 1.2], [18, 22, 2, 1.5], [11, 22, 1.2, 1]] : [[8, 19, 1.5, 1], [23, 11, 1.5, 1], [21, 23, 1.2, 1], [11, 24, 1, 1]]) {
    t.ell(x!, y!, rx!, ry!, R.stone, { flat: 2, occ: false });
    t.px(x!, y! - 1, R.stone, 3);
  }
  arc(t, 16, 18, 13, 5, 0, Math.PI, R.gold, 0);
  t.vline(20, 1, 5, R.bark, 1);
  t.rect(21, 1, 4, 3, R.rose, 1);
  t.px(22, 2, R.gold, 0);
  t.px(21, 1, WHITE);
  if (back) return;
  eyeBig(t, 10, 11);
  eyeBig(t, 18, 11);
  brow(t, 10, 11, 1);
  brow(t, 18, 11, -1);
  frown(t, 13, 20, 6);
  t.px(12, 19, INK, 1);
  t.px(19, 19, INK, 1);
};

const chopperdog: Paint = (t, back) => {
  t.cap(22, 22, 25, 19, 1.4, R.moss, { spec: false });
  paws(t, 16, 5, 3, R.moss);
  t.ell(16, 24, 7.5, 5.5, R.moss);
  if (!back) t.ell(16, 25, 4.5, 4, R.cream, { dome: 0.5, rim: false, spec: false });
  t.rect(20, 21, 5, 5, R.bark, 1);
  t.hline(20, 21, 5, R.bark, 0);
  for (const x of [21, 23]) t.vline(x, 19, 2, R.paper, 0);
  t.line(12, 18, 20, 22, R.bark, 2);
  t.ell(16, 13, 8.5, 7, R.moss);
  t.cap(8, 10, 6, 16, 2, R.moss, { spec: false });
  t.cap(24, 10, 26, 16, 2, R.moss, { spec: false });
  t.vline(16, 4, 3, R.slate, 1);
  t.px(16, 5, R.gold, 1);
  t.hline(8, 3, 17, R.gold, 0);
  t.px(8, 3, R.gold, 1); t.px(24, 3, R.gold, 1);
  t.px(8, 2, WHITE); t.px(24, 2, WHITE);
  if (back) return;
  eyeBig(t, 11, 10);
  eyeBig(t, 18, 10);
  nose(t, 15, 15);
  t.rect(13, 17, 6, 3, R.paper, 0);
  t.px(14, 18, INK, 1); t.px(16, 18, INK, 1);
  t.hline(13, 17, 6, INK, 1);
};

const chopperalpha: Paint = (t, back) => {
  t.cap(24, 22, 28, 18, 1.6, R.moss, { spec: false });
  paws(t, 16, 5.5, 3.4, R.moss);
  t.ell(16, 23, 9, 6.5, R.moss);
  if (back) {
    t.line(9, 17, 16, 25, R.gold, 1);
    t.line(23, 17, 16, 25, R.gold, 1);
  } else {
    t.ell(16, 22, 5, 5, R.gold, { dome: 0.5, rim: false, spec: false });
    t.line(9, 17, 12, 22, R.gold, 2);
  }
  t.rect(22, 20, 5, 4, R.paper, 1);
  t.ring(24, 22, 1.5, 1.5, R.paper, 2);
  t.ell(16, 12, 9.5, 8, R.moss);
  t.cap(7, 9, 5, 16, 2.2, R.moss, { spec: false });
  t.cap(25, 9, 27, 16, 2.2, R.moss, { spec: false });
  for (const x of [10, 22]) {
    t.vline(x, 3, 3, R.slate, 1);
    t.px(x, 4, R.gold, 1);
    t.hline(x - 6, 2, 13, R.gold, 0);
    t.px(x, 2, WHITE);
  }
  if (back) return;
  eyeBig(t, 10, 9);
  eyeBig(t, 18, 9);
  brow(t, 10, 9, 1);
  brow(t, 18, 9, -1);
  nose(t, 15, 14);
  teeth(t, 13, 17, 6);
};

const cloutling: Paint = (t, back) => {
  const wing = back ? R.blue : R.ice;
  for (const x of [9, 23]) {
    t.ell(x, 14, 6, 5.5, wing, { spec: false });
    t.ring(x, 14, 2, 2, R.blue, 2);
    t.px(x, 14, R.blue, 1);
  }
  for (const x of [10, 22]) t.ell(x, 21, 4.5, 3.5, R.blue, { shade: 1, spec: false });
  if (back) t.ring(16, 24, 6, 5, R.gold, 1);
  t.ell(16, 19, 3.5, 6, R.navy);
  t.ell(16, 11, 4.5, 4, R.navy);
  t.line(14, 8, 12, 4, R.slate, 1);
  t.line(18, 8, 20, 4, R.slate, 1);
  t.px(12, 4, R.gold, 0); t.px(20, 4, R.gold, 0);
  if (back) return;
  eyeBead(t, 13, 10);
  eyeBead(t, 17, 10);
  t.ring(16, 24, 6, 5, R.gold, 0);
  t.ring(16, 24, 5, 4, R.gold, 1);
  t.px(16, 19, R.gold, 0);
  t.px(16, 20, WHITE);
  for (const x of [11, 21]) t.px(x, 24, R.navy, 2);
};

const cloutking: Paint = (t, back) => {
  const confetti: Ramp[] = [R.gold, R.ice, R.rose, R.white];
  for (const x of [8, 24]) t.ell(x, 15, 7, 8, R.violet, { spec: false });
  for (const x of [9, 23]) t.ell(x, 24, 5, 4, R.rose, { shade: 1, spec: false });
  let k = 0;
  for (let y = 9; y <= 27; y += 3) for (const x of [5, 9, 12, 20, 23, 27]) {
    if (t.has(x, y) && !t.has(x, 5)) t.px(x, y, confetti[k++ % 4]!, 0);
  }
  if (!back) t.poly([[12, 16], [20, 16], [22, 28], [10, 28]], R.rose, { tone: 2 });
  t.ell(16, 19, 4, 7, R.navy);
  t.ell(16, 10, 5, 4.5, R.navy);
  crown(t, 16, 3, 7);
  t.line(13, 7, 11, 3, R.slate, 1);
  t.line(19, 7, 21, 3, R.slate, 1);
  t.px(11, 3, R.gold, 0); t.px(21, 3, R.gold, 0);
  t.px(16, 4, WHITE);
  if (back) return;
  eyeBig(t, 12, 8, R.rose);
  eyeBig(t, 17, 8, R.rose);
  smile(t, 15, 13, 3);
};

const hopiumite: Paint = (t, back) => {
  // R4: the leaf was a three-point `poly` at one flat tone with a line drawn on it for a vein —
  // the one prop the species is named after, and the rig never lit it. It is a blade now, which
  // means a lit leading edge, a mid face, a shaded trailing face and a tip, and the midrib is a
  // hard dark step through the middle rather than a hairline laid over a flat fill.
  t.cap(8, 27, 12, 25, 2.4, R.green, { shade: 1 });
  t.ell(14, 23, 6.5, 6, R.green);
  t.ell(15, 13, 5.5, 5, R.green);
  // Segment rings: the grub reads as segmented only where the light breaks, so each ring is one
  // dark step with one lit step above it. No outlines.
  for (const [x, y] of [[10, 26], [13, 28], [17, 26], [19, 21]] as const) {
    t.px(x, y, R.green, 4);
    t.px(x + 1, y - 1, R.green, 1);
  }
  // The candle it is protecting. One wick, one maybe.
  t.cap(22, 21, 22, 14, 1.4, R.paper, { spec: false });
  t.px(21, 17, R.paper, 0);
  flame(t, 22, 9, 5);
  // Signature: the leaf, held over the flame like a plan.
  blade(t, 13, 9, 29, 2, 4.2, R.moss);
  t.line(13, 9, 29, 2, R.moss, 3);
  if (back) {
    // From behind: the leaf's underside, where the midrib stands proud and the veins catch the
    // key light the top face was hiding — and the grub's back segments. The leaf asked for a
    // second source; this is the second source.
    t.line(13, 9, 29, 2, R.moss, 0);
    for (const [x, y] of [[16, 6], [20, 4], [24, 3]] as const) {
      t.line(x, y, x - 2, y + 3, R.moss, 1);
      t.px(x, y - 1, R.moss, 0);
    }
    t.vline(14, 18, 9, R.green, 4);
    t.px(15, 18, R.green, 1);
    t.cap(11, 17, 18, 17, 1.4, R.green, { shade: 1, spec: false });
    return;
  }
  // Veins on the top face read as shadow, not as light — that is the whole difference.
  for (const [x, y] of [[16, 6], [20, 4], [24, 3]] as const) t.line(x, y, x - 2, y + 3, R.moss, 3);
  eyeBig(t, 10, 11, R.gold);
  eyeBig(t, 16, 11, R.gold);
  smile(t, 13, 16, 4);
};

const copiumax: Paint = (t, back) => {
  // R4: "wax plates" was three pairs of flat `hline` bands ruled across a dome — painted-on wax,
  // with the same tone from end to end and no edge you could catch a light on. They are four
  // bevelled slabs now, shingled top over bottom, each with its own rim and gloss point, and the
  // moss core reads as a 1px seam at the widest rows where the wax did not reach.
  for (const x of [10, 22] as const) t.ell(x, 29, 3, 1.6, R.moss, { shade: 1, spec: false });
  t.ell(16, 20, 8.5, 9.5, R.moss);
  const plates: [number, number, number, number, number][] = [
    [15, 6.5, 2.8, 1.3, 0], [19.5, 8, 3, 1.4, 0], [23.5, 7.5, 3, 1.5, 1], [27, 6.5, 2.8, 1.4, 1],
  ];
  for (const [cy, rx, ry, k, sh] of plates) t.slab(16, cy, rx, ry, k, R.cream, { shade: sh });
  // Drips: the wax that got past the last plate, each one a lit bead on a shaded run. Three of
  // them, off-centre, because four evenly spaced drips reads as a fringe.
  for (const [x, y0, y1] of [[11, 16, 20], [20, 21, 25], [14, 25, 28]] as const) {
    t.cap(x, y0, x, y1, 1.2, R.cream, { shade: 1, spec: false });
    t.px(x, y1, R.cream, 0);
    t.px(x - 1, y0, R.cream, 3);
  }
  t.ell(16, 9, 6.5, 5.5, R.moss);
  t.cap(16, 5, 16, 2, 1.1, R.slate, { spec: false });
  flame(t, 16, 0, 4);
  if (back) {
    // From behind: the plates' upper lips, where a shingle shows you its thickness, the run that
    // went down the spine, and the wick's charred side. The rain clocked out; this side kept going.
    for (const [cy, rx] of [[15, 6.5], [19.5, 8], [23.5, 7.5], [27, 6.5]] as const) {
      t.hline(Math.round(16 - rx) + 1, Math.round(cy - 1), Math.round(rx * 2) - 1, R.cream, 4);
      t.px(Math.round(16 - rx) + 1, Math.round(cy - 2), R.cream, 1);
    }
    t.vline(16, 6, 22, R.cream, 5);
    t.px(17, 6, R.cream, 1);
    t.ring(16, 9, 4.8, 4, R.moss, 3);
    t.px(15, 3, R.slate, 2);
    return;
  }
  eyeBig(t, 11, 7, R.gold);
  eyeBig(t, 17, 7, R.gold);
  // Same maybe it had as a mite: a mouth that has not committed to a shape.
  t.px(14, 13, INK, 1);
  t.px(15, 14, INK, 1);
  t.px(16, 14, INK, 1);
  t.px(17, 13, INK, 1);
};

const chartgob: Paint = (t, back) => {
  for (const x of [11, 21]) {
    t.cap(x, 24, x, 27, 1.5, R.green, { spec: false });
    t.ell(x, 28.5, 3, 1.8, R.ink, { flat: 1, occ: false });
  }
  t.ell(16, 22, 6.5, 5.5, R.green);
  if (back) {
    t.rect(9, 17, 14, 11, R.bark, 1);
    t.rect(10, 18, 12, 9, R.paper, 2);
    t.line(9, 17, 22, 27, R.bark, 2);
  } else {
    for (const x of [8, 24]) t.cap(x, 20, x, 23, 1.5, R.green, { spec: false });
    t.rect(9, 18, 14, 11, R.bark, 1);
    t.rect(10, 19, 12, 9, R.paper, 0);
    const bars: [number, number, number, Ramp][] = [[12, 22, 3, R.green], [14, 21, 4, R.rose], [16, 20, 3, R.green], [18, 23, 3, R.rose], [20, 21, 4, R.green]];
    for (const [x, y, h, ramp] of bars) { t.rect(x, y, 1, h, ramp, 1); t.px(x, y - 1, ramp, 2); t.px(x, y + h, ramp, 2); }
  }
  t.ell(16, 12, 8, 6.5, R.green);
  t.poly([[8, 10], [3, 7], [8, 13]], R.green, { grad: true });
  t.poly([[24, 10], [29, 7], [24, 13]], R.green, { grad: true });
  t.rect(15, 3, 3, 3, R.paper, 0);
  t.px(17, 5, R.paper, 2);
  flame(t, 16, 0, 3);
  t.px(16, 0, WHITE);
  if (back) return;
  t.hline(9, 10, 14, R.slate, 2);
  for (const x of [12, 20]) {
    t.ring(x, 10, 2.5, 2, R.slate, 1);
    t.ell(x, 10, 1.8, 1.4, R.ice, { flat: 1, occ: false });
    t.px(x - 1, 9, WHITE);
  }
  t.px(16, 12, R.green, 3); t.px(16, 13, R.green, 3); t.px(17, 13, R.green, 3);
  teeth(t, 13, 15, 6);
};

const candlewyrm: Paint = (t, back) => {
  const m = (x: number) => (back ? 31 - x : x);
  t.poly([[m(18), 12], [m(24), 6], [m(24), 12]], R.rose, { tone: 2 });
  const segs: [number, number, number, number, number][] = [
    [6, 26, 12, 27, 3], [12, 27, 20, 25, 3.2], [20, 25, 25, 19, 3], [25, 19, 20, 13, 2.8], [20, 13, 15, 9, 2.6],
  ];
  for (const [x0, y0, x1, y1, r] of segs) t.cap(m(x0), y0, m(x1), y1, r, R.red);
  for (const [x, y] of [[9, 28], [15, 28], [22, 26], [26, 21], [22, 15]]) t.px(m(x!), y!, R.red, 0);
  t.line(m(5), 25, m(3), 22, R.slate, 1);
  flame(t, m(3), 18, 4);
  t.ell(m(13), 8, 5.5, 4.5, R.red);
  t.poly([[m(10), 5], [m(9), 1], [m(12), 4]], R.gold, { tone: 1 });
  t.poly([[m(15), 4], [m(17), 1], [m(17), 5]], R.gold, { tone: 1 });
  if (back) return;
  eyeBead(t, 9, 6);
  eyeBead(t, 14, 6);
  smile(t, 10, 10, 5);
  t.px(17, 9, WHITE);
};

const bagsnail: Paint = (t, back) => {
  const m = (x: number) => (back ? 31 - x : x);
  t.cap(m(6), 27, m(24), 27, 3, R.blue, { shade: 1 });
  t.ell(m(23), 22, 4, 3.5, R.blue, { shade: 1 });
  for (const [x0, x1] of [[22, 20], [25, 27]]) t.cap(m(x0!), 19, m(x1!), 14, 1, R.blue, { shade: 1, spec: false });
  t.poly([[m(6), 26], [m(7), 12], [m(11), 9], [m(17), 10], [m(19), 14], [m(20), 26]], R.paper, { grad: true });
  t.rect(m(back ? 18 : 7), 11, 12, 2, R.paper, 2);
  t.px(m(back ? 19 : 8), 11, R.gold, 0);
  t.px(m(back ? 19 : 8), 10, WHITE);
  t.line(m(10), 15, m(9), 25, R.paper, 2);
  t.line(m(15), 14, m(16), 25, R.paper, 2);
  if (back) return;
  t.rect(12, 18, 3, 2, R.rose, 1);
  eyeDot(t, 19, 13);
  eyeDot(t, 26, 13);
  smile(t, 22, 24, 3);
};

const diamondshell: Paint = (t, back) => {
  for (const x of [8, 24]) t.ell(x, 26, 3, 2.5, R.blue, { shade: 1, spec: false });
  for (const x of [12, 20]) t.ell(x, 28, 2.5, 1.6, R.blue, { shade: 2, spec: false });
  t.ell(16, 17, 11, 8.5, R.blue);
  // Faceted shell: a diamond lattice cut into the shell only, lit cells toward the key light.
  for (let y = 9; y <= 25; y++) for (let x = 6; x <= 26; x++) {
    if (!t.has(x, y)) continue;
    const a = x + y - 8, b = x - y + 24;
    if (a % 5 === 0 || b % 5 === 0) { t.darken(x, y); continue; }
    const cell = Math.floor(a / 5) + Math.floor(b / 5);
    if (cell % 2 === 0 && x + y < 34) t.lighten(x, y);
  }
  t.ring(16, 17, 11, 8.5, R.gold, 1);
  t.px(10, 10, WHITE);
  t.ell(16, 8.5, 4.5, 4, R.blue, { shade: 1 });
  if (back) return;
  eyeBead(t, 13, 7);
  eyeBead(t, 17, 7);
  brow(t, 13, 7, 1);
  brow(t, 17, 7, -1);
  t.px(16, 11, INK, 1);
};

const fomoling: Paint = (t, back) => {
  t.rect(22, 19, 3, 9, R.paper, 0);
  for (const y of [21, 23, 25]) t.px(23, y, INK, 1);
  t.px(22, 27, R.paper, 2); t.px(24, 26, R.paper, 2);
  t.px(23, 27, INK, 1);
  for (const x of [13, 17]) { t.vline(x, 28, 2, R.orange, 1); t.hline(x - 1, 29, 3, R.orange, 2); }
  t.ell(15, 22, 6.5, 6, R.gold);
  for (const x of [9, 21]) t.cap(x, 20, x - (x < 15 ? 1 : -1), 25, 1.6, R.gold, { shade: 1, spec: false });
  t.ell(15, 13, 6.5, 6, R.gold);
  t.poly([[13, 8], [15, 8], [14, 4]], R.gold, { tone: 1 });
  t.poly([[15, 8], [17, 8], [17, 4]], R.gold, { tone: 0 });
  t.px(24, 19, WHITE);
  if (back) return;
  eyeBig(t, 10, 10);
  eyeBig(t, 16, 10);
  t.poly([[13, 16], [17, 16], [15, 19]], R.orange, { tone: 1 });
  t.px(22, 11, R.ice, 1);
};

const fomobeast: Paint = (t, back) => {
  t.cap(26, 22, 29, 17, 1.2, R.red, { spec: false });
  t.px(29, 16, R.gold, 0);
  t.px(28, 19, WHITE);
  for (const x of [10, 22]) t.ell(x, 28, 3, 2, R.slate, { flat: 1, occ: false });
  for (const x of [10, 22]) t.cap(x, 24, x, 27, 2, R.red, { spec: false });
  t.ell(17, 22, 10, 7, R.red);
  t.ell(16, 13, 10.5, 8, R.gold);
  for (let a = 0; a < 12; a++) {
    const ang = Math.PI * 1.1 + (a / 11) * Math.PI * 0.8;
    t.px(Math.round(16 + Math.cos(ang) * 11), Math.round(13 + Math.sin(ang) * 8.5), R.gold, 0);
  }
  t.ell(16, 14, 8, 6.5, R.red);
  t.poly([[9, 10], [5, 6], [10, 8]], R.paper, { tone: 2 });
  t.poly([[23, 10], [27, 6], [22, 8]], R.paper, { tone: 2 });
  if (back) return;
  t.ell(16, 18, 4, 2.5, R.red, { shade: 1, spec: false });
  t.darken(14, 18); t.darken(18, 18);
  eyeSleepy(t, 11, 12);
  eyeSleepy(t, 18, 12);
  brow(t, 11, 12, 1);
  brow(t, 18, 12, -1);
};

const gasgob: Paint = (t, back) => {
  paws(t, 16, 4.5, 3, R.moss);
  t.ell(16, 22, 7, 6, R.moss);
  t.cap(22, 20, 25, 23, 1.5, R.moss, { spec: false });
  t.ell(26, 21, 2, 2, R.gold, { flat: 0, occ: false });
  t.px(26, 21, R.gold, 1);
  if (!back) t.ell(16, 23, 4, 4, R.green, { dome: 0.5, rim: false, spec: false });
  t.ell(16, 12, 8, 6.5, R.moss);
  t.poly([[8, 10], [3, 8], [8, 13]], R.moss, { grad: true });
  t.poly([[24, 10], [29, 8], [24, 13]], R.moss, { grad: true });
  t.rect(12, 2, 8, 5, R.slate, 1);
  t.hline(10, 6, 12, R.slate, 2);
  t.vline(13, 3, 3, R.slate, 0);
  t.ell(16, 1.5, 2, 0.8, R.stone, { flat: 1, occ: false, rim: false, spec: false });
  t.px(19, 1, R.stone, 0);
  t.px(16, 1, R.gold, 0);
  t.px(13, 2, WHITE);
  if (back) return;
  eyeBig(t, 11, 9);
  eyeBig(t, 18, 9);
  brow(t, 11, 9, 1);
  brow(t, 18, 9, -1);
  t.px(16, 12, R.moss, 3); t.px(16, 13, R.moss, 3);
  teeth(t, 12, 15, 8);
};

const slippeel: Paint = (t, back) => {
  const m = (x: number) => (back ? 31 - x : x);
  // R4: eight colours on the back and three flat `poly` fins that were paper cut-outs — the one
  // thing the species is named for, drawn with no light on it at all. The fins are blades now, and
  // they are deliberately mismatched: the right one rides high, the left one sits four rows low.
  // That offset is the joke and it is the silhouette, so it had better be visible at icon size.
  t.ell(m(10), 28.4, 6, 1.8, R.blue, { shade: 2, spec: false, rim: false });
  const segs: [number, number, number, number, number][] = [
    [8, 27, 13, 25, 2.8], [13, 25, 18, 21, 2.8], [18, 21, 21, 15, 2.6], [21, 15, 17, 9, 2.4],
  ];
  for (const [x0, y0, x1, y1, r] of segs) t.cap(m(x0), y0, m(x1), y1, r, R.blue);
  blade(t, m(19), 20, m(27), 16, 2.6, R.violet);
  blade(t, m(13), 24, m(4), 22, 2.4, R.violet);
  blade(t, m(20), 13, m(26), 9, 2.2, R.violet);
  blade(t, m(9), 26, m(3), 28, 2.2, R.violet);
  t.ell(m(14), 6, 5, 4.5, R.blue);
  if (back) {
    // From behind: the dorsal ridge running the whole body with a lit shoulder beside it, the
    // fins' far roots, and the tag hanging blank — the quote is on the side that moved.
    for (const [x, y] of [[10, 26], [13, 24], [16, 22], [18, 19], [20, 16], [19, 12], [16, 8]] as const) {
      t.darken(m(x), y, 2);
      t.lighten(m(x + 1), y - 1);
    }
    for (const [x, y] of [[19, 20], [13, 24], [20, 13]] as const) t.ell(m(x), y, 1.8, 1.5, R.violet, { flat: 2, occ: false });
    t.line(m(16), 8, m(24), 12, R.gold, 2);
    t.slab(m(26), 15, 3, 2.4, 1, R.paper, { shade: 1 });
    return;
  }
  // Belly: a cool pale underside stepped along the low side of each coil, so the toy has a top
  // and a bottom instead of one tube of blue.
  for (const [x, y] of [[9, 28], [12, 27], [15, 25], [18, 23], [20, 18], [19, 13]] as const) {
    t.px(x, y, R.ice, 1);
    t.px(x + 1, y, R.ice, 2);
  }
  // Gills, and the specular the rig will not give a 2px coil.
  for (const [x, y] of [[17, 11], [18, 12], [19, 13]] as const) t.px(x, y, R.navy, 3);
  t.px(12, 24, WHITE);
  // Signature: the quote it missed, still on its string. Paper stock, two slate rules, a gold clip
  // — one prop, three materials, and the only warm value on an otherwise cold toy.
  t.line(16, 8, 24, 12, R.gold, 2);
  t.slab(26, 15, 3, 2.4, 1, R.paper);
  t.hline(24, 14, 4, R.slate, 1);
  t.hline(24, 16, 3, R.slate, 1);
  t.px(25, 13, R.gold, 0);
  eyeBig(t, 10, 4, R.ice);
  eyeBig(t, 15, 4, R.ice);
  smile(t, 12, 9, 5);
};

const mintmoth: Paint = (t, back) => {
  const wing = back ? R.stone : R.paper;
  t.poly([[3, 12], [13, 10], [13, 22], [4, 24]], wing, { grad: true });
  t.poly([[19, 10], [29, 12], [28, 24], [19, 22]], wing, { grad: true });
  for (let y = 11; y <= 23; y += 2) { t.px(4, y, wing, 3); t.px(28, y, wing, 3); }
  for (const y of [14, 17, 20]) { t.hline(6, y, 5, wing, 2); t.hline(21, y, 5, wing, 2); }
  t.px(7, 17, INK, 1);
  t.px(7, 14, R.gold, 0); t.px(25, 14, R.gold, 0);
  t.px(25, 13, WHITE);
  for (const x of [9, 23]) t.ell(x, 25, 4, 2.5, wing, { shade: 1, spec: false });
  t.ell(16, 18, 3.5, 7, R.green);
  t.ell(16, 10, 4.5, 4, R.green);
  t.line(14, 7, 12, 3, R.slate, 1);
  t.line(18, 7, 20, 3, R.slate, 1);
  t.px(12, 3, R.paper, 0); t.px(20, 3, R.paper, 0);
  if (back) return;
  eyeBead(t, 13, 9);
  eyeBead(t, 17, 9);
  smile(t, 15, 13, 3);
};

const airdropqueen: Paint = (t, back) => {
  t.ell(16, 7, 12, 5.5, R.cream);
  for (const [x0, x1] of [[8, 14], [12, 15], [20, 17], [24, 18]]) t.line(x0!, 12, x1!, 17, R.cream, 3);
  t.poly([[11, 17], [21, 17], [24, 29], [8, 29]], R.rose, { tone: 2 });
  t.rect(15, 27, 3, 2, R.bark, 1);
  t.px(11, 18, WHITE);
  for (const x of [9, 23]) t.ell(x, 20, 5, 4.5, R.green, { shade: 1, spec: false });
  t.ell(16, 21, 4.5, 7, R.green);
  t.ell(16, 13, 5, 4.5, R.green);
  crown(t, 16, 7, 6);
  if (back) return;
  eyeBig(t, 12, 12);
  eyeBig(t, 17, 12);
  smile(t, 15, 17, 3);
};

const yieldyeti: Paint = (t, back) => {
  paws(t, 16, 6, 3.5, R.cream);
  for (const x of [11, 21]) { t.px(x, 29, R.ice, 1); t.px(x - 2, 29, R.ice, 1); }
  t.ell(16, 21, 9.5, 8, R.cream);
  for (const [x, y] of [[7, 15], [25, 15], [9, 27], [23, 27]]) t.poly([[x! - 1, y! + 1], [x! + 1, y! + 1], [x!, y! - 2]], R.cream, { tone: 0 });
  t.cap(9, 18, 6, 24, 2.2, R.cream, { spec: false });
  t.cap(23, 18, 25, 24, 2.2, R.cream, { spec: false });
  t.ell(27, 27, 2.5, 1.5, R.ice, { spec: false });
  t.ell(27, 24.5, 1.8, 1.2, R.ice, { spec: false });
  t.px(27, 23, R.ice, 0);
  t.px(26, 24, WHITE);
  t.ell(16, 10, 9, 6.5, R.cream);
  t.poly([[9, 7], [7, 3], [11, 6]], R.slate, { tone: 0 });
  t.poly([[23, 7], [25, 3], [21, 6]], R.slate, { tone: 0 });
  t.px(7, 2, R.rose, 1); t.px(25, 2, R.rose, 1);
  if (back) return;
  t.ell(16, 12, 6.5, 5.5, R.ice, { shade: 1, dome: 0.6, spec: false });
  eyeBig(t, 11, 10);
  eyeBig(t, 17, 10);
  smile(t, 15, 16, 3);
};

const yapling: Paint = (t, back) => {
  // Stood off-centre and turned to talk at you, so the bubble it will not stop filling gets the
  // whole right third of the frame instead of floating as a blob behind its ear.
  for (const x of [9, 17]) {
    t.cap(x, 24, x, 28.4, 2, R.moss, { shade: 1, spec: false });
    t.hline(x - 2, 29, 4, R.moss, 2);
    t.px(x - 1, 29, R.moss, 1);
  }
  // Arms mid-gesture: a capsule carries the rig's rim; the old 1px spur carried nothing.
  t.cap(7, 18, 2, 15, 2.2, R.moss, { shade: 1 });
  t.cap(19, 19, 23, 22, 2.2, R.moss, { shade: 1 });
  t.ell(13, 21, 7.5, 6, R.green);
  t.ell(13, 12, 6.5, 6, R.green);
  // Reed leaves for horns — tapered, lit on the key side, tipped.
  blade(t, 10, 8, 7, 2, 2, R.moss);
  blade(t, 16, 8, 18, 2, 2, R.moss);
  if (back) {
    // From behind: the reed spine it rattles, the leaf roots, and the bubble seen edge-on — empty.
    t.vline(13, 16, 10, R.moss, 3);
    for (const y of [17, 20, 23]) t.hline(9, y, 9, R.moss, 3);
    t.ell(13, 12, 4, 3.4, R.moss, { shade: 1, spec: false });
    t.px(11, 10, R.green, 0);
    t.slab(25, 9, 2, 4.5, 1, R.paper, { shade: 1 });
    t.vline(24, 6, 7, R.paper, 0);
    return;
  }
  eyeBig(t, 8, 10);
  eyeBig(t, 14, 10);
  grin(t, 11, 15, 5, 3);
  bubble(t, 25, 9, 5.5, 4.5, -1);
};

const yapstorm: Paint = (t, back) => {
  // One hedge, a quorum of mouths: the hedge is a real stack of shaded lobes and the bubbles come
  // in three graded sizes, so the storm reads as a storm and not as three loose paper blobs.
  for (const x of [8, 17]) {
    t.cap(x, 23, x, 28.4, 2.4, R.moss, { shade: 1, spec: false });
    t.hline(x - 3, 29, 6, R.moss, 2);
    t.px(x - 2, 29, R.moss, 1);
  }
  t.cap(5, 17, 1, 13, 2.6, R.moss, { shade: 1 });
  t.cap(19, 18, 24, 21, 2.6, R.moss, { shade: 1 });
  t.ell(12, 20, 9.5, 7.5, R.green);
  // Hedge lobes: each one its own barrel with a lit crown, so the mass keeps counting.
  for (const [x, y] of [[6, 15], [11, 13], [17, 15]] as const) {
    t.ell(x, y, 3.4, 3, R.green, { spec: false });
    t.px(x - 2, y - 2, R.green, 0);
  }
  t.ell(12, 11, 8, 6.5, R.green);
  blade(t, 8, 6, 4, 0, 2.4, R.moss);
  blade(t, 16, 6, 19, 0, 2.4, R.moss);
  if (back) {
    // From behind: eleven reed stems bound at the crown, and the bubbles edge-on and blank.
    t.ell(12, 11, 5, 4.2, R.moss, { shade: 1, spec: false });
    t.px(9, 8, R.green, 0);
    for (const x of [7, 10, 13, 16]) t.vline(x, 15, 11, R.moss, 3);
    t.hline(4, 18, 17, R.moss, 3);
    t.vline(12, 15, 11, R.green, 1);
    t.slab(26, 8, 2, 5, 1, R.paper, { shade: 1 });
    t.vline(25, 4, 8, R.paper, 0);
    t.slab(28, 18, 1.6, 2.6, 0.9, R.paper, { shade: 1 });
    return;
  }
  eyeBig(t, 7, 9);
  eyeBig(t, 13, 9);
  grin(t, 9, 15, 7, 4);
  bubble(t, 26, 7, 5, 4.5, -1);
  bubble(t, 26, 17, 3.2, 2.6, -1);
  bubble(t, 23, 25, 2.4, 1.8, -1);
};

const lurkit: Paint = (t, back) => {
  // Cloak: a graded membrane behind a lit leading bone, with one rib each side.
  for (const s of [-1, 1]) {
    t.poly([[16 + s * 12, 13], [16 + s * 4, 11], [16 + s * 4, 23], [16 + s * 11, 22]], R.navy, { grad: true });
    t.cap(16 + s * 4, 11.5, 16 + s * 11.5, 13, 1.3, R.navy, { shade: -1, spec: false });
    t.line(16 + s * 5, 14, 16 + s * 10, 21, R.navy, back ? 3 : 0);
  }
  paws(t, 16, 9, 2.2, R.violet);
  t.ell(16, 20, 5, 6.5, R.violet);
  // Hood: a drawn spire over a shaded dome, so the cowl has a back to it.
  t.poly([[11, 10], [16, 3], [21, 10]], R.navy, { grad: true });
  t.px(15, 5, R.navy, 1);
  t.ell(16, 11.5, 6.4, 5.4, R.navy);
  if (back) {
    t.vline(16, 15, 11, R.violet, 3);
    t.hline(11, 14, 11, R.navy, 3);
    t.ring(16, 11.5, 3.4, 2.8, R.navy, 3);
  } else {
    // Face recess under a hard brim shadow, then the eyes glowing out of it.
    t.ell(16, 13, 5.2, 3.4, R.violet, { dome: 0.5, rim: false, spec: false });
    for (let x = 12; x <= 20; x++) { t.darken(x, 9); t.darken(x, 10); }
    eyeGlow(t, 10, 11, R.gold);
    eyeGlow(t, 19, 11, R.gold);
    t.px(16, 16, INK, 1);
  }
  // Scroll: a slab sheet between two wooden dowels — a pale bar with dark ends at 16px.
  t.slab(16, 26, 7, 3.2, 1.6, R.paper, { shade: back ? 2 : 1 });
  t.ell(9.6, 26, 1.4, 3.2, R.bark, { spec: false });
  t.ell(22.4, 26, 1.4, 3.2, R.bark, { spec: false });
  if (back) return;
  for (const y of [24, 26, 28]) t.hline(13, y, 6, INK, 1);
  t.px(12, 24, R.violet, 0);
  t.px(20, 24, R.violet, 0);
};

const threadmaw: Paint = (t, back) => {
  // Full-span cloak: membrane, lit leading bone, two ribs each side.
  for (const s of [-1, 1]) {
    t.poly([[16 + s * 15, 11], [16 + s * 2, 9], [16 + s * 2, 25], [16 + s * 14, 26]], R.navy, { grad: true });
    t.cap(16 + s * 2, 9.5, 16 + s * 14.5, 11, 1.5, R.navy, { shade: -1, spec: false });
    t.line(16 + s * 4, 12, 16 + s * 12, 22, R.navy, back ? 3 : 0);
    t.line(16 + s * 5, 15, 16 + s * 13, 25, R.navy, back ? 3 : 0);
  }
  paws(t, 16, 10, 2.4, R.violet);
  t.ell(16, 19, 6.5, 8, R.violet);
  t.poly([[9, 8], [16, 0], [23, 8]], R.navy, { grad: true });
  t.px(15, 3, R.navy, 1);
  t.ell(16, 11, 7.5, 7, R.navy);
  if (back) {
    t.vline(16, 13, 13, R.violet, 3);
    t.hline(10, 14, 13, R.navy, 3);
    t.ring(16, 11, 4, 3.4, R.navy, 3);
  } else {
    t.ell(16, 12, 6, 4.5, R.violet, { dome: 0.5, rim: false, spec: false });
    for (let x = 11; x <= 21; x++) { t.darken(x, 6); t.darken(x, 7); }
    eyeGlow(t, 9, 9, R.gold);
    eyeGlow(t, 20, 9, R.gold);
    brow(t, 9, 9, 1);
    brow(t, 20, 9, -1);
    // The maw, and the thread it is already pouring into the scroll.
    grin(t, 12, 14, 8, 4);
    t.cap(16, 18, 16, 21, 1.2, R.paper, { spec: false });
  }
  t.slab(16, 25.5, 9.5, 4, 1.8, R.paper, { shade: back ? 2 : 1 });
  t.ell(7.3, 25.5, 1.5, 4, R.bark, { spec: false });
  t.ell(24.7, 25.5, 1.5, 4, R.bark, { spec: false });
  if (back) return;
  for (const y of [23, 25, 27]) t.hline(11, y, 10, INK, 1);
};

const ratiofin: Paint = (t, back) => {
  const m = (x: number) => (back ? 31 - x : x);
  // Stood up on its tail fan. Lying on the diagonal it touched the floor nowhere and read as a
  // smear; upright it is unmistakably a minnow, and the take it is holding has somewhere to go.
  blade(t, m(16), 25, m(9), 29, 3, R.blue);
  blade(t, m(16), 25, m(23), 29, 3, R.blue);
  t.cap(m(16), 26, m(16), 19, 3, R.blue);
  t.ell(m(15), 14, 7.5, 7, R.blue);
  blade(t, m(8), 16, m(2), 13, 2.6, R.blue);
  blade(t, m(21), 17, m(27), 19, 2.6, R.blue);
  // The dorsal it argues with: gold, tapered, tipped.
  blade(t, m(15), 8, m(13), 2, 3, R.gold);
  if (back) {
    // From behind: the lateral line, the tail's fin rays, and the card held blank-side out —
    // whatever it said, nobody behind it got to read it.
    t.vline(m(15), 9, 13, R.navy, 2);
    t.px(m(13), 10, R.blue, 0);
    for (const y of [12, 16, 20]) t.hline(m(19), y, 4, R.navy, 1);
    for (let i = 0; i < 4; i++) t.line(m(16), 25, m(10 + i * 4), 29, R.navy, 2);
    t.slab(m(26), 22, 3.6, 4.2, 1.3, R.paper, { shade: 1 });
    t.vline(m(26), 19, 7, R.paper, 3);
    return;
  }
  eyeBig(t, 11, 10);
  // Gill slit behind the eye: a lit fold over a hard seam.
  t.vline(16, 11, 6, R.navy, 1);
  t.vline(17, 11, 6, R.navy, 3);
  // Mouth mid-L.
  t.rect(17, 16, 2, 2, INK, 1);
  t.px(17, 16, WHITE);
  // Its one contribution, held up on a card so the whole pond can see it.
  t.slab(26, 22, 3.6, 4.2, 1.3, R.paper);
  t.vline(25, 20, 4, INK, 1);
  t.hline(25, 23, 3, INK, 1);
};

const ratioking: Paint = (t, back) => {
  const m = (x: number) => (back ? 31 - x : x);
  // Same upright minnow rig as ratiofin, one size up: a fanned tail on the floor, a taller gold
  // dorsal, and two cards instead of one — the pond voted twice and was wrong twice.
  blade(t, m(16), 24, m(7), 29, 3.6, R.blue);
  blade(t, m(16), 24, m(25), 29, 3.6, R.blue);
  t.cap(m(16), 26, m(16), 18, 3.4, R.blue);
  t.ell(m(15), 13, 8.5, 7.5, R.blue);
  blade(t, m(7), 15, m(1), 12, 3, R.blue);
  blade(t, m(22), 16, m(29), 19, 3, R.blue);
  // Dorsal set back off the crown: stacked on it the two golds read as one lump.
  blade(t, m(21), 7, m(28), 2, 3.4, R.gold);
  if (back) {
    // From behind: the lateral line, the fin rays, the crown's band seen from the nape, and both
    // cards blank-side out.
    t.vline(m(15), 8, 13, R.navy, 2);
    t.px(m(12), 9, R.blue, 0);
    for (const y of [11, 15, 19]) t.hline(m(19), y, 5, R.navy, 1);
    for (let i = 0; i < 5; i++) t.line(m(16), 24, m(8 + i * 4), 29, R.navy, 2);
    crown(t, m(15), 4, 9);
    t.hline(m(11), 8, 9, R.blue, 3);
    t.slab(m(27), 21, 3.4, 4, 1.2, R.paper, { shade: 1 });
    t.vline(m(27), 18, 7, R.paper, 3);
    return;
  }
  eyeBig(t, 11, 9);
  t.vline(16, 10, 7, R.navy, 1);
  t.vline(17, 10, 7, R.navy, 3);
  t.rect(18, 15, 3, 3, INK, 1);
  t.px(18, 15, WHITE);
  t.slab(27, 21, 3.4, 4, 1.2, R.paper);
  t.vline(26, 19, 4, INK, 1);
  t.hline(26, 22, 3, INK, 1);
  t.slab(24, 26, 3, 3, 1.1, R.paper, { shade: 1 });
  t.vline(23, 25, 3, INK, 1);
  t.hline(23, 27, 3, INK, 1);
  crown(t, m(15), 4, 9);
};

const scrollmite: Paint = (t, back) => {
  // R4: the phone was two nested rectangles pasted over the body — flat fills, no case, no glass,
  // and the mite behind it was a dome with two dots. Rebuilt as a held prop: a bevelled case, a
  // dark screen set into it, a hard 2px glint across the glass, and a thumb coming up onto it.
  // The screen is also a light — it kicks a cool band onto the jaw, which is the only thing that
  // explains why this toy's face is lit from underneath.
  for (const x of [10, 22]) {
    t.cap(x, 24, x, 27.6, 1.5, R.stone, { shade: 1, spec: false });
    t.hline(x - 2, 28, 5, R.stone, 3);
    t.px(x - 2, 28, R.stone, 2);
  }
  t.ell(16, 22, 8.5, 6.5, R.stone);
  t.ell(16, 10, 7, 5.8, R.stone);
  // Feelers: thumb first, but a mite is still a mite.
  for (const s of [-1, 1] as const) {
    t.line(16 + s * 4, 6, 16 + s * 9, 1, R.stone, 2);
    t.px(16 + s * 9, 1, R.stone, 0);
  }
  // Both arms up, because it has been holding this for four hours.
  for (const s of [-1, 1] as const) t.cap(16 + s * 7, 20, 16 + s * 4.5, 24, 1.6, R.stone, { shade: 1, spec: false });
  t.slab(16, 21, 5, 5.5, 1.1, R.slate, { shade: back ? 0 : 1 });
  if (back) {
    // From behind: the case's blank shell with its camera island and brass ring, the mite's back
    // plates shingling down the dome, and the legs' far side. No glass on this side, so no glow.
    t.slab(16, 21, 3.6, 4.2, 0.9, R.slate, { shade: 1, spec: false });
    t.ell(14, 18, 1.6, 1.4, INK, { flat: 1, occ: false });
    t.ring(14, 18, 2.2, 2, R.gold, 1);
    t.px(13, 17, WHITE);
    for (const y of [8, 11, 14] as const) {
      const hw = Math.round(Math.sqrt(Math.max(0, 1 - ((y - 10) / 5.8) ** 2)) * 7) - 1;
      t.hline(16 - hw, y, hw * 2, R.stone, 3);
      t.px(16 - hw, y - 1, R.stone, 1);
    }
    return;
  }
  // The glass: a dark screen inside the case, Tuesday's headline still on it, and one hard streak
  // of reflected key light. Two pixels, straight, no smear — glass, not bloom.
  t.slab(16, 21, 3.6, 4.4, 0.7, R.ink, { spec: false });
  t.hline(13, 18, 6, R.rose, 1);
  t.hline(13, 20, 5, R.paper, 2);
  t.hline(13, 22, 6, R.paper, 3);
  t.px(14, 17, WHITE);
  t.px(15, 18, R.ice, 1);
  // The thumb, mid-swipe, with its own nail.
  t.cap(13, 26, 15.5, 21.5, 2, R.stone, { shade: 1 });
  t.ell(15.5, 21.5, 1.5, 1.3, R.paper, { spec: false, occ: false });
  // Screen glow on the jaw: one cool band, quantized, where a lesser build would have bloomed.
  for (const x of [13, 15, 17, 19] as const) t.px(x, 15, R.ice, 1);
  eyeBig(t, 10, 7, R.ice);
  eyeBig(t, 18, 7, R.ice);
  brow(t, 10, 7, 1);
  brow(t, 18, 7, -1);
};

const doomwyrm: Paint = (t, back) => {
  const m = (x: number) => (back ? 31 - x : x);
  // R4: nine colours, and the phone was `rect` on `rect` — a flat pale panel with two rules, which
  // is what a 1998 sprite does when it means "screen". Same rebuild as scrollmite one stage up: a
  // case with a bevel, glass set into it, a hard streak of key light across the glass, and the
  // screen treated as a light source that puts one cool quantized band on the jaw above it.
  const segs: [number, number, number, number, number][] = [
    [6, 28, 12, 26, 3.6], [12, 26, 18, 22, 3.6], [18, 22, 22, 16, 3.4], [22, 16, 19, 9, 3.2],
  ];
  for (const [x0, y0, x1, y1, r] of segs) t.cap(m(x0), y0, m(x1), y1, r, R.navy);
  // Sel-out where the coils cross each other: a hard contact step, so four capsules read as one
  // animal folded up rather than four sausages stacked.
  for (const [x, y] of [[12, 26], [18, 22], [22, 16]] as const) {
    t.darken(m(x), y, 2);
    t.darken(m(x - 1), y + 1);
    t.lighten(m(x + 1), y - 1);
  }
  t.ell(m(18), 7, 7, 6, R.navy);
  blade(t, m(14), 4, m(11), 0, 1.7, R.rose);
  blade(t, m(21), 3, m(24), 0, 1.7, R.rose);
  t.slab(m(11), 18, 4, 5, 1, R.slate, { shade: 1 });
  if (back) {
    // From behind: the dorsal plates shingling the whole length of the coil, the horn roots, and
    // the case's blank shell with its camera island. Four hours in and it is still facing away.
    for (const [x, y, w] of [[8, 25, 5], [13, 23, 5], [17, 19, 5], [20, 13, 4]] as const) {
      t.cap(m(x - w / 2), y + 1, m(x + w / 2), y - 1, 1.5, R.navy, { shade: 1, spec: false });
      t.px(m(x), y - 2, R.navy, 0);
    }
    t.slab(m(11), 18, 2.8, 3.8, 0.8, R.slate, { shade: 0, spec: false });
    t.ell(m(10), 15.5, 1.4, 1.2, INK, { flat: 1, occ: false });
    t.ring(m(10), 15.5, 2, 1.8, R.gold, 1);
    t.px(m(9), 14.5, WHITE);
    for (const x of [14, 22] as const) t.ell(m(x), 4, 2, 1.6, R.rose, { flat: 2, occ: false });
    t.vline(m(18), 2, 4, R.navy, 3);
    return;
  }
  // Tuesday's headline, still open, one of four hours' worth.
  t.slab(11, 18, 2.9, 4, 0.7, R.ink, { spec: false });
  t.hline(9, 15, 5, R.rose, 1);
  t.hline(9, 17, 4, R.paper, 2);
  t.hline(9, 19, 5, R.paper, 3);
  t.hline(9, 21, 4, R.rose, 2);
  t.px(10, 15, WHITE);
  t.px(11, 16, R.ice, 1);
  // The claw that will not put it down.
  t.cap(15, 20, 12, 21, 1.8, R.navy, { shade: 1, spec: false });
  t.px(12, 20, R.navy, 0);
  // Screen glow on the underside of the jaw. One band, three pixels, hard steps.
  for (const x of [15, 17, 19] as const) t.px(x, 12, R.ice, 1);
  eyeGlow(t, 13, 5, R.ice);
  eyeGlow(t, 19, 5, R.ice);
  brow(t, 13, 5, 1);
  brow(t, 19, 5, -1);
  frown(t, 15, 10, 5);
};

const finekit: Paint = (t, back) => {
  // The whistle is on one side of the kettle, so it changes hands when the toy turns round.
  const m = (x: number) => (back ? 31 - x : x);
  t.ell(16, 26, 9, 4, R.gold);
  t.rect(9, 18, 14, 8, R.gold, 1);
  t.hline(9, 18, 14, R.gold, 0);
  t.ell(16, 20, 6.5, 5.5, R.cream);
  paws(t, 16, 4, 2.4, R.cream);
  t.ell(16, 12, 7, 6, R.cream);
  earPoint(t, 11, 9, 9, 3, R.cream, back ? R.cream : R.rose, 2);
  earPoint(t, 21, 9, 23, 3, R.cream, back ? R.cream : R.rose, 2);
  t.ell(m(22), 6, 2, 3, R.stone, { spec: false });
  t.px(m(22), 3, R.gold, 0);
  if (back) {
    // From behind there is no muzzle, so the crown is one flatter dome all the way down to a
    // scruff fold at the nape — and the kit's back, not a face, is what fills the kettle.
    t.ell(16, 11.6, 6.2, 5.4, R.cream, { dome: 0.72, spec: false, occ: false });
    // Both ears show cartilage instead of lining: a ridge down the middle, outer edge shaded.
    for (const [ax, bx] of [[9, 11], [23, 21]] as const) {
      t.line(ax + Math.sign(16 - ax), 5, bx, 8, R.cream, 0);
      for (let y = 4; y <= 8; y++) t.darken(ax, y, 2);
    }
    t.cap(12, 15.5, 20, 15.5, 1.5, R.cream, { shade: 2, spec: false });
    t.hline(12, 14, 9, R.cream, 1);
    // Two shoulder blades, the haunch fold, then the spine laid over both.
    t.ell(12.5, 20, 2.6, 2.4, R.cream, { shade: 1, spec: false });
    t.ell(19.5, 20, 2.6, 2.4, R.cream, { shade: 2, spec: false });
    t.cap(13, 23, 20, 23, 1.5, R.cream, { shade: 1, spec: false });
    t.vline(16, 17, 8, R.cream, 3);
    // The kettle's rolled base ring shows its far lip, either side of the paws.
    for (const [a0, a1] of [[Math.PI + 0.25, Math.PI + 1], [Math.PI * 2 - 1, Math.PI * 2 - 0.25]] as const) {
      arc(t, 16, 27.5, 8.6, 2, a0, a1, R.gold, 3);
    }
    // The crown repaint clears the rig's specular; the one gloss point goes back by hand on the
    // key shoulder, the one patch nothing after this paints or occludes.
    t.px(13, 9, WHITE);
    return;
  }
  eyeBead(t, 12, 10);
  eyeBead(t, 17, 10);
  smile(t, 14, 14, 5);
};

const kilnmutt: Paint = (t, back) => {
  // The kettle became a helmet: a real kettle, with a riveted band, a spout, a lid knob and the
  // yoke still swung back behind the dog's ears — instead of two flat gold boxes stacked on it.
  const m = (x: number) => (back ? 31 - x : x);
  paws(t, 16, 6.4, 3, R.cream, { shade: 1 });
  t.ell(16, 23, 9, 6.5, R.cream);
  t.cap(7, 19, 5, 25, 2.4, R.cream, { shade: 1 });
  t.cap(25, 19, 27, 25, 2.4, R.cream, { shade: 1 });
  t.ell(16, 16, 8.5, 6.5, R.cream);
  earPoint(t, 8, 13, 4, 7, R.cream, back ? R.cream : R.rose, 3);
  earPoint(t, 24, 13, 28, 7, R.cream, back ? R.cream : R.rose, 3);
  // Yoke first, so the kettle closes over its roots and only the handle clears the crown. At the
  // old 8px radius the kettle read as a mushroom cap and swallowed the arc whole.
  arc(t, 16, 13, 8.5, 8.5, Math.PI, Math.PI * 2, R.slate, 1);
  arc(t, 16, 13, 7.5, 7.5, Math.PI, Math.PI * 2, R.slate, 0);
  t.ell(16, 10, 7, 4.8, R.gold);
  t.cap(9.5, 13, 22.5, 13, 1.7, R.gold, { shade: 1 });
  for (const x of [11, 14, 17, 20] as const) rivet(t, x, 13, R.gold);
  // A kettle has one spout, so it swaps sides when the toy turns round — the single largest
  // thing that used to be byte-identical between the two poses.
  t.cap(m(22), 9, m(27), 12, 2, R.gold);
  t.px(m(28), 13, R.gold, 2);
  t.ell(16, 6, 2.4, 1.7, R.gold);
  if (back) {
    // From behind: the kettle's seam and base ring, the yoke's other pivot, the nape, and the
    // scorch where the room caught. The room is fine, thanks.
    t.vline(16, 4, 5, R.gold, 3);
    t.px(15, 5, R.gold, 0);
    t.ring(16, 10, 5.4, 3.6, R.gold, 3);
    // The band is one band, so it is still there — but its closure is at the nape, and from
    // behind that is all you see of it: a lapped plate over the middle two studs, cinched down
    // over its own loose end. It was a flat dark bar straight across the head.
    t.slab(16, 13, 3.4, 2.4, 0.9, R.gold, { shade: 1 });
    for (const [x, y] of [[14, 12], [18, 14]] as const) rivet(t, x, y, R.gold);
    t.vline(16, 15, 3, R.gold, 3);
    t.px(9, 13, R.gold, 0);
    t.ring(16, 16, 5, 4.2, R.cream, 3);
    t.vline(16, 19, 9, R.cream, 3);
    for (const x of [11, 21]) t.ell(x, 25, 2.6, 2, R.cream, { shade: 1, spec: false });
    // The scorch it walked away from. The room is fine, thanks.
    for (const [x, y] of [[24, 20], [26, 22], [25, 24]] as const) t.px(x, y, R.red, 1);
    return;
  }
  eyeBig(t, 10, 15);
  eyeBig(t, 18, 15);
  nose(t, 15, 20);
  smile(t, 14, 21, 5);
  // The room is on fire, out of the spout. The room is fine, thanks.
  flame(t, 28, 6, 4);
  flame(t, 26, 4, 3);
};

const npcat: Paint = (t, back) => {
  paws(t, 16, 4.6, 2.8, R.stone);
  t.ell(16, 22, 7, 6, R.stone);
  t.ell(25, 20, 3.2, 3, R.stone);
  t.ell(16, 12, 7.5, 6.5, R.stone);
  earPoint(t, 11, 8, 10, 2, R.stone, R.rose, 2);
  earPoint(t, 21, 8, 22, 2, R.stone, R.rose, 2);
  t.px(16, 4, R.gold, 0);
  t.px(16, 3, R.gold, 1);
  t.px(15, 2, R.gold, 1);
  t.px(17, 2, R.gold, 1);
  if (back) {
    // The one dark rule this back used to carry did no work: an NPC seen from behind is all the
    // things you never got a line of dialogue about. The nape, the spine, the outsides of both
    // ears, and the tail curling the other way.
    t.cap(11, 17, 21, 17, 1.4, R.stone, { shade: 1, spec: false });
    t.vline(16, 18, 8, R.stone, 3);
    for (const x of [11, 21]) t.ell(x, 6.5, 2, 2.2, R.stone, { shade: 2, spec: false, occ: false });
    // Thin cartilage at the tips: from behind an ear still lets a little of its inside through.
    for (const [x, y] of [[10, 4], [10, 5], [22, 4], [22, 5]] as const) t.px(x, y, R.rose, 1);
    t.cap(25, 20, 27, 24, 1.6, R.stone, { shade: 1, spec: false });
    return;
  }
  // Muzzle first: a lit pad for the face to sit on, so it has a plane instead of two dots and a
  // line floating on a flat head.
  t.ell(16, 15.5, 3.2, 1.8, R.cream, { dome: 0.5, rim: false, spec: false });
  eyeDot(t, 12, 11);
  eyeDot(t, 18, 11);
  t.hline(14, 15, 4, INK, 1);
};

const lorelord: Paint = (t, back) => {
  // The tail is on one side, so it changes sides when the toy turns round.
  const m = (x: number) => (back ? 31 - x : x);
  paws(t, 16, 5.4, 3.2, R.stone);
  t.ell(16, 22, 9, 7, R.stone);
  t.ell(m(26), 19, 4, 3.5, R.stone);
  t.ell(16, 11, 9, 7.5, R.stone);
  earPoint(t, 10, 7, 8, 1, R.stone, back ? R.stone : R.rose, 3);
  earPoint(t, 22, 7, 24, 1, R.stone, back ? R.stone : R.rose, 3);
  t.rect(12, 1, 8, 6, R.paper, 0);
  t.vline(16, 1, 6, R.rose, 1);
  t.hline(12, 6, 8, R.gold, 1);
  if (back) {
    // Nobody prints anything on the back of a FAQ. Turned round the page is a bound back cover:
    // the centre fold now reads as a rounded sewn spine with a headband at each end, the page
    // block shows its cut edge, and the ribbon is still marking page two.
    t.rect(12, 1, 8, 6, R.paper, 2);
    t.vline(15, 1, 6, R.paper, 0);
    t.vline(16, 1, 6, R.paper, 1);
    t.vline(17, 1, 6, R.paper, 3);
    for (const y of [1, 5]) { t.px(15, y, R.gold, 0); t.px(16, y, R.gold, 1); }
    t.hline(12, 6, 8, R.gold, 2);
    t.px(12, 3, R.paper, 3);
    t.px(19, 3, R.paper, 3);
    t.vline(13, 7, 3, R.rose, 1);
    t.px(13, 10, R.rose, 2);
    // The occiput's own shadow, then the nape fold under it and the lit lip above — instead of
    // one dark rule straight across the head.
    arc(t, 16, 11, 7, 5.8, 0.35, Math.PI - 0.35, R.stone, 3);
    t.cap(11, 16, 21, 16, 1.8, R.stone, { shade: 2, spec: false });
    t.hline(12, 14, 9, R.stone, 1);
    // Two shoulder blades, each with its own hard contact band rather than the rig's, which
    // would have taken the body's one specular with it.
    for (const [cx, sh] of [[11, 1], [21, 2]] as const) {
      t.ell(cx, 21, 3, 2.8, R.stone, { shade: sh, spec: false, occ: false });
      arc(t, cx, 21, 3.4, 3.2, 0.25, Math.PI - 0.25, R.stone, 3);
    }
    t.vline(16, 15, 11, R.stone, 3);
    return;
  }
  eyeBig(t, 10, 9);
  eyeBig(t, 17, 9);
  smile(t, 14, 15, 5);
};

const gatekit: Paint = (t, back) => {
  paws(t, 16, 10.5, 2.4, R.orange);
  t.ell(16, 21, 8.5, 6.5, R.orange);
  t.ell(16, 12, 6.5, 5.5, R.orange);
  earPoint(t, 11, 9, 10, 3, R.orange, back ? R.orange : R.cream, 2);
  earPoint(t, 21, 9, 22, 3, R.orange, back ? R.orange : R.cream, 2);
  if (back) {
    t.vline(16, 16, 11, R.orange, 3);
    t.ring(16, 12, 3.4, 3, R.orange, 3);
    t.cap(23, 25, 28, 19, 1.8, R.orange, { spec: false });
  } else {
    t.ell(16, 14.5, 4.2, 2.6, R.cream, { dome: 0.5, rim: false, spec: false });
    eyeBig(t, 10, 9);
    eyeBig(t, 19, 9);
    nose(t, 15, 13);
    smile(t, 14, 15, 5);
  }
  // Pocket fence: four pointed pickets on two rails. From behind the rails are the near side,
  // so the draw order flips and the panel reads as a back, not a mirror.
  const rails = () => {
    for (const y of [22, 27]) t.cap(8, y, 24, y, 1.3, R.bark, { shade: back ? -1 : 1, spec: false });
  };
  const pickets = () => {
    for (const x of [10, 14, 18, 22]) {
      slat(t, x, 28, x, 20, 1.4, R.bark);
      t.px(x, 18, R.bark, 0);
    }
  };
  if (back) { pickets(); rails(); t.line(9, 27, 23, 22, R.bark, 0); return; }
  rails();
  pickets();
  // The guest list, pinned on and decorative.
  t.slab(16, 24, 2.6, 2.2, 1, R.paper);
  t.hline(15, 23, 3, INK, 1);
  t.hline(15, 25, 3, INK, 1);
};

const gatewyrm: Paint = (t, back) => {
  const m = (x: number) => (back ? 31 - x : x);
  t.cap(m(6), 26, m(13), 24, 3.6, R.orange);
  t.cap(m(13), 24, m(19), 19, 3.4, R.orange);
  t.cap(m(19), 19, m(21), 13, 3.2, R.orange);
  // The fence that grew a spine: a rail down the back carrying three pointed pickets.
  t.cap(m(4), 21, m(15), 15.5, 1.2, R.bark, { shade: 1, spec: false });
  for (const [x, base, top] of [[5, 23, 18], [9, 22, 16], [14, 20, 13]] as const) {
    slat(t, m(x), base, m(x), top, 1.3, R.bark);
    t.px(m(x), top - 2, R.bark, 0);
  }
  t.ell(m(23), 9, 7, 6.2, R.orange);
  earPoint(t, m(19), 5, m(17), 0, R.orange, back ? R.orange : R.cream, 2.5);
  earPoint(t, m(27), 5, m(29), 0, R.orange, back ? R.orange : R.cream, 2.5);
  if (back) {
    t.ring(m(23), 9, 4, 3.4, R.orange, 3);
    t.vline(m(23), 3, 12, R.orange, 3);
    t.cap(m(4), 21, m(15), 15.5, 1.2, R.bark, { flat: 0, occ: false });
    return;
  }
  t.ell(23, 11, 4.5, 2.6, R.cream, { dome: 0.5, rim: false, spec: false });
  eyeBig(t, 18, 6);
  eyeBig(t, 24, 6);
  nose(t, 22, 10);
  grin(t, 20, 12, 7, 3);
  // Still asks for your name.
  t.slab(7, 25, 2.8, 2.4, 1, R.paper);
  t.hline(6, 24, 3, INK, 1);
  t.hline(6, 26, 3, INK, 1);
};

const glazeling: Paint = (t, back) => {
  // The glaze pour is the whole species, and it was only ever visible from the front. It now sits
  // on the toy as an asymmetric volume that swaps sides with the pose, so the reverse is the same
  // pastry turned around rather than the same pastry with a rule drawn on it.
  const m = (x: number) => (back ? 31 - x : x);
  t.cap(m(7), 26, m(22), 26, 3.2, R.cream);
  t.ell(m(22), 20, 5, 4.5, R.gold);
  t.ell(16, 18, 8, 6.5, R.cream);
  for (const [x, y] of [[12, 16], [18, 15], [14, 20]] as const) t.px(m(x), y, R.gold, 0);
  if (back) {
    // From behind: the glaze was poured over the crown and is still running — a lit cap proud of
    // the dome, three tongues down the nape, the dough's proving seam between them.
    t.cap(11, 13, 21, 13, 2.6, R.gold, { shade: 1 });
    t.px(10, 12, R.gold, 0);
    for (const [x, y, h] of [[12, 17, 2.4], [16, 18, 3], [20, 17, 2.2]] as const) {
      t.ell(x, y, 1.5, h, R.gold, { spec: false, shade: 1 });
      t.px(x - 1, Math.round(y - h * 0.5), R.gold, 0);
    }
    arc(t, 16, 18, 6.4, 5.2, 0.35, 2.8, R.cream, 4);
    t.vline(16, 22, 4, R.cream, 4);
    t.px(15, 22, R.cream, 1);
    for (const [x, y] of [[10, 21], [22, 23]] as const) t.px(x, y, R.rose, 1);
    return;
  }
  eyeDot(t, 19, 18);
  eyeDot(t, 23, 18);
  smile(t, 20, 22, 3);
};

const glazewyrm: Paint = (t, back) => {
  const m = (x: number) => (back ? 31 - x : x);
  const segs: [number, number, number, number, number][] = [
    [6, 27, 13, 25, 3.4], [13, 25, 20, 20, 3.4], [20, 20, 22, 13, 3.2],
  ];
  for (const [x0, y0, x1, y1, r] of segs) t.cap(m(x0), y0, m(x1), y1, r, R.cream);
  t.ell(m(20), 10, 6.5, 5.5, R.gold);
  for (const [x, y] of [[10, 26], [16, 22], [21, 16]] as const) t.px(m(x), y, R.gold, 0);
  if (back) {
    // The back used to be the front with the face left off — a mirrored blank. From behind the
    // coil turns its dorsal scutes to the camera, the occiput closes on a ringed seam, and the
    // glaze that ran off the crown is caught halfway down the neck in two tongues.
    for (const [x, y] of [[9, 26], [14, 24], [18, 21], [21, 17]] as const) {
      t.ell(m(x), y, 2.2, 1.8, R.cream, { spec: false, shade: 1 });
      t.px(m(x) - 1, y - 1, R.cream, 0);
    }
    t.ring(m(20), 10, 4.8, 4, R.gold, 3);
    t.vline(m(20), 5, 8, R.gold, 3);
    t.px(m(20) - 1, 6, R.gold, 0);
    for (const [x, y, h] of [[18, 16, 2.6], [22, 15, 2.2]] as const) {
      t.ell(m(x), y, 1.4, h, R.gold, { spec: false, shade: 1 });
      t.px(m(x) - 1, Math.round(y - h * 0.5), R.gold, 0);
    }
    for (const [x, y] of [[12, 21], [17, 26]] as const) t.px(m(x), y, R.rose, 1);
    return;
  }
  eyeBead(t, 17, 8);
  eyeBead(t, 22, 8);
  smile(t, 18, 13, 5);
};

const capling: Paint = (t, back) => {
  paws(t, 16, 4, 2.4, R.cream);
  t.ell(16, 22, 7, 5.5, R.cream);
  t.ell(16, 14, 8, 6, R.red);
  t.ell(16, 10, 9, 4, R.red);
  t.hline(8, 10, 16, R.gold, 1);
  if (back) {
    // The crown's pinch is at the front, so from behind the cap is one smoother dome closing on a
    // single back seam, with its own shadow under the curve.
    t.ell(16, 11.5, 7.6, 4.4, R.red, { dome: 0.6, spec: false, occ: false });
    t.vline(16, 7, 6, R.red, 3);
    t.px(15, 8, R.red, 0);
    arc(t, 16, 13, 7, 4.6, 0.3, Math.PI - 0.3, R.red, 3);
    // A hatband is a barrel, not a rule — and it knots at the back. Half the stories are the
    // hat's; the bow is the half the front has never once shown you.
    t.cap(8, 10, 23, 10, 1.3, R.gold, { shade: 2, spec: false });
    t.slab(16, 10, 1.8, 1.5, 0.7, R.gold, { spec: false });
    for (const dx of [-4, 4]) {
      t.ell(16 + dx, 10, 2.2, 1.6, R.gold, { shade: 1, spec: false, occ: false });
      t.px(16 + dx, 10, R.gold, 3);
    }
    t.px(15, 9, R.gold, 0);
    // The witness, from behind: the nape fold under the band, a spine, and two haunches.
    t.hline(11, 20, 11, R.cream, 1);
    t.cap(12, 22, 20, 22, 1.6, R.cream, { shade: 2, spec: false });
    t.ell(12.5, 25, 2, 2.1, R.cream, { shade: 1, spec: false, occ: false });
    t.ell(19.5, 25, 2, 2.1, R.cream, { shade: 2, spec: false, occ: false });
    t.vline(16, 23, 5, R.cream, 3);
    // The dome repaint takes the cap's one specular with it; it goes back by hand on the key
    // shoulder, above every band and bow pixel that follows.
    t.px(13, 8, WHITE);
    return;
  }
  t.ell(16, 16, 4.5, 3.5, R.cream, { dome: 0.5, rim: false, spec: false });
  eyeBead(t, 12, 14);
  eyeBead(t, 17, 14);
  smile(t, 14, 18, 4);
};

const nocapra: Paint = (t, back) => {
  // The horns were two flat gold cutouts and the muzzle patch was painted on the back of the head.
  // Horns are tapered volumes now, the muzzle belongs to the face, and the tail swaps sides.
  const m = (x: number) => (back ? 31 - x : x);
  paws(t, 16, 5.2, 3, R.cream);
  t.ell(16, 22, 8.5, 6.5, R.cream);
  t.cap(m(24), 18, m(28), 12, 1.6, R.cream, { spec: false });
  t.ell(16, 12, 8, 7, R.cream);
  blade(t, 11.5, 7.5, 8, 2, 2.2, R.gold);
  blade(t, 20.5, 7.5, 24, 2, 2.2, R.gold);
  if (back) {
    // From behind: the ears hang as flaps with the key on their leading edge, the fleece parts
    // down a nape strap between two shoulder blades, and the halter that keeps this one honest is
    // buckled across the withers.
    t.ell(8.5, 14, 2.6, 3.6, R.cream, { spec: false, shade: 1 });
    t.ell(23.5, 14, 2.6, 3.6, R.cream, { spec: false, shade: 2 });
    t.vline(7, 13, 3, R.paper, 0);
    t.vline(25, 13, 3, R.paper, 2);
    arc(t, 16, 12, 6.4, 5.6, 0.25, 2.9, R.cream, 4);
    t.cap(16, 15, 16, 21, 1.6, R.cream, { shade: 2, spec: false });
    t.px(15, 16, R.cream, 1);
    t.ell(11, 22, 3.2, 2.6, R.cream, { spec: false, shade: 1 });
    t.ell(21, 22, 3.2, 2.6, R.cream, { spec: false, shade: 2 });
    t.cap(11, 18, 21, 18, 1.1, R.bark, { shade: 1, spec: false });
    t.hline(11, 17, 5, R.bark, 0);
    t.px(16, 18, R.gold, 0);
    t.px(16, 19, R.gold, 2);
    for (const [x, y] of [[13, 26], [19, 26]] as const) t.px(x, y, R.paper, 2);
    return;
  }
  t.rect(13, 14, 6, 4, R.paper, 0);
  t.px(15, 15, INK, 1); t.px(16, 16, INK, 1);
  eyeBig(t, 10, 9);
  eyeBig(t, 17, 9);
  smile(t, 14, 16, 5);
};

const grassnudge: Paint = (t, back) => {
  // Every leaf on this toy was a flat `poly` cutout, which is why turning it around had nothing to
  // show. Blades are keeled volumes now, and the reverse is the half of the tuft the front hides.
  for (const x of [10, 22]) t.ell(x, 28, 3, 1.8, R.moss, { spec: false });
  t.ell(16, 22, 7, 6, R.green);
  blade(t, 9, 17, 4, 9, 2.4, R.moss);
  blade(t, 23, 17, 28, 9, 2.4, R.moss);
  t.ell(16, 13, 6.5, 5.5, R.green);
  blade(t, 16, 9, 16, 2, 2, R.gold);
  if (back) {
    // From behind: three more leaves, the ones the face was standing in front of, fanned off the
    // crown over a sheath at the nape — and the clod of root soil the whole tuft stands in.
    blade(t, 14, 16, 9, 4, 2.2, R.moss);
    blade(t, 18, 16, 23, 4, 2.2, R.moss);
    blade(t, 15, 16, 12, 2, 1.8, R.green);
    arc(t, 16, 13, 5.4, 4.6, 0.3, 2.85, R.green, 4);
    t.cap(16, 16, 16, 22, 1.5, R.green, { shade: 2, spec: false });
    t.px(15, 17, R.green, 1);
    t.ell(12, 21, 2.6, 2.2, R.green, { spec: false, shade: 1 });
    t.ell(20, 21, 2.6, 2.2, R.green, { spec: false, shade: 2 });
    t.ell(16, 26, 5, 2.4, R.bark, { spec: false, shade: 1 });
    for (const [x, y] of [[13, 25], [19, 26]] as const) t.px(x, y, R.bark, 0);
    return;
  }
  eyeBead(t, 12, 11);
  eyeBead(t, 17, 11);
  smile(t, 14, 15, 4);
};

const lawnlord: Paint = (t, back) => {
  // Crown blades were 1px `vline`s and the reverse was one rule of moss. The blades are lit bars
  // now, the handle swaps shoulders with the pose, and the back is the part a lawn lord would
  // actually be proud of.
  const m = (x: number) => (back ? 31 - x : x);
  paws(t, 16, 5.6, 3.2, R.green);
  t.ell(16, 22, 9.5, 7.5, R.green);
  t.cap(m(25), 18, m(29), 10, 1.4, R.bark, { spec: false });
  t.hline(back ? 2 : 24, 9, 6, R.bark, 1);
  t.ell(16, 11, 8.5, 7, R.green);
  for (const x of [9, 16, 23]) {
    slat(t, x, 7, x, 2, 1.3, R.moss, false);
    t.px(x, 1, R.gold, 0);
    t.px(x, 2, R.gold, 1);
  }
  if (back) {
    // From behind: the lawn is mown into its own back — four stripes laid alternately with and
    // against the nap, each one its own lit barrel — under a collar seam and two shoulder blades.
    arc(t, 16, 11, 7, 5.8, 0.3, 2.85, R.green, 4);
    t.cap(16, 14, 16, 19, 1.8, R.green, { shade: 2, spec: false });
    t.px(15, 15, R.green, 1);
    t.ell(10, 18, 2.6, 2.4, R.green, { spec: false, shade: 1 });
    t.ell(22, 18, 2.6, 2.4, R.green, { spec: false, shade: 2 });
    for (const [x, sh] of [[10, 0], [14, 2], [18, 0], [22, 2]] as const) {
      t.cap(x, 20, x, 27, 1.7, R.green, { shade: sh, spec: false });
    }
    for (const x of [10, 18]) t.px(x - 1, 21, R.green, 0);
    return;
  }
  eyeBig(t, 10, 8);
  eyeBig(t, 17, 8);
  smile(t, 14, 14, 5);
};

const seethtoad: Paint = (t, back) => {
  paws(t, 16, 5, 3, R.rose);
  t.ell(16, 22, 8, 6.5, R.rose);
  t.ell(16, 13, 7.5, 6, R.rose);
  // The lid it is trying to keep on: a bevelled case over a rolled flange, not two flat bars.
  t.slab(16, 5.5, 3.2, 2.2, 1, R.gold);
  t.cap(12, 8, 19, 8, 1.1, R.gold, { shade: 1, spec: false });
  t.px(12, 8, R.gold, 0);
  t.ell(16, 3, 2, 1.4, R.stone, { spec: false });
  t.px(15, 2, WHITE);
  if (back) {
    // From behind: the dorsum is all wart — five lit bosses stepping down a ridged spine — the
    // parotoid glands swell behind the jaw, and what it is holding in is venting past the lid.
    t.ell(10, 12, 2.6, 2.2, R.rose, { spec: false, shade: 1 });
    t.ell(22, 12, 2.6, 2.2, R.rose, { spec: false, shade: 2 });
    arc(t, 16, 13, 6, 4.8, 0.3, 2.85, R.rose, 3);
    t.cap(16, 15, 16, 24, 1.6, R.rose, { shade: 2, spec: false });
    t.px(15, 16, R.rose, 0);
    for (const [x, y] of [[11, 19], [21, 19], [12, 24], [20, 24], [16, 27]] as const) {
      t.ell(x, y, 2, 1.7, R.rose, { spec: false, shade: 1 });
      t.px(x - 1, y - 1, R.rose, 0);
    }
    for (const [x, y, r] of [[9, 6, 1.8], [24, 4, 1.5]] as const) {
      t.ell(x, y, r, r * 0.85, R.paper, { spec: false, shade: 1 });
      t.px(x - 1, y - 1, R.paper, 0);
    }
    return;
  }
  eyeBead(t, 11, 11);
  eyeBead(t, 18, 11);
  frown(t, 13, 16, 6);
};

const maldmaw: Paint = (t, back) => {
  // The lid left; the argument stayed. A pot-bellied maw boiling with its rolled rim wide open,
  // steam going straight up, and the lid itself tumbling out of frame on the right.
  paws(t, 16, 6.4, 3.2, R.rose, { shade: 2 });
  t.ell(16, 22, 10, 7.5, R.rose, { shade: 1 });
  t.cap(7, 17, 5, 23, 2.6, R.rose, { shade: 2 });
  t.cap(25, 17, 27, 23, 2.6, R.rose, { shade: 2 });
  t.ell(16, 13, 9, 7, R.rose, { shade: 1 });
  // The rim it used to close on: a rolled lip catching the key, with nothing sitting in it.
  // Steel, not rose: R.rose tops out at LCD.white and the lip flooded the whole crown white.
  t.cap(8, 7, 24, 7, 2, R.slate);
  for (const x of [10, 13, 16, 19, 22]) t.darken(x, 8);
  for (const [x, y, r] of [[12, 2.8, 2.6], [19, 1.8, 2.2], [16, 4.6, 1.8]] as const) {
    t.ell(x, y, r, r * 0.85, R.paper, { spec: false, shade: 1 });
  }
  t.px(11, 1.8, R.paper, 0);
  if (back) {
    // From behind: the weld seam, the scorch ring where it has been boiling dry, the rim bolts,
    // and the lid on the floor behind it, face down, where it landed weeks ago.
    t.vline(16, 16, 11, R.rose, 3);
    t.ring(16, 22, 6, 4.5, R.rose, 3);
    for (const x of [9, 13, 16, 19, 23]) rivet(t, x, 7, R.rose);
    t.ell(16, 13, 5, 4, R.rose, { shade: 1, spec: false });
    t.ell(27, 26, 4, 2, R.gold, { spec: false, shade: 1 });
    t.hline(25, 25, 5, R.gold, 0);
    return;
  }
  eyeBig(t, 10, 10);
  eyeBig(t, 18, 10);
  brow(t, 10, 10, 1);
  brow(t, 18, 10, -1);
  grin(t, 12, 17, 8, 4);
  // The lid, mid-tumble. Gold, edge-on, and not coming back.
  t.ell(28, 12, 3.4, 1.8, R.gold, { spec: false });
  t.hline(26, 11, 5, R.gold, 0);
  t.px(28, 9, R.gold, 1);
  t.darken(29, 13);
};

const deluling: Paint = (t, back) => {
  t.poly([[3, 14], [13, 12], [13, 22], [4, 24]], R.rose, { grad: true });
  t.poly([[19, 12], [29, 14], [28, 24], [19, 22]], R.rose, { grad: true });
  t.ell(16, 18, 4, 6.5, R.cream);
  t.ell(16, 11, 5, 4.5, R.cream);
  t.hline(11, 10, 10, R.rose, 1);
  t.ring(13, 10, 2.2, 1.8, R.rose, 1);
  t.ring(19, 10, 2.2, 1.8, R.rose, 1);
  if (back) {
    // Rose lenses seen from the inside: there is no tint to look through from back here, only
    // the frames' back rims, the hinges, and the strap across the nape. It is still squinting.
    for (const [cx, sh] of [[7, 1], [24, 2]] as const) {
      t.ell(cx, 18, 3.4, 4.4, R.rose, { shade: sh, spec: false, occ: false });
      t.vline(cx, 14, 9, R.rose, 3);
      t.px(cx - 1, 15, R.rose, 0);
    }
    t.cap(13, 13, 19, 13, 1.2, R.rose, { shade: 1, spec: false });
    for (const x of [12, 20]) rivet(t, x, 13, R.gold);
    t.cap(13, 15, 19, 15, 1.2, R.cream, { shade: 2, spec: false });
    t.ell(14, 20, 1.8, 2.2, R.cream, { shade: 1, spec: false, occ: false });
    t.ell(18, 20, 1.8, 2.2, R.cream, { shade: 2, spec: false, occ: false });
    t.vline(16, 16, 8, R.cream, 3);
    // The strap's contact band takes the body's one specular; it goes back by hand beside it.
    t.px(14, 17, WHITE);
    return;
  }
  eyeBead(t, 13, 9);
  eyeBead(t, 17, 9);
  smile(t, 14, 14, 4);
};

const vibeling: Paint = (t, back) => {
  // Pocket sun: rays first, then the cream underbody, then the gold dome over both.
  for (const a of [-2.7, -2.2, -1.57, -0.94, -0.44]) {
    const c = Math.cos(a), s = Math.sin(a);
    slat(t, 16 + c * 7.5, 14 + s * 7, 16 + c * 11.5, 14 + s * 10.5, 1.2, R.gold);
  }
  paws(t, 16, 7, 2.2, R.gold);
  t.ell(16, 25, 7, 4.5, R.cream);
  t.ell(16, 14, 8.5, 8, R.gold);
  if (back) {
    t.vline(16, 19, 8, R.cream, 3);
    t.ring(16, 14, 4.5, 4, R.gold, 3);
    // The shades' temple arms hook round the dome — the back is not a mirrored front.
    for (const x of [8, 24]) t.vline(x, 15, 4, R.slate, 1);
    t.hline(9, 15, 14, R.slate, 2);
    return;
  }
  eyeBig(t, 10, 9);
  eyeBig(t, 19, 9);
  // Shades pushed down to rate the puddle: the lenses are glossy slabs, the eyes still read.
  t.hline(9, 15, 14, R.slate, 0);
  t.slab(12, 17, 3.2, 2.2, 1.1, R.slate, { shade: 1 });
  t.slab(20, 17, 3.2, 2.2, 1.1, R.slate, { shade: 1 });
  t.px(16, 16, R.slate, 0);
  t.px(16, 17, R.slate, 1);
  t.px(10, 16, WHITE);
  t.px(18, 16, WHITE);
  smile(t, 14, 20, 5);
};

const vibewyrm: Paint = (t, back) => {
  const m = (x: number) => (back ? 31 - x : x);
  for (const [x0, y0, x1, y1] of [[15, 5, 11, 1], [19, 4, 18, 0], [24, 4, 26, 0], [27, 7, 30, 3]] as const) {
    slat(t, m(x0), y0, m(x1), y1, 1.2, R.gold);
  }
  t.cap(m(6), 26, m(13), 23, 3.6, R.gold);
  t.cap(m(13), 23, m(18), 17, 3.4, R.gold);
  t.cap(m(18), 17, m(19), 11, 3.2, R.gold);
  t.ell(m(20), 9, 7.5, 6.5, R.cream);
  if (back) {
    t.ring(m(20), 9, 4.5, 4, R.cream, 3);
    t.vline(m(20), 3, 12, R.cream, 3);
    for (const x of [14, 26]) t.vline(m(x), 10, 3, R.slate, 1);
    return;
  }
  eyeBig(t, 16, 5);
  eyeBig(t, 22, 5);
  t.hline(14, 10, 12, R.slate, 0);
  t.slab(17, 12, 2.8, 1.8, 0.9, R.slate, { shade: 1 });
  t.slab(23, 12, 2.8, 1.8, 0.9, R.slate, { shade: 1 });
  t.px(20, 11, R.slate, 0);
  t.px(20, 12, R.slate, 1);
  t.px(15, 11, WHITE);
  t.px(21, 11, WHITE);
  smile(t, 18, 14, 6);
};

const draftkit: Paint = (t, back) => {
  // Pencil tucked behind the note: graphite point, brass ferrule, rose eraser.
  slat(t, 26, 26, 28, 8, 1.4, R.gold, false);
  t.px(28, 7, R.slate, 1);
  t.px(28, 8, R.ink, 1);
  t.ell(26.2, 26.6, 1.5, 1.5, R.rose, { spec: false });
  t.slab(16, 19.5, 8.5, 9.5, 2, R.paper);
  // Dog-ear: the corner is cut out of the silhouette, then folded back across the face.
  for (let y = 10; y <= 18; y++) for (let x = 17; x <= 25; x++) if (x - 17 > y - 10) t.erase(x, y);
  t.poly([[17, 10], [24, 17], [17, 17]], R.paper, { tone: back ? 3 : 2 });
  for (let i = 0; i <= 7; i++) t.px(17 + i, 10 + i, R.paper, back ? 3 : 0);
  if (back) {
    t.slab(15, 22, 6, 5.5, 1.5, R.paper, { shade: 1, spec: false });
    for (const y of [20, 23, 26]) t.hline(11, y, 9, R.paper, 3);
    return;
  }
  eyeBig(t, 10, 19);
  eyeBig(t, 19, 19);
  smile(t, 14, 24, 5);
  // The crease is load-bearing: a lit lip over its own shadow.
  t.hline(8, 26, 17, R.paper, 0);
  t.hline(8, 27, 17, R.paper, 3);
};

const postwyrm: Paint = (t, back) => {
  const m = (x: number) => (back ? 31 - x : x);
  t.cap(m(6), 26, m(12), 23, 3.4, R.paper);
  t.cap(m(12), 23, m(18), 17, 3.4, R.paper);
  t.cap(m(18), 17, m(20), 11, 3.2, R.paper);
  // The spine it grew: folded-paper crest fins with a creased leading edge.
  for (const [x, y, h] of [[6, 23, 5], [11, 20, 6], [15, 17, 6]] as const) {
    t.poly([[m(x) - 2, y], [m(x) + 2, y], [m(x), y - h]], R.paper, { grad: true });
    t.line(m(x), y - h, m(x), y, R.paper, 3);
  }
  t.ell(m(22), 8, 7.5, 6.5, R.cream);
  if (back) {
    // From behind it is a filed stack: shingled sheet-plates banded down the spine, each its own
    // key-lit barrel with a backlight rim on the far side.
    // R3 narrowed the rig's rim to a 1px lip, and these plates had been leaning on the old 3px
    // halo to separate — without it the stack melted into one smooth cream tube. So every plate
    // now carries its own hard fold lip on the key side and its own seam on the shadow side,
    // banded across the body instead of along it.
    for (const [cx, cy, r] of [[24.6, 25.9, 3], [21.2, 24.2, 3], [17.8, 21.6, 2.8],
      [14.6, 18.6, 2.6], [12.4, 16.6, 2.3]] as const) {
      const ax = cx - 1.4, ay = cy - 1.4, bx = cx + 1.4, by = cy + 1.4;
      t.cap(ax, ay, bx, by, r, R.cream, { shade: 1, spec: false });
      t.cap(ax + 1, ay - 1, bx + 1, by - 1, r - 1.1, R.cream, { flat: 0, occ: false });
      t.cap(ax - 1.1, ay + 1.1, bx - 1.1, by + 1.1, r - 1.5, R.stone, { flat: 3, occ: false });
    }
    // A crease only ever darkens the toy it is folded into — never grows the silhouette.
    const crease = (x: number, y: number, ramp: Ramp, tone: number) => {
      if (t.has(x, y)) t.px(x, y, ramp, tone);
    };
    // Where one sheet laps the next: a sepia crease under its own lit fold lip.
    for (const [gx, gy] of [[22.9, 25.05], [19.5, 22.9], [16.2, 20.1], [13.5, 17.6]] as const) {
      for (let i = -2; i <= 2; i++) {
        const x = Math.floor(gx + i), y = Math.floor(gy - i);
        crease(x, y, R.stone, 2);
        t.lighten(x - 1, y - 1);
      }
    }
    // The crest still reads on the back silhouette: a lit fold edge over each fin's shaded face.
    for (const [x, y, h] of [[6, 23, 5], [11, 20, 6], [15, 17, 6]] as const) {
      for (let k = 1; k < h; k++) {
        const hw = Math.round(2 * (1 - k / h));
        crease(m(x) - hw, y - k, R.paper, 0);
        if (hw > 0) crease(m(x) + hw, y - k, R.stone, 2);
      }
    }
    // The publish button from behind: the bezel boss it is riveted through, its lamp still lit.
    // Too small for the rig's own rim, so the crown and the backlight kiss are hand-stepped.
    t.ell(17.8, 21.6, 2.6, 2.5, R.slate, { spec: false });
    arc(t, 17.8, 21.6, 1.7, 1.6, Math.PI * 0.8, Math.PI * 1.8, R.green, 0);
    arc(t, 17.8, 21.6, 1.7, 1.6, Math.PI * 1.8, Math.PI * 2.8, R.green, 2);
    t.px(18, 22, R.green, 1);
    t.px(16, 20, WHITE);
    t.px(19, 23, R.slate, 0);
    // A brass staple binds the last sheet to the neck.
    t.px(12, 15, R.gold, 0);
    t.px(13, 15, R.gold, 1);
    t.darken(13, 16);
    // The head from behind: the whorl it spirals out of, over the seam down the sheet.
    t.ring(m(22), 8, 4.5, 4, R.stone, 2);
    t.ring(m(22) + 1, 9, 2.2, 2, R.stone, 3);
    t.vline(m(22), 2, 12, R.cream, 3);
    return;
  }
  eyeBig(t, 17, 5);
  eyeBig(t, 23, 5);
  nose(t, 21, 9);
  grin(t, 19, 11, 7, 3);
  // The publish button: a bezelled disc, one white glint, a green dot at 16px.
  t.ell(14, 21, 2.8, 2.6, R.paper, { shade: -1 });
  t.ell(14, 21, 1.9, 1.8, R.green);
  t.px(13, 20, WHITE);
};

const spoilpup: Paint = (t, back) => {
  // R4: seven colours on the back, eight on the front, and a pale run down the chin that painted
  // it as *spoiled* — dairy, not spoilers. Focus C settled the species: cone ears and the last
  // page. So the ears are real cones with a dark bore, and the book is the prop it holds.
  paws(t, 16, 4.8, 2.8, R.navy);
  t.ell(16, 23, 8, 6, R.navy);
  t.ell(16, 11, 7.5, 6.5, R.navy);
  // Cone ears: a tapered shell from the skull out to a mouth ring, with the bore darkest where it
  // faces away from the key. A cone reads as a cone only if you can see into one end of it.
  for (const s of [-1, 1] as const) {
    t.poly([[16 + s * 4, 8], [16 + s * 14, 4], [16 + s * 10, 0]], R.paper, { grad: true });
    t.ell(16 + s * 12, 2, 2.6, 2.4, R.paper, { spec: false });
    if (!back) t.ell(16 + s * 12, 2, 1.4, 1.3, R.rose, { flat: 2, occ: false });
  }
  // Signature: the book, already read to the end. Red boards, a paper block, and the last page
  // turned down — the dog-ear is the joke and it survives the icon because it breaks the corner.
  t.slab(10, 22, 3.6, 4.2, 1.2, R.red);
  t.slab(10.6, 22, 2.8, 3.6, 0.9, R.paper, { shade: -1 });
  t.px(12, 19, R.paper, 3);
  t.px(12, 18, R.paper, 2);
  t.vline(9, 25, 3, R.gold, 1);
  t.px(9, 25, R.gold, 0);
  if (back) {
    // From behind: the cones' solid shells, the nape the head sits down onto, the spine, and the
    // book closed on its ribbon — from this side the ending is still in there.
    t.cap(10, 16, 22, 16, 1.6, R.navy, { shade: 1, spec: false });
    t.vline(16, 18, 9, R.navy, 3);
    t.vline(8, 19, 7, R.red, 2);
    for (const y of [20, 23] as const) t.px(8, y, R.gold, 1);
    for (const y of [20, 26] as const) t.px(19, y, R.navy, 4);
    return;
  }
  t.ell(16, 14.5, 4.2, 3, R.ice, { dome: 0.5, rim: false, spec: false });
  eyeBig(t, 11, 7, R.ice);
  eyeBig(t, 18, 7, R.ice);
  brow(t, 11, 7, -1);
  brow(t, 18, 7, 1);
  nose(t, 15, 13);
  // Psst. A small round mouth, mid-whisper — this one does not grin, it tells you.
  t.ell(16, 16, 1.6, 1.4, INK, { flat: 1, occ: false });
};

const leakmaw: Paint = (t, back) => {
  const m = (x: number) => (back ? 31 - x : x);
  // R4: seven colours on the back, two gold horns doing the work of a species about spoilers, and
  // a pale run that still said dairy. "The cone became a megaphone" is the whole design brief, so
  // one ear stayed a cone and the other grew into the loudest volume in the frame. The toy is
  // asymmetric now, which is why this one mirrors for the back pose instead of repeating itself.
  paws(t, 16, 5.6, 3.4, R.navy);
  t.ell(16, 23, 9.5, 7.5, R.navy);
  t.ell(m(13), 11, 8, 7, R.navy);
  // The ear that stayed an ear.
  t.poly([[m(10), 9], [m(2), 5], [m(5), 1]], R.paper, { grad: true });
  t.ell(m(3), 3, 2.5, 2.3, R.paper, { spec: false });
  if (!back) t.ell(m(3), 3, 1.3, 1.2, R.rose, { flat: 2, occ: false });
  // Signature: the megaphone. A flared shell with a brass mouth band and a dark bore — three
  // materials meeting on one prop, which is what makes it read at 16px as a thing and not a blob.
  t.poly([[m(18), 14], [m(21), 1], [m(30), 11]], R.paper, { grad: true });
  t.ell(m(25.5), 6, 5.2, 5.6, R.paper);
  t.ring(m(25.5), 6, 4.4, 4.8, R.gold, 1);
  t.px(m(23), 2, WHITE);
  // The books it read first. Two of them — the sequel's ending is out as well.
  t.slab(m(7.4), 23.6, 3.2, 1.7, 0.8, R.gold, { shade: 1 });
  t.slab(m(7), 26.6, 3.6, 1.8, 0.8, R.red, { shade: 1 });
  if (back) {
    // From behind: the flare's closed reverse with the strap that carries it, the cone's solid
    // shell, the nape, the spine, and the two books showing nothing but their spines.
    t.ell(m(25.5), 6, 3.6, 4, R.paper, { shade: 2, spec: false, occ: false });
    t.cap(m(23), 10, m(17), 16, 1.4, R.bark, { spec: false });
    t.cap(9, 15, 23, 15, 1.9, R.navy, { shade: 1, spec: false });
    t.vline(16, 17, 10, R.navy, 3);
    for (const y of [23, 24] as const) t.px(m(7), y, R.gold, 3);
    for (const y of [26, 27] as const) t.px(m(7), y, R.red, 2);
    return;
  }
  t.ell(25.5, 6, 3.4, 3.7, R.red, { flat: 1, occ: false });
  t.ell(25.5, 6, 1.6, 1.8, R.red, { flat: 2, occ: false });
  t.ell(13, 14.5, 5, 3.4, R.ice, { dome: 0.5, rim: false, spec: false });
  eyeBig(t, 8, 7, R.ice);
  eyeBig(t, 15, 7, R.ice);
  brow(t, 8, 7, 1);
  brow(t, 15, 7, -1);
  nose(t, 12, 13);
  grin(t, 9, 15, 8, 4);
};

const queueling: Paint = (t, back) => {
  paws(t, 16, 5, 2.6, R.paper, { shade: 1 });
  t.slab(16, 18, 6.5, 9, 1.5, R.paper);
  // Torn off a roll: the perforation is cut out of the silhouette, not drawn on it.
  for (let x = 11; x <= 21; x += 2) t.erase(x, 9);
  t.slab(16, 12, 5.5, 2, 1, R.red, { shade: 1 });
  if (back) {
    t.slab(16, 19, 5, 7, 1.2, R.paper, { shade: 1, spec: false });
    for (let x = 13; x <= 19; x += 2) t.vline(x, 15, 8, INK, 1);
    t.hline(11, 25, 11, R.paper, 3);
    return;
  }
  eyeBig(t, 11, 15);
  eyeBig(t, 18, 15);
  t.hline(15, 20, 3, INK, 1);
  // The number it believes in, stamped on a brass plate.
  t.slab(16, 24, 5, 3, 1, R.gold, { shade: 1 });
  t.hline(14, 23, 5, INK, 1);
  t.line(18, 23, 15, 26, INK, 1);
};

const linebaron: Paint = (t, back) => {
  t.slab(16, 23, 8.5, 6.5, 2, R.paper);
  for (let x = 9; x <= 23; x += 2) t.erase(x, 29);
  t.ell(16, 14, 7, 5.5, R.cream);
  if (back) {
    t.ring(16, 14, 4, 3.4, R.cream, 3);
    t.vline(16, 10, 9, R.cream, 3);
    t.slab(16, 24, 6, 4.5, 1.5, R.paper, { shade: 1, spec: false });
    for (let x = 12; x <= 20; x += 2) t.vline(x, 21, 7, INK, 1);
  } else {
    t.ell(16, 16.5, 4.5, 2.4, R.paper, { dome: 0.5, rim: false, spec: false });
    eyeBig(t, 10, 12);
    eyeBig(t, 19, 12);
    nose(t, 15, 16);
    smile(t, 14, 18, 5);
    t.slab(16, 24, 5.5, 3, 1.2, R.gold, { shade: 1 });
    t.vline(16, 22, 5, INK, 1);
    t.px(15, 23, INK, 1);
    t.hline(14, 26, 5, INK, 1);
  }
  // The counter bell it grew: brass dome on a rim plate under a struck knob.
  t.ell(16, 6, 5.5, 4.2, R.gold);
  t.cap(9.5, 9.5, 22.5, 9.5, 1.4, R.gold, { shade: 1, spec: false });
  t.cap(16, 1, 16, 3, 1.1, R.gold, { shade: -1, spec: false });
  t.px(13, 4, WHITE);
  if (back) return;
  for (const [x, y] of [[7, 4], [8, 6], [25, 4], [24, 6]] as const) t.px(x, y, R.gold, 0);
};

const receiptling: Paint = (t, back) => {
  // Its body IS the slip: a paper ribbon fed off the till drum it wears for a head, standing on
  // the zigzag hem it was torn from. The tear is the footprint, so the silhouette names it.
  t.slab(16, 20, 7, 9, 2.6, R.paper);
  for (const x of [10, 13, 16, 19, 22]) {
    t.cap(x, 27, x, 29.4, 1.5, R.paper, { shade: 1, spec: false });
    t.px(x, 26, R.paper, 0);
    t.darken(x + 1, 29);
    t.darken(x + 2, 28);
  }
  t.ell(16, 9, 7, 6, R.cream);
  // The feed: paper still curling out from under the drum, lit along the fold.
  t.cap(10, 15, 22, 15, 1.8, R.cream, { shade: -1 });
  if (back) {
    // From behind: the spindle it turns on, the gummed unprinted reverse, and the carbon ghost of
    // the total showing through the paper backwards.
    t.ell(16, 9, 2.6, 2.4, R.slate, { spec: false });
    t.px(15, 8, WHITE);
    t.ring(16, 9, 5, 4.2, R.cream, 3);
    t.vline(16, 15, 12, R.paper, 3);
    for (const y of [18, 21, 26]) t.hline(11, y, 8, R.paper, 2);
    t.rect(11, 23, 10, 2, R.paper, 2);
    t.px(10, 23, R.paper, 0);
    // The stamp soaked through: thermal paper is thin, so PAID reads from the back too — mirrored,
    // bled out a step, and carrying the tonal range this pose was otherwise missing.
    t.slab(10, 20, 3.2, 2.8, 1.2, R.rose, { shade: 2 });
    t.hline(9, 19, 4, R.red, 2);
    t.hline(9, 21, 4, R.red, 2);
    t.px(9, 18, R.rose, 1);
    return;
  }
  // Itemized: the joke, the delivery, the pause — then the one line anybody reads.
  for (const y of [18, 21, 26]) t.hline(11, y, 8, R.slate, 1);
  t.rect(11, 23, 10, 2, R.slate, 1);
  eyeBig(t, 10, 6);
  eyeBig(t, 18, 6);
  smile(t, 14, 11, 5);
  // Stamped and closed out, over its own print.
  t.slab(22, 20, 3.4, 3, 1.2, R.rose, { shade: 1 });
  t.hline(20, 19, 4, INK, 1);
  t.hline(20, 21, 4, INK, 1);
};

const auditlord: Paint = (t, back) => {
  paws(t, 16, 9.5, 2.6, R.paper, { shade: 1 });
  t.ell(16, 22, 9.5, 7.5, R.paper);
  t.cap(8, 17, 5, 22, 2.4, R.paper, { shade: 1 });
  t.cap(24, 17, 27, 22, 2.4, R.paper, { shade: 1 });
  t.ell(16, 11, 8.5, 7, R.cream);
  if (back) {
    // From behind: the ledger's cloth spine with its raised bands, the ribbon's other end hanging
    // out the tail, and the nape seam. The slip grew a binding.
    t.cap(16, 16, 16, 27, 2.6, R.bark, { spec: false });
    for (const y of [18, 21, 24]) {
      t.hline(14, y, 5, R.bark, 3);
      t.px(14, y - 1, R.bark, 0);
    }
    t.ring(16, 11, 5, 4.2, R.cream, 3);
    t.vline(16, 4, 9, R.cream, 3);
    t.vline(17, 27, 3, R.rose, 1);
    t.px(17, 27, R.rose, 0);
    return;
  }
  eyeBig(t, 10, 8);
  eyeBig(t, 18, 8);
  smile(t, 14, 14, 5);
  // The monocle the timestamp goes through. A `ring`, not a `hoop` — a hoop would bore the lens
  // straight through the face and the outline pass would ink the hole.
  t.ring(12, 10, 3.4, 3.2, R.gold, 1);
  t.px(10, 8, R.gold, 0);
  t.line(15, 12, 17, 15, R.gold, 2);
  // The ledger, open: two gold boards on a cloth spine, ruled page blocks, and the ribbon still
  // marking the line that did not add up.
  t.cap(16, 17, 16, 26, 1.5, R.bark, { spec: false });
  for (const [cx, sh] of [[11.5, 0], [20.5, 1]] as const) {
    t.slab(cx, 21.5, 4.2, 5, 1.3, R.gold, { shade: sh });
    t.slab(cx, 21.5, 3.2, 4, 1, R.paper, { shade: -1 });
    for (const y of [20, 22, 24]) t.hline(Math.round(cx) - 2, y, 4, R.slate, 1);
  }
  t.vline(17, 25, 4, R.rose, 1);
  t.px(17, 25, R.rose, 0);
};

const clipfinch: Paint = (t, back) => {
  // R4: the prop was a clipboard — nine colours and the wrong noun. Focus C settled it: this bird
  // carries two seconds of *film*. So the prop is a strip with punched sprockets down both edges
  // and exactly two frames in it, which is the joke and also the only thing on the toy with a
  // repeating rhythm — at 16px a perforated ribbon is unmistakable where a grey board was not.
  for (const x of [13, 19] as const) {
    slat(t, x, 25, x, 28.4, 1, R.gold, false);
    t.hline(x - 1, 29, 3, R.gold, 2);
    t.px(x - 1, 29, R.gold, 1);
  }
  t.ell(16, 21, 7, 6, R.rose);
  // Tail: three stepped feathers, so the back end is a shape and not the body's other pole.
  for (const [ax, ay] of [[26, 22], [27, 25], [24, 27]] as const) blade(t, 20, 23, ax, ay, 2.2, R.rose);
  t.ell(14, 11, 6, 5.5, R.rose);
  blade(t, 13, 6, 10, 1, 1.7, R.rose);
  blade(t, 19, 12.5, 27, 11, 2.2, R.gold);
  // Signature: the strip. Ink stock, cream sprockets, two lit frames — three values, hard edges.
  t.slab(8, 20, 2.7, 6.5, 0.8, R.ink, { spec: false });
  for (let y = 15; y <= 25; y += 2) {
    t.px(6, y, R.cream, 1);
    t.px(10, y, R.cream, 1);
  }
  t.rect(7, 16, 3, 4, R.ice, 1);
  t.rect(7, 21, 3, 4, R.ice, 2);
  t.px(8, 17, R.rose, 1);
  t.px(8, 22, R.gold, 1);
  // Snipped short, and not straight.
  for (const [x, y] of [[7, 26], [9, 26], [6, 14], [8, 14]] as const) t.erase(x, y);
  if (back) {
    // From behind: the nape, the wing folded over the flank with its coverts stepped, the tail's
    // shafts, and the strip's blank backing — the setup was on this side and it has been sold.
    t.cap(10, 16, 20, 16, 1.4, R.rose, { shade: 1, spec: false });
    t.ell(17, 21, 3.4, 4, R.rose, { shade: 1, spec: false });
    for (const y of [19, 21, 23] as const) t.hline(15, y, 5, R.rose, 3);
    t.vline(14, 6, 5, R.rose, 3);
    t.rect(7, 16, 3, 9, R.slate, 1);
    t.px(7, 17, R.slate, 0);
    return;
  }
  eyeBig(t, 10, 8, R.gold);
  eyeBig(t, 15, 8, R.gold);
  t.px(12, 13, INK, 1);
  t.px(17, 13, INK, 1);
};

const cliplord: Paint = (t, back) => {
  // R4: the back was the front body with one rule across it — nine colours — and the front still
  // carried clipfinch's clipboard. Same correction, one stage louder: the clip is the whole show,
  // so the toy is wearing film. Two strips, both perforated, both cut short of anything that
  // would explain them.
  for (const x of [13, 20] as const) {
    slat(t, x, 26, x, 28.4, 1.2, R.gold, false);
    t.hline(x - 2, 29, 5, R.gold, 2);
    t.px(x - 2, 29, R.gold, 1);
  }
  t.ell(16, 22, 9, 7, R.rose);
  for (const [ax, ay] of [[28, 22], [29, 26], [25, 28]] as const) blade(t, 22, 24, ax, ay, 2.4, R.rose);
  t.ell(15, 11, 7.5, 6.5, R.rose);
  blade(t, 21, 12.5, 30, 11, 2.4, R.gold);
  // Signature: two strips of it, hung where a lesser toy would have a scarf.
  t.slab(7, 16, 3.2, 10, 0.8, R.ink, { spec: false });
  t.slab(25, 20, 2.7, 6.5, 0.8, R.ink, { spec: false });
  for (let y = 7; y <= 25; y += 2) {
    t.px(4, y, R.cream, 1);
    t.px(10, y, R.cream, 1);
  }
  for (let y = 15; y <= 25; y += 2) {
    t.px(23, y, R.cream, 1);
    t.px(27, y, R.cream, 1);
  }
  if (back) {
    // From behind: the strips' blank backing, the nape, the wing folded over the flank with its
    // coverts stepped, the tail's shafts and the crown's far band. Context is not back here either.
    t.rect(5, 8, 5, 17, R.slate, 1);
    t.px(5, 9, R.slate, 0);
    t.rect(24, 16, 3, 9, R.slate, 1);
    t.px(24, 17, R.slate, 0);
    t.cap(11, 16, 21, 16, 1.6, R.rose, { shade: 1, spec: false });
    t.ell(18, 21, 3.8, 4.4, R.rose, { shade: 1, spec: false });
    for (const y of [19, 21, 23] as const) t.hline(16, y, 5, R.rose, 3);
    t.vline(15, 5, 6, R.rose, 3);
    crown(t, 15, 2, 9);
    return;
  }
  // Four frames on the long strip, two on the short one. Every one of them is the good part.
  for (const [y, tone] of [[8, 1], [13, 2], [18, 1], [23, 2]] as const) {
    t.rect(5, y, 5, 4, R.ice, tone);
    t.px(7, y + 1, R.rose, 1);
    t.px(8, y + 2, R.gold, 1);
  }
  for (const [y, tone] of [[16, 1], [21, 2]] as const) {
    t.rect(24, y, 3, 4, R.ice, tone);
    t.px(25, y + 1, R.gold, 1);
  }
  for (const [x, y] of [[6, 26], [8, 26], [24, 26], [26, 26]] as const) t.erase(x, y);
  crown(t, 15, 2, 9);
  eyeBig(t, 11, 8, R.gold);
  eyeBig(t, 16, 8, R.gold);
  t.px(13, 13, INK, 1);
  t.px(18, 13, INK, 1);
};

const toquelin: Paint = (t, back) => {
  // There is no head under there: the toy IS the tuque. So the hollow under the brim is the face,
  // the boots carry it, and the load-bearing pompom is the tallest thing in the frame.
  for (const x of [12, 20]) {
    t.cap(x, 25, x, 28.4, 2.2, R.cream, { shade: 1, spec: false });
    t.hline(x - 2, 29, 5, R.cream, 2);
    t.px(x - 2, 29, R.cream, 1);
  }
  t.ell(16, 17, 9, 8, R.navy);
  t.ell(15, 9, 7, 5.5, R.navy);
  t.ell(19, 5, 4.4, 3.4, R.navy);
  // The row it was knit in: paired flecks, each a lit stitch over its own dip.
  for (const [x, y] of [[10, 12], [13, 10.5], [16, 10], [19, 10.5], [22, 12]] as const) {
    t.px(x, y, R.ice, 1);
    t.px(x + 1, y, R.ice, 3);
    t.darken(x, y + 1);
  }
  if (back) {
    // From behind: the decrease spiral at the crown, the brim's join seam, and the knit ribs
    // running the other way — plus the pompom's tie, which is what makes it load-bearing.
    t.ring(15, 9, 4.6, 3.4, R.navy, 3);
    t.vline(16, 12, 12, R.navy, 3);
    for (const x of [10, 13, 16, 19, 22]) t.vline(x, 13, 8, R.navy, 1);
    t.cap(7.5, 22, 24.5, 22, 2.6, R.navy, { shade: 1 });
    t.hline(8, 21, 17, R.navy, 1);
    t.ell(21, 2.5, 3, 2.6, R.cream, { spec: false });
    t.cap(20, 5, 19, 7, 1.2, R.paper, { spec: false, shade: 1 });
    return;
  }
  // The hollow, and two glossy eyes floating in it with nothing behind them.
  t.ell(16, 15, 6, 3.6, INK, { flat: 1, occ: false });
  arc(t, 16, 15, 6, 3.6, Math.PI, Math.PI * 2, R.navy, 1);
  eyeHollow(t, 13, 15);
  eyeHollow(t, 19, 15);
  // Rolled brim over the hollow's hem, with the ribs it was knit in.
  t.cap(7.5, 22, 24.5, 22, 2.6, R.navy, { shade: -1 });
  for (const x of [9, 12, 15, 18, 21, 24]) {
    t.darken(x, 21);
    t.darken(x, 23);
  }
  t.ell(21, 2.5, 3, 2.6, R.cream, { spec: false });
  t.px(20, 1, WHITE);
  for (const [x, y] of [[20, 3], [22, 2], [21, 4]] as const) t.darken(x, y);
};

const tuquelord: Paint = (t, back) => {
  // The tuque annexed a scarf. Same headless rig as toquelin one size up, with earflaps, braided
  // ties, and a striped scarf wound round the brim — short turns with the hat showing between
  // them, knotted over the gap between the boots with one loose end hanging clear down it.
  for (const x of [11, 21]) {
    t.cap(x, 24, x, 28.4, 2.6, R.cream, { shade: 1, spec: false });
    t.hline(x - 3, 29, 6, R.cream, 2);
    t.px(x - 3, 29, R.cream, 1);
  }
  t.ell(16, 17, 10, 9, R.navy);
  // Earflaps, each its own hanging volume.
  t.ell(6, 18, 3, 5, R.navy);
  t.ell(26, 18, 3, 5, R.navy);
  t.ell(15, 8, 8, 6, R.navy);
  t.ell(20, 3.5, 5, 3.6, R.navy);
  if (back) {
    // From behind: the crown spiral, the flaps' lining seam, both braided ties knotted at the
    // nape, and the same coil carrying round — it used to stop at the silhouette, so the scarf
    // simply vanished on this pose.
    t.ring(15, 8, 5.4, 3.8, R.navy, 3);
    t.vline(16, 12, 12, R.navy, 3);
    for (const x of [9, 12, 15, 18, 21, 24]) t.vline(x, 13, 8, R.navy, 1);
    t.cap(6, 22, 26, 22, 3, R.navy, { shade: 1 });
    t.hline(7, 20, 19, R.navy, 1);
    // Wound, not sashed. Two short turns ride the nape with navy showing between them, a third
    // crosses below, and the bight — the far side of the knot the front pulls through — hangs
    // down the gap between the boots. Two long caps meeting mid-nape read as one band instead.
    t.cap(6.5, 22.4, 13, 21.8, 1.3, R.rose, { shade: 1 });
    t.cap(19, 21.6, 25.5, 22.6, 1.3, R.rose, { shade: 1 });
    t.cap(10.5, 25.6, 17.5, 25.2, 1.25, R.rose, { shade: 1 });
    t.cap(15.8, 26.5, 16.2, 29.4, 1.1, R.rose, { shade: 1 });
    // Stripe repeat read across each turn, and the grooves where one turn laps the next.
    for (const [x, y] of [[8, 22], [12, 22], [20, 22], [24, 22], [12, 25], [16, 25]] as const) {
      t.px(x, y, R.rose, 0);
      t.px(x, y + 1, R.rose, 3);
    }
    for (const [x, y] of [[14, 23], [17, 23], [14, 26], [17, 26]] as const) t.darken(x, y);
    for (const x of [15, 17]) t.px(x, 30, R.rose, 2);
    for (const x of [6, 26]) {
      t.cap(x, 23, x < 16 ? 4 : 28, 28, 1.4, R.paper, { spec: false, shade: 1 });
      for (const y of [24, 26, 28]) t.px(x < 16 ? x - (y - 23) * 0.5 : x + (y - 23) * 0.5, y, R.paper, 3);
    }
    t.ell(21, 1.5, 3.4, 2.8, R.gold, { spec: false });
    t.cap(20, 4, 18, 6, 1.2, R.paper, { spec: false, shade: 1 });
    return;
  }
  t.ell(16, 14, 7, 4.2, INK, { flat: 1, occ: false });
  arc(t, 16, 14, 7, 4.2, Math.PI, Math.PI * 2, R.navy, 1);
  eyeHollow(t, 12, 14);
  eyeHollow(t, 20, 14);
  // Rolled brim, its ribs kept to rows 19/20 so the coil's turns have bare hat to read against.
  t.cap(6, 21, 26, 21, 3, R.navy, { shade: -1 });
  for (const x of [8, 11, 14, 17, 20, 23]) {
    t.darken(x, 19);
    t.px(x + 1, 20, R.navy, 1);
  }
  // The coil, three short turns of it: two riding high on the brim with the tuque showing through
  // the gaps where the wrap passes behind the head, a third along the hem. One turn spanning the
  // whole brim read as a sash — and worse, the 16px icon averaged it back into one red stripe.
  t.cap(8.6, 20.6, 12.4, 20.4, 1.2, R.rose, { shade: 1 });
  t.cap(19.3, 20.4, 22.7, 20.6, 1.2, R.rose, { shade: 1 });
  t.cap(7, 23.6, 10.6, 24.2, 1.3, R.rose, { shade: 1 });
  // The ends cinched into a knot, then the loose one hanging straight down the gap between the
  // boots to a fringe — a vertical stalk, so nothing of either boot is buried.
  t.ell(15.5, 24.6, 2.3, 2.7, R.rose, { shade: 1 });
  t.cap(15.5, 26.4, 15.6, 28.4, 1.9, R.rose, { shade: 1 });
  t.cap(15.6, 28.4, 15.8, 29.8, 1.1, R.rose, { shade: 1 });
  // Stripe repeat read across each turn, and the grooves where the wrap tucks under itself.
  for (const [x, y] of [[10, 20], [21, 20], [9, 24]] as const) {
    t.px(x, y, R.rose, 0);
    t.px(x, y + 1, R.rose, 3);
  }
  for (const [x, y] of [[13, 21], [18, 21], [12, 25], [14, 25], [17, 25]] as const) t.darken(x, y);
  // Braided ties off both flaps.
  for (const x of [6, 26]) {
    t.cap(x, 23, x < 16 ? 4 : 28, 28, 1.4, R.paper, { spec: false, shade: 1 });
    t.px(x < 16 ? 4 : 28, 28, WHITE);
  }
  t.ell(21, 1.5, 3.4, 2.8, R.gold, { spec: false });
  t.px(20, 0, WHITE);
  for (const [x, y] of [[20, 2], [22, 1], [21, 3]] as const) t.darken(x, y);
};

const poutkit: Paint = (t, back) => {
  // Four materials, four ramps: gold fries, cream curd, brown gravy, red boat.
  slat(t, 12, 18, 7, 5, 1.5, R.gold);
  slat(t, 16, 17, 16, 2, 1.5, R.gold);
  slat(t, 20, 18, 25, 5, 1.5, R.gold);
  t.ell(16, 14, 7, 6.5, R.cream);
  if (back) {
    t.vline(16, 9, 11, R.cream, 3);
    t.ring(16, 15, 3, 3.4, R.cream, 3);
    t.cap(22, 18, 26, 21, 1.4, R.cream, { spec: false });
  } else {
    t.ell(16, 15, 4.5, 2.6, R.paper, { dome: 0.5, rim: false, spec: false });
    eyeBig(t, 11, 10);
    eyeBig(t, 18, 10);
    nose(t, 15, 14);
    smile(t, 14, 16, 5);
  }
  // Gravy pool laps at the chin; curd nuggets and loose fries float in it.
  t.ell(16, 19.5, 9, 2.2, R.bark, { spec: false, rim: false });
  t.ell(10.5, 19, 2, 1.4, R.cream, { spec: false });
  t.ell(21.5, 19, 2, 1.4, R.cream, { spec: false });
  slat(t, 9, 20, 5, 17, 1.3, R.gold);
  slat(t, 23, 20, 27, 17, 1.3, R.gold);
  // Boat: a rounded lip wider than the wall, so the wall reads as tucked under it.
  t.cap(5, 22.5, 27, 22.5, 1.8, R.red, { shade: -1 });
  t.slab(16, 26.5, 10, 2.9, 2, R.red, { shade: 1 });
  if (back) {
    for (const x of [12, 20]) t.vline(x, 25, 4, R.red, 3);
    return;
  }
  for (const [x, y0, h] of [[11, 22, 4], [16, 23, 3], [21, 22, 5]] as const) {
    t.vline(x, y0, h, R.bark, 1);
    t.px(x - 1, y0, R.bark, 0);
    t.px(x, y0 + h, R.bark, 2);
  }
};

const gravylord: Paint = (t, back) => {
  // Fry crest fans wide around the crown instead of behind it.
  slat(t, 11, 18, 3, 9, 1.5, R.gold);
  slat(t, 13, 17, 8, 4, 1.5, R.gold);
  slat(t, 19, 17, 24, 4, 1.5, R.gold);
  slat(t, 21, 18, 29, 9, 1.5, R.gold);
  t.ell(16, 14, 9.5, 8.5, R.cream);
  if (back) {
    t.vline(16, 7, 15, R.cream, 3);
    t.ring(16, 15, 4.5, 4, R.cream, 3);
  } else {
    t.ell(16, 15, 5.5, 3, R.paper, { dome: 0.5, rim: false, spec: false });
    eyeBig(t, 9, 8);
    eyeBig(t, 20, 8);
    nose(t, 15, 13);
    smile(t, 13, 16, 7);
    // Gravy dribble off the chin — the lord does not use the napkin.
    t.vline(17, 18, 3, R.bark, 1);
    t.px(16, 18, R.bark, 0);
    t.px(17, 21, R.bark, 2);
  }
  crown(t, 16, 2, 9);
  // Curd pauldrons, then the platter the whole lane is served on.
  t.ell(7, 19, 3.2, 2.8, R.cream, { shade: -1 });
  t.ell(25, 19, 3.2, 2.8, R.cream, { shade: -1 });
  t.ell(16, 23, 12, 2.6, R.bark, { spec: false, rim: false });
  t.cap(2, 25.5, 30, 25.5, 2.2, R.red, { shade: -1 });
  t.slab(16, 28, 9, 1.8, 1.2, R.red, { shade: 1 });
  if (back) {
    for (const x of [11, 16, 21]) t.vline(x, 27, 3, R.red, 3);
    return;
  }
  for (const [x, y0, h] of [[7, 25, 3], [13, 26, 3], [19, 25, 4], [24, 26, 3]] as const) {
    t.vline(x, y0, h, R.bark, 1);
    t.px(x - 1, y0, R.bark, 0);
    t.px(x, y0 + h, R.bark, 2);
  }
};

const ghostkit: Paint = (t, back) => {
  // R4: the drape had thickness but no weight — one bevelled slab tapering to a point, eight
  // colours, eyes that were two dots. Cloth gets weight from a hem that hangs in lobes, so the
  // hem is three shaded lobes laid down first (the drape's own contact shadow then falls onto
  // them, which is what sells the sheet as hanging off something rather than printed flat).
  for (const [x, r] of [[10, 3], [16, 3.4], [22, 3]] as const) {
    t.ell(x, 25.4, r, 2.8, R.paper, { shade: 1, spec: false });
  }
  t.slab(16, 17, 8.5, 8, 3.4, R.paper);
  t.ell(16, 12, 7, 6, back ? R.paper : R.cream);
  // Two folds, each a hard two-step: a darkened valley with a lit shoulder on its key side. No
  // gradient down the cloth — three quantized bands is the whole vocabulary at this size.
  for (const x of [11, 21]) {
    for (let y = 18; y <= 25; y++) {
      t.darken(x, y);
      t.lighten(x - 1, y);
    }
  }
  // Signature: the read receipt. A cool plate against the warm sheet — the hue shift is what makes
  // a prop of the same value read as a different material — and the two ticks stay hard grey,
  // which is the whole species. Legible as a double check when the icon halves it.
  t.slab(20.5, 21, 4, 3.4, 1.4, R.ice);
  if (back) {
    // From behind: the crown the cloth was gathered over, one deep fold down the spine, and the
    // receipt's blank reverse. From this side it has nothing to tell you, which is the joke.
    t.cap(10, 10, 22, 10, 1.6, R.paper, { shade: 1, spec: false });
    for (let y = 13; y <= 25; y++) {
      t.darken(16, y);
      t.lighten(15, y);
    }
    t.hline(18, 21, 5, R.ice, 2);
    t.px(18, 20, R.ice, 1);
    t.px(19, 23, R.slate, 1);
    t.px(21, 23, R.slate, 1);
    return;
  }
  for (const ox of [18, 21]) {
    t.px(ox, 21, R.slate, 1);
    t.px(ox + 1, 22, R.slate, 1);
    t.px(ox + 2, 21, R.slate, 1);
    t.px(ox + 3, 20, R.slate, 1);
  }
  eyeBig(t, 10, 10, R.ice);
  eyeBig(t, 19, 10, R.ice);
  smile(t, 14, 16, 5);
};

const ghostwyrm: Paint = (t, back) => {
  const m = (x: number) => (back ? 31 - x : x);
  // R4: seven colours in every pose, the roster's flattest toy. The wisp already tapered; what it
  // had no room for was the species' one prop. So the tail now ends in three torn tatters — cloth
  // that has been dragged, which also puts the widest part of the silhouette on the floor — and
  // the frame above the shoulder is cleared for the bubble.
  const segs: [number, number, number, number, number][] = [
    [6, 28, 11, 25, 2.2], [11, 25, 15, 21, 3], [15, 21, 19, 16, 3.4], [19, 16, 21, 12, 2.8],
  ];
  for (const [x0, y0, x1, y1, r] of segs) {
    t.cap(m(x0), y0, m(x1), y1, r, R.paper, { shade: r < 2.5 ? 1 : 0 });
  }
  for (const [x, y, r] of [[5, 28.2, 2], [8, 28.8, 1.8], [11, 28.4, 1.7]] as const) {
    t.ell(m(x), y, r, 1.7, R.paper, { shade: 1, spec: false });
  }
  for (const [x, y] of [[11, 25], [15, 21], [19, 16]] as const) {
    t.darken(m(x), y, 2);
    t.darken(m(x - 1), y + 1);
    t.lighten(m(x + 1), y - 1);
  }
  t.ell(m(20), 9, 7, 6.2, R.cream);
  // Hood hem across the jaw, then the opening's own edge ringed a step darker. Without the ring
  // the hood is a scoop; with it the cloth reads as falling over something and the face sits in a
  // recess, which is the only reason a ghost has a head shape at all.
  t.cap(m(13), 16, m(27), 15, 1.7, R.paper, { shade: 1, spec: false });
  t.ring(m(20), 9.5, 6.6, 5.8, R.paper, 2);
  // Signature: the bubble, with its wedge tail aimed back at the hood. Cool plate, warm sheet —
  // the hue shift does the material separation that value alone cannot at this size.
  t.slab(m(8), 6, 5, 3.6, 1.5, R.ice);
  t.poly([[m(11), 8], [m(12.5), 7.5], [m(13), 11]], R.ice, { tone: 2 });
  if (back) {
    // From behind: the fold running the wisp's whole length with a lit shoulder beside it, the
    // crown gathered over nothing, and the bubble's blank reverse — three dots' worth of nothing.
    for (const [x, y] of [[8, 27], [10, 25], [12, 23], [14, 21], [15, 19], [17, 17], [19, 14]] as const) {
      t.darken(m(x), y);
      t.lighten(m(x + 1), y - 1);
    }
    t.cap(m(16), 6, m(25), 5, 1.5, R.paper, { shade: 1, spec: false });
    t.ell(m(20), 4, 2.2, 1.7, R.paper, { flat: 1, occ: false });
    t.hline(m(10), 6, 5, R.ice, 2);
    return;
  }
  // Still typing, allegedly. Three hard grey dots — never blue, never a fourth.
  for (const dx of [-2, 0, 2]) t.px(m(8 + dx), 6, R.slate, 1);
  eyeBig(t, 15, 6, R.ice);
  eyeBig(t, 21, 6, R.ice);
  smile(t, 18, 11, 5);
};

const crashpup: Paint = (t, back) => {
  // R4: eight colours, and two grey whips off the crown that read as an insect, not a pup on a
  // thread. The whips are gone; the species' own prop takes the frame instead — the red thread
  // runs *through* a collar and knots on the floor, so the leash is the silhouette's business.
  paws(t, 16, 4.8, 2.8, R.rose);
  t.ell(16, 23, 8, 6, R.rose);
  t.ell(16, 11, 7.5, 6.5, R.rose);
  earPoint(t, 10, 7, 8, 1, R.rose, back ? R.rose : R.red, 2.6);
  earPoint(t, 22, 7, 24, 1, R.rose, back ? R.rose : R.red, 2.6);
  // The collar: a bevelled band, not a painted stripe, so the thread has something to seat in.
  t.slab(16, 19, 5.5, 1.7, 0.9, R.red, { shade: 1 });
  if (back) {
    // From behind: the nape the head sits down onto, the spine, the collar's buckle — and the
    // thread still going, because from this side you can see where the argument is heading.
    t.cap(10, 16, 22, 16, 1.6, R.rose, { shade: 1, spec: false });
    t.vline(16, 20, 8, R.rose, 3);
    t.slab(16, 19, 1.8, 1.6, 0.7, R.gold, { spec: false });
    t.px(15, 18, WHITE);
    t.line(12, 20, 5, 26, R.red, 1);
    t.line(12, 19, 5, 25, R.red, 0);
    t.ell(4, 27, 2.6, 2.2, R.red, { spec: false });
    t.line(20, 20, 27, 25, R.red, 1);
    t.line(20, 19, 27, 24, R.red, 0);
    for (const y of [22, 25]) t.px(10, y, R.rose, 4);
    return;
  }
  t.ell(16, 21, 1.7, 1.5, R.gold, { spec: false });
  t.px(15, 20, WHITE);
  t.ell(16, 14.5, 4.5, 3, R.cream, { dome: 0.5, rim: false, spec: false });
  // Signature: one thread, taut, entering the collar on one side and leaving on the other. A lit
  // upper edge one step above the body of the cord is the whole trick — 1px props get no rim from
  // the rig, so the highlight is marched by hand.
  t.line(13, 20, 5, 26, R.red, 1);
  t.line(13, 19, 5, 25, R.red, 0);
  t.ell(4, 27, 2.6, 2.2, R.red, { spec: false });
  t.px(3, 26, WHITE);
  t.line(19, 20, 27, 25, R.red, 1);
  t.line(19, 19, 27, 24, R.red, 0);
  t.px(27, 26, R.red, 2);
  eyeBig(t, 11, 7, R.gold);
  eyeBig(t, 18, 7, R.gold);
  brow(t, 11, 7, 1);
  brow(t, 18, 7, -1);
  nose(t, 15, 13);
  grin(t, 13, 15, 6, 3);
};

const crashmaw: Paint = (t, back) => {
  // R4: open D-ring gold on both faces, and three grey whips the pup never had. Same correction as
  // crashpup, one stage louder — the leash snapped, so the thread no longer leads the toy, it
  // crosses it. The collar's ring is drawn open.
  paws(t, 16, 5.6, 3.4, R.rose);
  t.ell(16, 23, 10, 7.5, R.rose);
  t.ell(16, 10, 8.5, 7.2, R.rose);
  earPoint(t, 9, 6, 6, 0, R.rose, back ? R.rose : R.red, 3.4);
  earPoint(t, 23, 6, 26, 0, R.rose, back ? R.rose : R.red, 3.4);
  // One ear came back from reply nine short a tip.
  for (const [x, y] of [[25, 0], [24, 1], [25, 1], [23, 2], [24, 2], [25, 2]] as const) t.erase(x, y);
  t.slab(16, 18.5, 7, 2, 1, R.red, { shade: 1 });
  // Signature: the thread crosses instead of leading, knots where it crosses, and runs off into a
  // loose loop on the floor. Every cord is two hand-stepped rows — the body of the thread and a
  // lit edge one step above it — because the rig rims nothing this thin.
  for (const [x0, y0, x1, y1] of [[7, 20, 25, 28], [25, 20, 8, 27]] as const) {
    t.line(x0, y0, x1, y1, R.red, 1);
    t.line(x0, y0 - 1, x1, y1 - 1, R.red, 0);
  }
  t.ring(5, 26, 3, 2.4, R.red, 1);
  arc(t, 5, 26, 3, 2.4, Math.PI, Math.PI * 1.5, R.red, 0);
  t.ell(16, 24, 3.2, 2.7, R.red);
  t.ring(16, 24, 1.9, 1.6, R.red, 2);
  if (back) {
    // From behind: the nape, the spine, the ears' roots, the collar strap's cut end still hanging
    // — and the tangle, which is the one thing about this toy that looks the same from every side.
    // Gold open D-ring + cream nape glint push the back past the 8-colour gate (was 7).
    t.cap(9, 15, 23, 15, 1.9, R.rose, { shade: 1, spec: false });
    t.vline(16, 17, 4, R.rose, 3);
    for (const x of [9, 23]) {
      t.ell(x, 6, 2.2, 1.8, R.rose, { shade: 1, spec: false });
      t.px(x, 6, R.rose, 4);
    }
    t.vline(12, 20, 4, R.red, 2);
    t.px(12, 23, R.red, 1);
    for (const y of [21, 26]) t.px(8, y, R.rose, 4);
    arc(t, 10.5, 19, 2.4, 2.2, -2.5, 1.9, R.gold, 1);
    t.px(9, 18, R.gold, 0);
    t.px(16, 12, R.cream, 1);
    t.px(15, 11, WHITE);
    return;
  }
  // The D-ring the leash was clipped to, drawn open: the break is at the lower left, where it went.
  arc(t, 10.5, 19, 2.4, 2.2, -2.5, 1.9, R.gold, 1);
  t.px(9, 18, R.gold, 0);
  t.px(10, 17, WHITE);
  t.ell(16, 14, 5.5, 3.5, R.cream, { dome: 0.5, rim: false, spec: false });
  eyeBig(t, 9, 6, R.gold);
  eyeBig(t, 20, 6, R.gold);
  brow(t, 9, 6, 1);
  brow(t, 20, 6, -1);
  nose(t, 15, 12);
  grin(t, 12, 14, 8, 4);
};

const sauceling: Paint = (t, back) => {
  t.cap(16, 28, 16, 14, 3.6, R.gold);
  t.ell(16, 12, 6.5, 5.5, R.cream);
  // The cap was a flat navy block; it is a bevelled case with a knurled crown now.
  t.slab(16, 8, 3.2, 2.6, 1, R.navy);
  for (const x of [14, 16, 18]) t.darken(x, 9);
  t.ell(16, 6, 2.2, 1.6, R.gold, { spec: false });
  if (back) {
    // From behind: the neck ring under the cap, the blow-mould seam running the length of the
    // glass, and the label on backwards — a blank panel carrying only a fill line and a batch rule.
    t.cap(12.5, 11, 19.5, 11, 1.3, R.cream, { shade: 1, spec: false });
    t.hline(13, 10, 5, R.cream, 0);
    t.vline(16, 7, 4, R.navy, 0);
    t.px(14, 7, R.navy, 1); t.px(18, 7, R.navy, 1);
    t.vline(16, 14, 3, R.gold, 3);
    t.px(15, 15, R.gold, 0);
    t.slab(16, 20, 3.6, 5, 1, R.paper, { shade: 1 });
    t.hline(13, 17, 7, R.rose, 1);
    t.px(13, 17, R.rose, 0);
    t.hline(13, 22, 7, R.slate, 1);
    t.vline(16, 26, 2, R.gold, 3);
    return;
  }
  eyeDot(t, 13, 10);
  eyeDot(t, 18, 10);
  smile(t, 14, 14, 4);
};

const saucelord: Paint = (t, back) => {
  const m = (x: number) => (back ? 31 - x : x);
  paws(t, 16, 5, 2.8, R.gold);
  t.ell(16, 22, 9, 7, R.gold);
  t.ell(16, 12, 8, 6.5, R.cream);
  t.slab(16, 5.5, 3.2, 2.6, 1, R.navy);
  for (const x of [14, 16, 18]) t.darken(x, 7);
  t.ell(16, 3, 2.4, 1.8, R.gold, { spec: false });
  t.cap(m(22), 16, m(28), 10, 1.6, R.rose);
  if (back) {
    // From behind: the knurled cap skirt over a neck ring, the label reversed to its batch rules,
    // and the sauce that got away running two tongues straight down over its own paperwork.
    t.cap(11, 9, 21, 9, 1.4, R.cream, { shade: 1, spec: false });
    t.hline(12, 8, 6, R.cream, 0);
    // The skirt, the label slab and the drip each land on a pixel the rig had chosen as a
    // specular, so the reverse had no white left anywhere on it. The skirt is the one volume the
    // reconstruction does not paint over, so it carries the gloss point by hand.
    t.px(12, 8, WHITE);
    for (const x of [13, 15, 17, 19]) t.vline(x, 5, 3, R.navy, 0);
    t.vline(16, 12, 3, R.cream, 4);
    t.slab(16, 21, 5, 5.4, 1.2, R.paper, { shade: 1 });
    t.hline(12, 19, 9, R.rose, 1);
    t.px(12, 19, R.rose, 0);
    t.hline(12, 23, 9, R.slate, 1);
    for (const [x, y, h] of [[13, 18, 3.4], [20, 17, 2.8]] as const) {
      t.ell(x, y, 1.4, h, R.rose, { spec: false, shade: 1 });
      t.px(x - 1, Math.round(y - h * 0.5), R.rose, 0);
    }
    return;
  }
  eyeBig(t, 10, 9);
  eyeBig(t, 17, 9);
  smile(t, 14, 15, 5);
};

const pingling: Paint = (t, back) => {
  const m = (x: number) => (back ? 31 - x : x);
  t.ell(16, 20, 7, 7.5, R.blue);
  t.ell(16, 12, 6, 5.5, R.cream);
  t.ell(m(22), 8, 3, 3, R.rose);
  t.px(m(22), 8, R.gold, 0);
  if (back) {
    // From behind: the badge is pressed blank — the ping has already left, stepping away in two
    // arcs off the far shoulder — over an occiput seam and the grille it actually broadcasts
    // through, bolted to the shell.
    t.ell(m(22), 8, 2.6, 2.6, R.rose, { spec: false, shade: 2 });
    t.ring(m(22), 8, 1.4, 1.4, R.rose, 3);
    for (const [r, tone] of [[4.8, 1], [6.6, 2]] as const) {
      arc(t, m(22), 8, -r, r, -1.2, -0.25, R.blue, tone);
    }
    arc(t, 16, 12, 5, 4.4, 0.3, 2.85, R.cream, 3);
    t.cap(16, 14, 16, 18, 1.4, R.cream, { shade: 2, spec: false });
    t.px(15, 15, R.cream, 1);
    t.slab(16, 22, 4.6, 4.2, 1, R.navy, { shade: 1 });
    for (const y of [20, 22, 24]) {
      t.cap(13, y, 19, y, 0.8, R.blue, { shade: 1, spec: false });
      t.px(13, y, R.blue, 0);
    }
    for (const x of [12, 20]) rivet(t, x, 18, R.gold);
    return;
  }
  eyeBead(t, 13, 10);
  eyeBead(t, 17, 10);
  smile(t, 14, 14, 4);
};

const pinglord: Paint = (t, back) => {
  const m = (x: number) => (back ? 31 - x : x);
  paws(t, 16, 5, 2.8, R.blue);
  t.ell(16, 22, 9, 7, R.blue);
  t.ell(16, 11, 8, 6.5, R.cream);
  t.ell(m(24), 6, 3.6, 3.6, R.rose);
  t.px(m(24), 6, R.gold, 0);
  crown(t, 16, 2, 8);
  if (back) {
    // From behind: the crown's plain rear band — no stones are set on the side nobody photographs
    // — the badge pressed blank with the ping two arcs out already, and the grille bolted to the
    // shoulder between two blades.
    t.px(15, 5, R.gold, 2);
    t.px(13, 4, R.gold, 2);
    t.px(18, 4, R.gold, 2);
    t.ell(m(24), 6, 3, 3, R.rose, { spec: false, shade: 2 });
    t.ring(m(24), 6, 1.6, 1.6, R.rose, 3);
    for (const [r, tone] of [[5, 1], [6.6, 2]] as const) {
      arc(t, m(24), 6, -r, r, -1.2, -0.25, R.blue, tone);
    }
    arc(t, 16, 11, 6.6, 5.6, 0.3, 2.85, R.cream, 3);
    t.cap(16, 14, 16, 19, 1.7, R.cream, { shade: 2, spec: false });
    t.px(15, 15, R.cream, 1);
    t.ell(10, 19, 2.8, 2.5, R.blue, { spec: false, shade: 1 });
    t.ell(22, 19, 2.8, 2.5, R.blue, { spec: false, shade: 2 });
    t.slab(16, 24, 5.4, 4, 1.2, R.navy, { shade: 1 });
    for (const y of [22, 24, 26]) {
      t.cap(12, y, 20, y, 0.9, R.blue, { shade: 1, spec: false });
      t.px(12, y, R.blue, 0);
    }
    for (const x of [11, 21]) rivet(t, x, 21, R.gold);
    return;
  }
  eyeBig(t, 11, 8);
  eyeBig(t, 17, 8);
  smile(t, 14, 14, 5);
};

const slayling: Paint = (t, back) => {
  paws(t, 16, 4.2, 2.4, R.rose);
  t.ell(16, 22, 6.5, 5.5, R.rose);
  t.ell(16, 14, 6.5, 6, R.cream);
  // The band was a slate bar with a gold rule on it; it is a bevelled case over a lit gold trim.
  t.slab(16, 10.5, 5.2, 1.6, 0.9, R.slate);
  t.cap(12, 11, 20, 11, 0.9, R.gold, { shade: 1, spec: false });
  t.px(12, 11, R.gold, 0);
  t.cap(10, 26, 8, 30, 1.2, R.gold);
  t.cap(22, 26, 24, 30, 1.2, R.gold);
  if (back) {
    // From behind: the band closes on a gold clasp under a ponytail pulled up through it, and the
    // fit turns out to be backless — two shoulder blades and a bare spine laced shut in gold.
    // The hair goes up *through* the band, so the tail lays down first and the clasp closes over
    // it; drawn the other way round the ponytail cuts the band in half.
    t.cap(16, 9, 16, 4, 2.2, R.cream, { shade: 1 });
    t.ell(16, 3, 2.4, 2, R.cream, { spec: false });
    t.slab(16, 10.5, 3, 1.6, 0.8, R.slate, { shade: 1 });
    // The clasp is the one hard prop on this side, so it is a 2×2 tab with a step under it rather
    // than two stacked pixels — and it carries the pose's white. Both rig speculars are painted
    // over here (the band slab takes the head's, the shoulder blade takes the body's), so without
    // a catch on the clasp the reverse dropped a colour against the baseline.
    t.px(15, 10, WHITE);
    t.px(16, 10, R.gold, 0);
    t.px(15, 11, R.gold, 1);
    t.px(16, 11, R.gold, 2);
    t.cap(16, 13, 16, 19, 1.5, R.cream, { shade: 2, spec: false });
    t.px(15, 14, R.cream, 1);
    t.ell(12, 19, 2.6, 2.2, R.cream, { spec: false, shade: 1 });
    t.ell(20, 19, 2.6, 2.2, R.cream, { spec: false, shade: 2 });
    t.line(13, 22, 19, 25, R.gold, 1);
    t.line(19, 22, 13, 25, R.gold, 1);
    for (const y of [22, 25]) { rivet(t, 13, y, R.gold); rivet(t, 19, y, R.gold); }
    return;
  }
  eyeBead(t, 12, 12);
  eyeBead(t, 17, 12);
  smile(t, 14, 17, 4);
};

const slaylord: Paint = (t, back) => {
  const m = (x: number) => (back ? 31 - x : x);
  paws(t, 16, 5.2, 3, R.rose);
  t.poly([[m(4), 12], [m(12), 10], [m(12), 24], [m(2), 26]], R.gold, { grad: true });
  t.ell(16, 22, 9, 7, R.rose);
  t.ell(16, 12, 8, 6.5, R.cream);
  t.slab(16, 8.5, 6, 1.6, 0.9, R.slate);
  t.cap(11, 9, 21, 9, 0.9, R.gold, { shade: 1, spec: false });
  t.px(11, 9, R.gold, 0);
  crown(t, 16, 2, 8);
  if (back) {
    // From behind: the crown's plain rear band — no stones on the side nobody photographs — the
    // headband closing on its clasp, the fit opening on two shoulder blades laced down a bare
    // spine, and the cape swung round to the far shoulder with its hem seam showing.
    t.px(15, 5, R.gold, 2);
    t.px(13, 4, R.gold, 2);
    t.px(18, 4, R.gold, 2);
    t.slab(16, 8.5, 3.4, 1.6, 0.8, R.slate, { shade: 1 });
    t.px(16, 8, R.gold, 0);
    t.px(16, 9, R.gold, 2);
    t.cap(16, 12, 16, 19, 1.8, R.cream, { shade: 2, spec: false });
    t.px(15, 13, R.cream, 1);
    t.ell(11, 19, 3, 2.4, R.cream, { spec: false, shade: 1 });
    t.ell(21, 19, 3, 2.4, R.cream, { spec: false, shade: 2 });
    t.line(12, 22, 20, 25, R.gold, 1);
    t.line(20, 22, 12, 25, R.gold, 1);
    for (const y of [22, 25]) { rivet(t, 12, y, R.gold); rivet(t, 20, y, R.gold); }
    t.cap(m(5), 14, m(4), 24, 1.5, R.gold, { shade: 1, spec: false });
    return;
  }
  eyeBig(t, 10, 9);
  eyeBig(t, 17, 9);
  smile(t, 14, 15, 5);
};

const cookling: Paint = (t, back) => {
  paws(t, 16, 4.4, 2.4, R.orange);
  t.ell(16, 22, 7, 5.5, R.orange);
  t.ell(16, 14, 6.5, 5.5, R.cream);
  // The toque was two stacked `rect`s. It is the same three-puff band the wyrm wears one stage up,
  // so the line reads as one kitchen instead of two unrelated hats.
  for (const [x, y, r] of [[12, 5, 2.6], [16, 3.5, 3], [20, 5, 2.4]] as const) {
    t.ell(x, y, r, r * 0.9, R.paper, { spec: false });
    // 1px in, not 2: at these radii a 2px offset falls off the puff and leaves a lit pixel
    // floating in the air with its own ink ring round it.
    t.px(x - 1, y - 1, R.paper, 0);
  }
  t.cap(11, 9, 21, 9, 2, R.paper, { shade: -1 });
  for (const x of [13, 16, 19]) t.darken(x, 10);
  if (back) {
    // The toque is a third of this toy, and drawing a seam over the front's three puffs left that
    // third reading the same from both sides — which is most of why the reverse scored as a
    // repaint. From behind a toque has no puffs at all: the pleats are set at the brow and the
    // gathers pull the whole crown into one seamed dome, cinched at the band and knotted off at
    // the nape, with the band's own shadow hard across the occiput. The apron is all tie from
    // here — the waist band right round, bowed between the shoulder blades, both ends down the
    // flank past the paws.
    t.ell(16, 4.2, 6.3, 3.2, R.paper, { spec: false });
    t.vline(16, 1, 6, R.paper, 0);
    for (const x of [13, 19]) t.vline(x, 3, 4, R.paper, 2);
    t.hline(11, 7, 11, R.paper, 0);
    t.cap(12, 9.5, 20, 9.5, 1.3, R.paper, { shade: 2, spec: false });
    t.cap(13, 13.5, 19, 13.5, 1.1, R.cream, { shade: 3, spec: false });
    t.ell(16, 12, 1.9, 1.6, R.paper, { spec: false });
    t.px(15, 11, R.paper, 0);
    arc(t, 16, 14, 5.4, 4.6, 0.3, 2.85, R.cream, 3);
    t.cap(16, 16, 16, 21, 1.5, R.cream, { shade: 2, spec: false });
    t.px(15, 17, R.cream, 1);
    t.ell(12, 20, 2.6, 2.2, R.orange, { spec: false, shade: 1 });
    t.ell(20, 20, 2.6, 2.2, R.orange, { spec: false, shade: 2 });
    t.cap(11, 23, 21, 23, 1.3, R.paper, { shade: 2, spec: false });
    t.hline(12, 22, 8, R.paper, 0);
    t.ell(13, 23.5, 1.8, 1.7, R.paper, { spec: false });
    t.ell(19, 23.5, 1.8, 1.7, R.paper, { spec: false, shade: 1 });
    t.ell(16, 23.5, 1.5, 1.5, R.paper, { spec: false });
    t.cap(15, 25, 13, 26.5, 0.9, R.paper, { shade: 1, spec: false });
    t.cap(17, 25, 19, 26.5, 0.9, R.paper, { shade: 2, spec: false });
    // The band cap's contact shadow darkens the head's specular pixel, in both poses — so the
    // front keeps its white on the eye catches and the back had none at all. The bow knot is the
    // one volume nothing paints over afterwards, so it takes the gloss point.
    t.px(15, 23, WHITE);
    t.px(16, 22, R.rose, 1);
    return;
  }
  t.px(16, 6, R.rose, 1);
  eyeDot(t, 13, 12);
  eyeDot(t, 18, 12);
  smile(t, 14, 16, 4);
};

const cookwyrm: Paint = (t, back) => {
  const m = (x: number) => (back ? 31 - x : x);
  // The kitchen finally opened. The old flame was drawn inside the tail barrel and never showed,
  // and the old toque was two flat rectangles; the coil now runs on the geometry that already
  // reads across the wyrm line, and everything it cooks with is out front and lit.
  t.cap(m(6), 26, m(13), 24, 3.6, R.orange);
  t.cap(m(13), 24, m(19), 19, 3.4, R.orange);
  t.cap(m(19), 19, m(21), 13, 3.2, R.orange);
  t.ell(m(22), 10, 7, 6.2, R.cream);
  // Toque: three puffs over a pleated band, each puff its own lit barrel.
  for (const [x, y, r] of [[18, 2.5, 3.2], [22, 1, 3.6], [26, 2.5, 3]] as const) {
    t.ell(m(x), y, r, r * 0.9, R.paper, { spec: false });
    // Same 1px correction as one stage down: at 2px the front pose's left highlight sat off the
    // puff entirely, as an isolated pixel above the toque.
    t.px(m(x) - 1, y - 1, R.paper, 0);
  }
  t.cap(m(17), 5.5, m(27), 5.5, 2, R.paper, { shade: -1 });
  // Integer row: `darken` indexes `y * 32 + x` directly, so a fractional y silently pleated three
  // unrelated pixels on the far side of the frame instead of the toque band.
  for (const x of [19, 22, 25]) t.darken(m(x), 7);
  if (back) {
    // From behind: the toque cinched at the band and seamed up the crown, dorsal scutes down the
    // coil, and the spoon's shadow side — service looks the same from the pass.
    t.hline(Math.min(m(17), m(27)), 4, 11, R.paper, 0);
    t.vline(m(22), 0, 6, R.paper, 2);
    t.ring(m(22), 10, 4.6, 4, R.cream, 3);
    t.vline(m(22), 4, 10, R.cream, 3);
    for (const [x, y] of [[9, 25], [14, 23], [18, 20], [20, 15]] as const) {
      t.ell(m(x), y, 2.2, 1.8, R.orange, { spec: false, shade: 1 });
      t.px(m(x) - 1, y - 1, R.orange, 0);
    }
    t.cap(m(12), 22, m(9), 20, 2.2, R.orange, { shade: 1 });
    slat(t, m(10), 24, m(6), 15, 1.5, R.bark, false);
    t.ell(m(5), 13, 2.8, 2.2, R.gold, { spec: false, shade: 1 });
    return;
  }
  eyeBig(t, 18, 7);
  eyeBig(t, 24, 7);
  nose(t, 22, 12);
  grin(t, 19, 13, 7, 3);
  // The spoon, held up so the whole route can see what is cooking on it.
  t.cap(12, 22, 9, 20, 2.2, R.orange, { shade: 1 });
  slat(t, 10, 24, 6, 15, 1.5, R.bark);
  t.ell(5, 13, 2.8, 2.2, R.gold);
  flame(t, 5, 8, 4);
  flame(t, 8, 10, 3);
};

const midling: Paint = (t, back) => {
  // R4: a grey dome, a paler dome and a flat slate rectangle with two gold dots in it — ten
  // colours and no idea what the species was. "Agrees with both sides, sides with neither" is a
  // design, so the toy is built out of it: a neutral core with one warm panel and one cool panel
  // bolted on either side of a hard seam. Same value, opposite hue — the split has to read at
  // 16px on hue alone, because the whole joke is that neither side is brighter than the other.
  for (const x of [12, 20] as const) {
    t.cap(x, 26, x, 27.6, 1.7, R.stone, { shade: 1, spec: false });
    t.hline(x - 2, 28, 5, R.stone, 3);
  }
  t.ell(16, 22, 8.5, 6.5, R.stone);
  t.slab(11.8, 22, 3.8, 4.2, 1.2, R.paper);
  t.slab(20.2, 22, 3.8, 4.2, 1.2, R.ice);
  t.vline(16, 18, 9, R.stone, 4);
  t.ell(16, 11, 6.5, 5.5, R.stone);
  t.slab(12.8, 11.5, 2.6, 4, 1, R.paper);
  t.slab(19.2, 11.5, 2.6, 4, 1, R.ice);
  t.vline(16, 8, 8, R.stone, 4);
  // Signature: the balance, dead level, both pans empty. A beam that is level is only legible if
  // the two pans land on exactly the same row — so they do, and nothing else in the frame does.
  t.cap(7, 18, 25, 18, 1.2, R.gold, { spec: false });
  for (const x of [7, 25] as const) {
    t.vline(x, 19, 2, R.gold, 2);
    t.ell(x, 21.4, 3, 1.5, R.gold, { spec: false, rim: false });
  }
  t.px(8, 17, WHITE);
  if (back) {
    // From behind: the seam runs the whole length, the panels show their rivets, the beam's far
    // pans hang off the same row, and the nape. Both sides, from this side too.
    t.cap(10, 16, 22, 16, 1.5, R.stone, { shade: 1, spec: false });
    for (const [x, y] of [[10, 20], [13, 24], [19, 20], [22, 24]] as const) rivet(t, x, y, R.stone);
    for (const [x, y] of [[11, 9], [21, 9]] as const) rivet(t, x, y, R.stone);
    t.px(7, 21, R.gold, 2);
    t.px(25, 21, R.gold, 2);
    return;
  }
  eyeBig(t, 11, 9, R.paper);
  eyeBig(t, 18, 9, R.ice);
  // A mouth with no opinion in it: one flat rule, straight across the seam.
  frown(t, 12, 15, 8);
};

const midbaron: Paint = (t, back) => {
  // R4: two overlapping domes and a slate rectangle off the shoulder — the "both sides" read was
  // a colour accident, not a construction. Same seam as midling one stage up, and the blurb's own
  // prop underneath it: a throne bolted to the top of a fence. The fence is the floor contact, so
  // the silhouette says *straddling* before you have read a single pixel of the face.
  for (const x of [5, 11, 21, 27] as const) slat(t, x, 21, x, 29, 1.4, R.bark, false);
  t.cap(4, 23, 28, 23, 1.2, R.bark, { spec: false });
  t.slab(16, 16, 9, 8, 2, R.bark, { shade: 1 });
  for (const x of [7, 25] as const) {
    t.ell(x, 9, 1.8, 1.6, R.gold, { spec: false });
    t.cap(x, 10, x, 14, 1.3, R.bark, { shade: 1, spec: false });
  }
  t.ell(16, 20, 9.5, 7, R.stone);
  t.slab(11.4, 20, 4.2, 4.6, 1.3, R.paper);
  t.slab(20.6, 20, 4.2, 4.6, 1.3, R.ice);
  t.vline(16, 15, 11, R.stone, 4);
  t.ell(16, 9, 7.5, 6, R.stone);
  t.slab(12.3, 9.5, 3, 4.2, 1, R.paper);
  t.slab(19.7, 9.5, 3, 4.2, 1, R.ice);
  t.vline(16, 5, 9, R.stone, 4);
  crown(t, 16, 1, 9);
  if (back) {
    // From behind: the fence's lower rail — the one you only see from the far side — the throne's
    // posts, the seam running the whole length, and the panels' rivets. It charges rent this way
    // round too.
    t.cap(4, 26.5, 28, 26.5, 1.2, R.bark, { shade: 1, spec: false });
    for (const x of [5, 27] as const) t.px(x, 25, R.bark, 0);
    t.cap(9, 14, 23, 14, 1.7, R.stone, { shade: 1, spec: false });
    for (const [x, y] of [[10, 18], [13, 23], [19, 18], [22, 23]] as const) rivet(t, x, y, R.stone);
    for (const [x, y] of [[11, 8], [21, 8]] as const) rivet(t, x, y, R.stone);
    t.hline(12, 3, 9, R.gold, 2);
    return;
  }
  // Arm rests, both of them occupied by nobody's side.
  for (const x of [7, 25] as const) {
    t.cap(x, 18, x + (x < 16 ? 3 : -3), 18, 1.3, R.gold, { spec: false });
    t.px(x, 17, R.gold, 0);
  }
  eyeBig(t, 11, 7, R.paper);
  eyeBig(t, 18, 7, R.ice);
  frown(t, 12, 13, 8);
};

const lockkit: Paint = (t, back) => {
  // Steel shackle first: the brass case is drawn over its legs, so the bar seats into the top.
  hoop(t, 16, 12, 6, 7, 2.2, R.slate);
  t.px(12, 8, WHITE);
  t.px(13, 7, R.slate, 0);
  // Stubby steel feet, set wide enough to clear the case's rounded corners.
  for (const x of [6, 26]) t.ell(x, 28, 2.6, 1.9, R.slate, { spec: false, shade: 1 });
  t.slab(16, 22, 9.5, 6.5, 3, R.gold);
  if (back) {
    // Back plate: case seam, recessed keyway cover, four rivets. No face.
    t.hline(8, 20, 17, R.gold, 3);
    t.slab(16, 23, 7, 4.2, 2, R.gold, { shade: 1, spec: false });
    t.ring(16, 23, 2.6, 2.2, R.gold, 3);
    t.ell(16, 23, 1.3, 1.2, R.slate, { spec: false });
    for (const [x, y] of [[9, 17], [23, 17], [9, 27], [23, 27]] as const) rivet(t, x, y, R.gold);
    return;
  }
  for (const [x, y] of [[8, 17], [24, 17], [8, 27], [24, 27]] as const) rivet(t, x, y, R.gold);
  eyeBig(t, 10, 18);
  eyeBig(t, 19, 18);
  keyhole(t, 16, 22.8, 1.5);
};

const locklord: Paint = (t, back) => {
  // Heavy shackle, then the crown seated on its arc, then the case over the legs.
  hoop(t, 16, 11, 8, 8.5, 3, R.slate);
  t.px(11, 5, WHITE);
  crown(t, 16, 0, 9);
  for (const x of [5, 27]) t.ell(x, 27.6, 3, 2.2, R.slate, { spec: false, shade: 1 });
  t.slab(16, 21, 11, 8, 3.5, R.gold);
  if (back) {
    t.hline(6, 19, 21, R.gold, 3);
    t.slab(16, 23, 8, 5.2, 2.5, R.gold, { shade: 1, spec: false });
    t.ring(16, 23, 3.8, 3.2, R.gold, 3);
    t.ell(16, 23, 1.8, 1.6, R.slate, { spec: false });
    for (const [x, y] of [[8, 15], [24, 15], [8, 28], [24, 28]] as const) rivet(t, x, y, R.gold);
    return;
  }
  // Armoured corner brackets: the case is bolted shut, not just shaded.
  for (const [x, y] of [[7, 16], [25, 16], [7, 26], [25, 26]] as const) {
    t.slab(x, y, 1.6, 1.6, 0.8, R.navy, { spec: false });
  }
  eyeBig(t, 9, 16);
  eyeBig(t, 20, 16);
  brow(t, 9, 16, 1);
  brow(t, 20, 16, -1);
  // The maw is the keyhole: a bored well flaring into a fanged slot.
  keyhole(t, 16, 21.5, 2.3);
  t.px(14, 25, WHITE);
  t.px(17, 25, WHITE);
};

const depkit: Paint = (t, back) => {
  // The slush is in one paw, so it changes paws when the toy turns round.
  const m = (x: number) => (back ? 31 - x : x);
  t.rect(9, 14, 14, 14, R.green, 1);
  t.rect(10, 15, 12, 12, R.green, 0);
  t.ell(16, 12, 6, 5, R.cream);
  t.rect(back ? 7 : 20, 6, 5, 7, R.blue, 1);
  t.ell(m(22), 6, 2.4, 1.8, R.paper, { spec: false });
  t.px(m(22), 8, R.rose, 1);
  if (back) {
    // Nobody puts a window on the back of a dépanneur. Turned round it is the alley side: a
    // bevelled steel service door in its frame, the extractor that runs all night, and the crate
    // stack nobody has taken in. Open late, closed on nuance, blank behind.
    t.slab(16, 22, 6.2, 5.2, 1.3, R.green, { shade: 1 });
    t.slab(17, 23, 2.8, 4.2, 0.8, R.green, { shade: 2 });
    t.vline(20, 19, 8, R.green, 3);
    t.px(15, 23, R.gold, 0);
    t.px(15, 24, R.gold, 2);
    t.ell(11.5, 19, 2.4, 1.6, R.stone, { shade: 1, spec: false });
    for (const y of [18, 19, 20]) t.px(11, y, R.slate, 1);
    for (const [y, tone] of [[22, 1], [25, 3]] as const) {
      t.slab(11.5, y, 2.2, 1.3, 0.6, R.blue, { shade: tone, spec: false });
    }
    // The head keeps its own gloss: the occiput's shadow and the nape fold sit clear of it.
    arc(t, 16, 12, 4.6, 3.8, 0.35, Math.PI - 0.35, R.cream, 3);
    t.cap(13, 15, 19, 15, 1.3, R.cream, { shade: 2, spec: false });
    t.hline(13, 13, 7, R.cream, 1);
    return;
  }
  eyeBead(t, 13, 10);
  eyeBead(t, 17, 10);
  smile(t, 14, 14, 4);
};

const depbaron: Paint = (t, back) => {
  // Cup and scratch card were flat `rect`s sat on either side of the toy, in the same place from
  // both sides. They are bevelled volumes now, and they change hands when the toy turns round.
  const m = (x: number) => (back ? 31 - x : x);
  paws(t, 16, 5, 3, R.green);
  t.ell(16, 22, 9.5, 7, R.green);
  t.ell(16, 12, 8, 6.5, R.cream);
  t.slab(m(24), 8, 3, 4.2, 1, R.blue);
  t.ell(m(24), 4, 2.8, 2, R.paper, { spec: false });
  slat(t, m(24), 4, m(27), 0, 1, R.rose);
  t.slab(m(8.5), 17.5, 2.6, 1.8, 0.8, R.gold);
  t.px(m(7), 17, R.rose, 1);
  if (back) {
    // From behind: the cup turns its unprinted seam to the camera, the scratch card is face-down
    // on its barcode, and the store smock ties at the nape over two shoulder blades.
    t.vline(m(24), 5, 7, R.blue, 3);
    t.px(m(24) - 1, 6, R.blue, 0);
    for (const dx of [0, 2, 4]) t.vline(m(7 + dx), 17, 2, INK, 1);
    arc(t, 16, 12, 6.6, 5.6, 0.3, 2.85, R.cream, 3);
    t.cap(16, 15, 16, 20, 1.7, R.cream, { shade: 2, spec: false });
    t.px(15, 16, R.cream, 1);
    t.ell(10, 20, 3, 2.6, R.green, { spec: false, shade: 1 });
    t.ell(22, 20, 3, 2.6, R.green, { spec: false, shade: 2 });
    t.cap(10, 24, 22, 24, 1.3, R.paper, { shade: 1, spec: false });
    t.hline(11, 23, 7, R.paper, 0);
    t.ell(16, 24, 1.8, 1.6, R.paper, { spec: false });
    return;
  }
  eyeBig(t, 10, 9);
  eyeBig(t, 17, 9);
  smile(t, 14, 15, 5);
};

const delulord: Paint = (t, back) => {
  t.poly([[1, 12], [14, 10], [14, 24], [2, 26]], R.rose, { grad: true });
  t.poly([[18, 10], [31, 12], [30, 26], [18, 24]], R.rose, { grad: true });
  t.ell(16, 20, 5.5, 8, R.cream);
  t.ell(16, 10, 6.5, 5.5, R.cream);
  crown(t, 16, 2, 8);
  t.hline(10, 10, 12, R.gold, 1);
  t.ring(12, 10, 2.6, 2, R.gold, 1);
  t.ring(20, 10, 2.6, 2, R.gold, 1);
  if (back) {
    // The same lenses from the inside, grown: frames' back rims, hinges, the strap across the
    // nape. The plot got a title page and a sequel — this is the spine of it.
    for (const [cx, sh] of [[5, 1], [26, 2]] as const) {
      t.ell(cx, 18, 3.8, 5.6, R.rose, { shade: sh, spec: false, occ: false });
      t.vline(cx, 13, 11, R.rose, 3);
      t.px(cx - 1, 14, R.rose, 0);
    }
    t.cap(12, 12, 20, 12, 1.4, R.gold, { shade: 1, spec: false });
    for (const x of [11, 21]) rivet(t, x, 12, R.gold);
    // A crown from behind is its band and the backs of its points: no jewels, just solder.
    t.hline(12, 4, 8, R.gold, 2);
    for (const x of [12, 15, 19]) t.vline(x, 2, 2, R.gold, 1);
    t.cap(13, 14, 19, 14, 1.3, R.cream, { shade: 2, spec: false });
    t.ell(13, 20, 2.2, 2.6, R.cream, { shade: 1, spec: false, occ: false });
    t.ell(19, 20, 2.2, 2.6, R.cream, { shade: 2, spec: false, occ: false });
    t.vline(16, 15, 11, R.cream, 3);
    return;
  }
  eyeBig(t, 11, 8);
  eyeBig(t, 17, 8);
  smile(t, 14, 15, 5);
};

// ASTRA R8: new IDs only; all earlier painters keep their authored volumes.
const ehcorn: Paint = (t, back) => {
  paws(t, 16, 4.6, 2.6, R.bark, { shade: 1 });
  // The nut, and the dent the boot left in it — which it apologized for.
  t.ell(16, 20, 8, 8.5, R.orange);
  for (const [x, y] of [[21, 15], [22, 16], [23, 17], [22, 18], [21, 17]] as const) t.darken(x, y, 2);
  t.px(20, 14, R.orange, 0);
  t.cap(9, 19, 6, 23, 2.2, R.bark, { shade: 1 });
  t.cap(23, 19, 26, 23, 2.2, R.bark, { shade: 1 });
  // The cupule: overlapping scale barrels instead of one flat brown disc with four dots on it.
  // R.bark, not R.brown: brown shares three steps with the nut's orange and the cap vanished
  // into it. Bark puts a dark beret over a light nut, which is the whole read at 16px.
  t.ell(16, 11.5, 9, 4.5, R.bark);
  // The cap's own lower lip, so it reads as sitting on the nut rather than painted onto it.
  for (let x = 8; x <= 24; x++) {
    const y = Math.round(11.5 + 4.5 * Math.sqrt(Math.max(0, 1 - ((x - 16) / 9) ** 2)));
    t.px(x, y, R.bark, 0);
    t.darken(x, y + 1, 2);
  }
  for (const [x, y] of [[9, 12], [13, 11.5], [17, 11.5], [21, 12], [11, 9.5], [15, 9], [19, 9.5]] as const) {
    t.ell(x, y, 2.2, 1.7, R.bark, { spec: false, shade: 1 });
    t.px(x - 1, y - 1, R.bark, 0);
    t.darken(x + 1, y + 1);
  }
  t.cap(16, 9, 17, 4, 1.4, R.bark);
  blade(t, 18, 5.5, 24, 3, 2.4, R.green);
  if (back) {
    // From behind: the cupule's woven underside, the seam it will split along, and the boot tread
    // still printed on it — the apology went to the boot, the tread stayed here.
    t.vline(16, 16, 11, R.orange, 3);
    t.ring(16, 11.5, 6.5, 3, R.bark, 3);
    for (const x of [11, 15, 19]) t.vline(x, 9, 6, R.bark, 3);
    // The tread, as a block of lugs rather than three loose scan lines.
    for (const y of [17, 21]) for (const x of [12, 15, 18]) {
      t.rect(x, y, 2, 3, R.orange, 3);
      t.px(x, y, R.orange, 2);
    }
    t.px(9, 16, R.bark, 0);
    return;
  }
  eyeBig(t, 10, 15);
  eyeBig(t, 18, 15);
  nose(t, 15, 20);
  smile(t, 14, 21, 5);
};

const ehvergreen: Paint = (t, back) => {
  // Every branch has a reply. Three needled tiers, each hemmed with alternating lit tips and
  // shadow notches so they stay countable instead of melting into one green cone, and a reply
  // bubble hung off each branch.
  for (const x of [11, 21]) {
    t.cap(x, 25, x, 28.4, 2.4, R.bark, { shade: 1, spec: false });
    t.hline(x - 3, 29, 6, R.bark, 2);
    t.px(x - 3, 29, R.bark, 1);
  }
  t.ell(16, 24, 5.5, 5, R.bark);
  t.cap(10, 20, 4, 17, 2, R.bark, { shade: 1 });
  t.cap(22, 20, 28, 17, 2, R.bark, { shade: 1 });
  for (const [cy, rx, ry] of [[20, 11, 4.5], [14.5, 8.5, 4], [9, 6, 3.6]] as const) {
    t.ell(16, cy, rx, ry, R.green);
    for (let x = Math.ceil(16 - rx); x <= Math.floor(16 + rx); x += 2) {
      const y = Math.round(cy + ry * Math.sqrt(Math.max(0, 1 - ((x - 16) / rx) ** 2)));
      t.px(x, y, R.green, 0);
      t.px(x + 1, y, R.moss, 2);
      t.darken(x + 1, y + 1, 2);
    }
  }
  // Snow on the crown, and the spire under it.
  t.cap(16, 6, 16, 2.5, 1.4, R.bark);
  t.ell(16, 2.5, 2.6, 1.8, R.paper, { spec: false });
  t.px(15, 1, WHITE);
  for (const [x, y] of [[12, 7], [19, 7.5], [14, 12], [20, 12.5]] as const) {
    t.hline(x, y, 2, R.paper, 1);
    t.px(x, y - 1, R.paper, 0);
  }
  if (back) {
    // From behind: the trunk running the whole height, the whorl each tier springs from, and the
    // bubbles edge-on — the canopy calls that consensus.
    t.vline(16, 6, 20, R.bark, 3);
    t.px(15, 7, R.bark, 0);
    for (const cy of [20, 14.5, 9]) t.ring(16, cy, 4, 2, R.moss, 3);
    for (const y of [18, 22]) t.hline(9, y, 14, R.moss, 3);
    t.slab(4, 14, 1.6, 2.6, 0.9, R.paper, { shade: 1 });
    t.slab(28, 13, 1.4, 2.4, 0.8, R.paper, { shade: 1 });
    return;
  }
  eyeBig(t, 11, 16);
  eyeBig(t, 18, 16);
  smile(t, 14, 21, 5);
  bubble(t, 4, 14, 3.2, 2.4, 1);
  bubble(t, 28, 13, 3, 2.2, -1);
};

const snoozlet: Paint = (t, back) => {
  paws(t, 16, 3, 2, R.violet);
  t.ell(9, 20, 6, 7, R.paper);
  t.ell(23, 20, 6, 7, R.paper);
  t.ell(16, 22, 4, 6, R.violet);
  t.cap(13, 10, 10, 6, 1, R.violet);
  t.cap(19, 10, 22, 6, 1, R.violet);
  t.ell(16, 12, 6, 5, R.cream);
  for (const x of [6, 25]) t.vline(x, 18, 5, R.paper, 2);
  if (back) {
    // Pillows are pillowcases. The printed side is the front; the back is the plain half, piped
    // all the way round a seam, with the stuffing pressing out either side of it.
    for (const [cx, sh] of [[9, 1], [23, 2]] as const) {
      t.ell(cx, 20, 3.2, 5.4, R.paper, { shade: sh, spec: false, occ: false });
      t.vline(cx, 15, 11, R.paper, 3);
      t.px(cx - 1, 16, R.paper, 0);
      arc(t, cx, 20, 3.6, 5.8, 0.3, Math.PI - 0.3, R.paper, 3);
    }
    // And the moth itself: the thorax tuft the front hides behind a face, and three abdomen
    // segments that were one flat rule down the middle.
    t.ell(16, 17, 3.4, 2.6, R.violet, { shade: 1, spec: false });
    for (const y of [20, 23, 26]) t.cap(13, y, 19, y, 1.2, R.violet, { shade: 2, spec: false });
    t.vline(16, 18, 9, R.violet, 3);
    return;
  }
  eyeSleepy(t, 11, 10);
  eyeSleepy(t, 18, 10);
  t.ell(16, 16, 1.5, 1.5, INK, { flat: 1, occ: false });
};

const napocalypse: Paint = (t, back) => {
  // The nightcap droops to one side, so it droops to the other when the toy turns round.
  const m = (x: number) => (back ? 31 - x : x);
  paws(t, 16, 4, 2.6, R.violet);
  t.ell(7, 15, 5, 10, R.paper);
  t.ell(25, 15, 5, 10, R.paper);
  t.ell(10, 23, 6, 5, R.cream);
  t.ell(22, 23, 6, 5, R.cream);
  for (const x of [5, 25]) t.vline(x, 11, 11, R.paper, 2);
  t.ell(16, 21, 5.5, 8, R.violet);
  t.ell(16, 11, 7, 6, R.cream);
  t.poly([[m(10), 7], [m(16), 1], [m(24), 6], [m(17), 5]], R.violet, { grad: true });
  t.ell(m(24), 6, 2, 2, R.paper);
  if (back) {
    // A duvet has a back: the cover's plain half, piped down a seam the length of each wing,
    // with the stuffing pressing out either side of it. Filed as a weather warning either way.
    for (const [cx, sh] of [[7, 1], [25, 2]] as const) {
      t.ell(cx, 13, 2.6, 4.6, R.paper, { shade: sh, spec: false, occ: false });
      t.vline(cx, 10, 7, R.paper, 3);
      t.px(cx - 1, 11, R.paper, 0);
    }
    for (const [cx, sh] of [[8, 1], [24, 2]] as const) {
      t.ell(cx, 23, 2.6, 3.4, R.cream, { shade: sh, spec: false, occ: false });
      t.vline(cx, 20, 7, R.cream, 3);
      t.px(cx - 1, 21, R.cream, 0);
    }
    // The thorax tuft the front hides behind a face, and the abdomen's three segments — which
    // were one flat rule down the middle.
    t.ell(16, 16, 4, 3, R.violet, { shade: 1, spec: false });
    for (const y of [20, 24, 27]) t.cap(12, y, 20, y, 1.3, R.violet, { shade: 2, spec: false });
    t.vline(16, 17, 11, R.violet, 3);
    return;
  }
  eyeSleepy(t, 10, 10);
  eyeSleepy(t, 19, 10);
  t.ell(16, 16, 2, 2, INK, { flat: 1, occ: false });
};

const coneling: Paint = (t, back) => {
  for (const x of [7, 25]) {
    t.cap(x, 23, x - 2, 28, 1.3, R.slate);
    t.cap(x, 20, x - 3, 24, 1, R.slate);
  }
  paws(t, 16, 4, 2, R.slate);
  t.ell(16, 26, 11, 2.5, R.orange);
  t.poly([[8, 25], [16, 7], [24, 25]], R.orange, { grad: true });
  t.hline(12, 17, 9, R.paper, 1);
  t.hline(11, 18, 11, R.paper, 0);
  t.cap(14, 9, 11, 4, 1, R.slate);
  t.cap(18, 9, 21, 4, 1, R.slate);
  if (back) {
    // A cone is moulded in one piece around a seam, and the seam is at the back. From behind there
    // is no face — the taper is one continuous shell with its own shade ramp, a hard moulding
    // step, and a hollow base plate. Promised one more lane in May. Still behind you.
    t.poly([[8, 25], [16, 7], [24, 25]], R.orange, { tone: 1 });
    t.ell(16, 18, 5.2, 6.4, R.orange, { shade: 2, spec: false, occ: false });
    t.vline(16, 8, 16, R.orange, 3);
    t.px(15, 10, R.orange, 0);
    t.px(15, 14, R.orange, 0);
    t.px(15, 20, R.orange, 0);
    arc(t, 16, 16, 5.4, 7.2, 0.25, Math.PI - 0.25, R.orange, 3);
    // The collar is a wrapped sleeve buckled at the nape — overlap, catch, two rivets — not the
    // front's flat tape stripe.
    t.cap(11, 18, 21, 18, 1.5, R.paper, { shade: 2, spec: false });
    t.slab(16, 17.5, 2.4, 1.8, 0.8, R.paper, { shade: 1, spec: false });
    rivet(t, 15, 17, R.ink);
    rivet(t, 17, 18, R.ink);
    t.px(13, 17, WHITE);
    // Hollow base: far lip plus moulded webs under the plate, and the outboard paw arcs the
    // front never shows.
    t.ell(16, 26, 11, 2.5, R.orange, { shade: 1, spec: false });
    arc(t, 16, 26, 10.4, 2.2, Math.PI + 0.3, Math.PI * 2 - 0.3, R.orange, 1);
    for (const dx of [-9, -6, 6, 9]) t.vline(16 + dx, 25, 3, R.orange, 3);
    for (const cx of [7, 25]) arc(t, cx, 27, 2.2, 1.4, Math.PI + 0.2, Math.PI * 2 - 0.2, R.slate, 1);
    return;
  }
  eyeDot(t, 12, 20);
  eyeDot(t, 19, 20);
  frown(t, 15, 24, 3);
};

const detourant: Paint = (t, back) => {
  for (const y of [18, 22, 26]) {
    t.cap(10, y, 3, y + 3, 1.5, R.slate);
    t.cap(22, y, 29, y + 3, 1.5, R.slate);
  }
  t.ell(16, 24, 7, 5, R.orange);
  t.ell(16, 17, 5, 5, R.slate);
  t.ell(16, 11, 6, 5, R.orange);
  t.poly([[11, 8], [16, 1], [21, 8]], R.orange, { grad: true });
  t.hline(14, 5, 5, R.paper, 0);
  // The sign was a flat sheet of `rect`; it is a bevelled panel that catches the key along its top
  // edge, which is what makes the blank reverse below read as the *same* board turned round.
  t.slab(16, 21.5, 11.5, 3, 1.2, R.paper);
  for (const x of [7, 13, 19]) {
    t.poly([[x, 19], [x + 3, 19], [x + 6, 23], [x + 3, 23]], R.orange, { tone: 1 });
  }
  // The stripes land on top of where the slab put its specular, so the board gets its gloss point
  // back by hand, on the one corner of sheet the hazard chevrons leave clear.
  t.px(6, 20, WHITE);
  if (back) {
    // From behind: the board is blank galvanised sheet on a channel strut, bolted at four corners
    // — nobody paints the back of a detour sign — and the cone shows its moulding seam.
    t.slab(16, 21.5, 11.5, 3, 1.2, R.stone, { shade: 1 });
    t.cap(6, 21, 26, 21, 0.8, R.stone, { shade: 2, spec: false });
    t.cap(16, 19, 16, 24, 1.6, R.slate, { shade: 1, spec: false });
    t.px(15, 20, R.slate, 0);
    for (const [x, y] of [[7, 20], [25, 20], [7, 23], [25, 23]] as const) rivet(t, x, y, R.stone);
    t.vline(16, 2, 3, R.orange, 3);
    t.vline(16, 6, 6, R.orange, 3);
    t.px(15, 8, R.orange, 0);
    arc(t, 16, 11, 5, 4.2, 0.3, 2.85, R.orange, 3);
    // The reflective band wraps the whole cone, so from behind it is still there — just with its
    // own shadow under it instead of a face beside it.
    t.hline(13, 6, 7, R.paper, 2);
    return;
  }
  eyeBead(t, 11, 9);
  eyeBead(t, 18, 9);
  frown(t, 14, 15, 4);
};

const syrupup: Paint = (t, back) => {
  paws(t, 16, 4.5, 2.5, R.cream);
  t.ell(16, 21, 8, 7, R.stone);
  t.ring(16, 17, 8, 7, R.slate, 0);
  t.hline(10, 25, 13, R.stone, 3);
  t.ell(16, 13, 6, 5, R.gold);
  t.cap(10, 10, 8, 17, 2, R.orange);
  t.cap(22, 10, 24, 17, 2, R.orange);
  t.ell(16, 9, 4, 2, R.gold);
  if (back) {
    // A sap bucket hangs off its spile on the front, so the back is plain galvanised shell: the
    // lap seam riveted its whole height, the bail lugs the handle pivots on, and the rolled
    // bottom rim. Overpaint the front drip and face — neither belongs on this side. The floor is
    // sticky. The floor is supposed to be sticky.
    t.ell(16, 21, 8, 7, R.stone, { shade: 1, spec: false, occ: false });
    t.ring(16, 17, 8, 7, R.slate, 1);
    t.vline(16, 17, 10, R.stone, 3);
    t.px(15, 19, R.stone, 0);
    t.px(15, 22, R.stone, 0);
    for (const y of [19, 22, 25]) rivet(t, 16, y, R.slate);
    for (const x of [10, 22]) t.slab(x, 18, 1.4, 1.2, 0.5, R.slate, { spec: false });
    // Rolled rim pulled up off the paws, plus short outboard arcs so it does not float.
    arc(t, 16, 24.2, 7.4, 2.4, 0.35, Math.PI - 0.35, R.stone, 3);
    for (const cx of [10, 22]) arc(t, cx, 26.5, 2.4, 1.3, Math.PI + 0.15, Math.PI * 2 - 0.15, R.stone, 2);
    // The sap that got out anyway, down the back where nobody wipes — not the front chest pour.
    for (const [x, y] of [[20, 18], [20, 19], [21, 20], [21, 21], [20, 22], [19, 23]] as const) t.px(x, y, R.gold, 2);
    t.px(20, 24, R.gold, 1);
    // Head from behind: flatter occiput dome, nape fold, lit lip — no muzzle.
    t.ell(16, 12.6, 5.6, 4.8, R.gold, { dome: 0.7, spec: false, occ: false });
    arc(t, 16, 13, 4.8, 3.9, 0.3, Math.PI - 0.3, R.gold, 3);
    t.cap(13, 16, 19, 16, 1.4, R.gold, { shade: 2, spec: false });
    t.hline(13, 14, 7, R.gold, 1);
    // Ear backs: cartilage ridge, no inner glow.
    for (const [ax, bx] of [[8, 10], [24, 22]] as const) {
      t.line(ax, 12, bx, 15, R.orange, 1);
      t.px(ax + Math.sign(16 - ax), 11, R.orange, 0);
    }
    // Neither pose keeps the rig's specular here — the head's contact band takes it — so the
    // reverse gets one hard gloss by hand, on the shoulder the rig aimed at.
    t.px(14, 19, WHITE);
    return;
  }
  eyeBead(t, 11, 11);
  eyeBead(t, 18, 11);
  nose(t, 15, 16);
  t.poly([[13, 21], [15, 19], [16, 21], [19, 20], [17, 24]], R.gold, { tone: 1 });
};

const sapreme: Paint = (t, back) => {
  // The tail is on one side, so it changes sides when the toy turns round.
  const m = (x: number) => (back ? 31 - x : x);
  paws(t, 16, 5.5, 3, R.gold);
  t.cap(m(25), 25, m(28), 18, 2.3, R.orange);
  t.ell(16, 22, 10, 7, R.stone);
  t.ring(16, 20, 10, 3, R.slate, 0);
  t.ell(16, 12, 7.5, 6, R.gold);
  t.cap(9, 10, 6, 17, 2.5, R.orange);
  t.cap(23, 10, 26, 17, 2.5, R.orange);
  t.poly([[10, 6], [9, 2], [14, 4], [16, 1], [18, 4], [23, 2], [22, 6]], R.red, { grad: true });
  if (back) {
    // The same bucket its kit was, grown: lap seam riveted its whole height, bail lugs, rolled
    // bottom rim. All rise, if your boots will let you. They will not.
    t.vline(16, 19, 9, R.stone, 3);
    t.px(15, 20, R.stone, 0);
    for (const y of [20, 23, 26]) rivet(t, 16, y, R.slate);
    for (const x of [10, 22]) t.slab(x, 19, 1.6, 1.3, 0.5, R.slate, { spec: false });
    arc(t, 16, 21.5, 9.2, 3.9, 0.35, Math.PI - 0.35, R.stone, 3);
    for (const [x, y] of [[20, 20], [20, 21], [21, 22], [21, 23], [20, 24]] as const) t.px(x, y, R.gold, 2);
    t.px(20, 25, R.gold, 1);
    // A crown from behind is its band and the backs of the points: no jewels, just solder.
    t.hline(10, 5, 12, R.red, 2);
    for (const x of [11, 16, 21]) t.vline(x, 3, 3, R.red, 1);
    // The head keeps its own gloss — the occiput shadow, nape fold and lip all sit clear of it.
    arc(t, 16, 12, 5.8, 4.6, 0.35, Math.PI - 0.35, R.gold, 3);
    t.cap(12, 15, 20, 15, 1.5, R.gold, { shade: 2, spec: false });
    t.hline(12, 13, 9, R.gold, 1);
    return;
  }
  eyeBig(t, 10, 9);
  eyeBig(t, 18, 9);
  nose(t, 15, 15);
  smile(t, 14, 17, 5);
  t.poly([[12, 23], [15, 20], [16, 23], [20, 21], [18, 26], [14, 26]], R.gold, { grad: true });
};

const bufferret: Paint = (t, back) => {
  // This toy is built off-centre — the ring on one side, the ferret leaning into it — so turning
  // it round turns the whole pose, spinner included. Mirroring x also mirrors the arc's sweep.
  const m = (x: number) => (back ? 31 - x : x);
  arc(t, m(24), 20, 5, 7, back ? Math.PI * -0.6 : 0.1, back ? Math.PI - 0.1 : Math.PI * 1.6, R.blue, 1);
  t.ell(m(27), 16, 1.5, 1.5, R.ice);
  paws(t, m(14), 4, 2.4, R.navy);
  t.cap(m(19), 24, m(12), 17, 5, R.navy);
  t.ell(m(11), 11, 6, 6, R.cream);
  t.ell(m(7), 6, 2.5, 2.5, R.navy);
  t.ell(m(15), 6, 2.5, 2.5, R.navy);
  if (back) {
    // Both ears show their outsides — no lining, a shaded outer and a lit ridge — over a nape
    // fold, a spine, two haunches and the tail laid along the back. Still ninety-nine percent.
    for (const cx of [16, 24]) {
      t.ell(cx, 6, 2, 2, R.navy, { shade: 1, spec: false, occ: false });
      t.px(cx - 1, 5, R.navy, 0);
    }
    arc(t, 20, 11, 4.6, 3.8, 0.35, Math.PI - 0.35, R.cream, 3);
    t.cap(17, 15, 23, 15, 1.4, R.cream, { shade: 2, spec: false });
    t.hline(17, 13, 7, R.cream, 1);
    t.ell(14, 22, 2.6, 2.4, R.navy, { shade: 1, spec: false, occ: false });
    t.ell(17, 19, 2.6, 2.4, R.navy, { shade: 2, spec: false, occ: false });
    t.line(13, 23, 18, 18, R.navy, 3);
    t.cap(13, 25, 11, 21, 1.4, R.navy, { shade: 1, spec: false });
    // The tail's contact band takes the body's one specular; it goes back on the same pixel.
    t.px(13, 20, WHITE);
    return;
  }
  eyeBead(t, 6, 10);
  eyeBead(t, 13, 10);
  nose(t, 10, 15);
  for (const x of [12, 15, 18]) t.px(x, 23, R.ice, 0);
};

const lagomorph: Paint = (t, back) => {
  // Off-centre like its kit: the ring on one side, the packets streaking past on the other. The
  // whole pose turns, and the ears' blue lining is a lining — it does not show from behind.
  const m = (x: number) => (back ? 31 - x : x);
  arc(t, m(25), 21, 4, 5, back ? Math.PI * -0.8 : 0.4, back ? Math.PI - 0.4 : Math.PI * 1.8, R.blue, 0);
  paws(t, m(15), 5, 3.5, R.navy);
  t.ell(m(15), 23, 8, 6, R.navy);
  t.cap(m(10), 11, m(8), 3, 2.5, R.cream);
  t.cap(m(19), 11, m(22), 3, 2.5, R.cream);
  t.cap(m(10), 9, m(8), 4, 1, back ? R.cream : R.blue, { spec: false });
  t.cap(m(19), 9, m(22), 4, 1, back ? R.cream : R.blue, { spec: false });
  t.ell(m(15), 14, 7, 6, R.cream);
  t.hline(back ? 26 : 2, 17, 4, R.blue, 0);
  t.hline(back ? 26 : 1, 21, 5, R.blue, 1);
  if (back) {
    // The ears loaded first, so from behind they are the only part fully rendered. The rabbit is
    // still arriving: occiput shadow, nape fold, spine, two haunches, then the scut on top.
    arc(t, 16, 14, 5.6, 4.6, 0.35, Math.PI - 0.35, R.cream, 3);
    t.cap(12, 18, 20, 18, 1.6, R.cream, { shade: 2, spec: false });
    t.hline(12, 16, 9, R.cream, 1);
    t.ell(11, 24, 2.6, 2.8, R.navy, { shade: 1, spec: false, occ: false });
    t.ell(21, 24, 2.6, 2.8, R.navy, { shade: 2, spec: false, occ: false });
    t.vline(16, 21, 7, R.navy, 3);
    t.ell(16, 25, 2.5, 2.5, R.paper);
    return;
  }
  eyeBig(t, 9, 11);
  eyeBig(t, 17, 11);
  nose(t, 14, 17);
  for (const x of [11, 15, 19]) t.rect(x, 23, 2, 2, R.ice, 0);
};

// R10 additions use the existing toy rig; all earlier painters remain unchanged.
const paintHonk = (grown: boolean): Paint => (t, back) => {
  const wing = grown ? R.navy : R.cream;
  paws(t, 16, 5, 3, R.orange);
  t.ell(16, 21, grown ? 10 : 8, 7, R.paper);
  t.ell(8, 21, 3, grown ? 7 : 5, wing);
  t.ell(24, 21, 3, grown ? 7 : 5, wing);
  t.cap(16, 18, 16, 10, grown ? 5 : 4, R.paper);
  t.ell(16, 10, grown ? 7 : 6, 6, R.paper);
  if (grown) {
    t.cap(23, 7, 26, 10, 1.5, R.slate);
    t.cap(26, 10, 25, 15, 1, R.slate);
    t.ell(24, 15, 1.5, 1.5, R.blue);
  }
  if (back) {
    t.ell(16, 23, 3, 3, R.cream);
    t.hline(12, 10, 8, R.cream, 2);
    return;
  }
  eyeBead(t, 11, 8); eyeBead(t, 19, 8);
  t.ell(16, 14, 4, 2, R.orange);
  t.hline(13, 14, 6, R.orange, 3);
  t.rect(13, 21, 6, 5, R.blue, 2);
  t.hline(14, 22, 4, R.paper, 0);
};
const honklet = paintHonk(false), honkference = paintHonk(true);

const paintChill = (grown: boolean): Paint => (t, back) => {
  t.ell(16, 26, grown ? 12 : 10, 4, R.ice);
  t.cap(8, 24, 4, 27, 2, R.blue);
  t.cap(24, 24, 28, 27, 2, R.blue);
  t.ell(16, 20, grown ? 10 : 8, 8, R.ice);
  t.ell(16, 12, grown ? 8 : 6.5, 7, R.ice);
  t.rect(10, grown ? 4 : 5, 12, 3, R.stone, 1);
  t.ell(16, grown ? 3 : 4, 3, 1.5, R.slate);
  if (grown) {
    t.poly([[6, 15], [2, 10], [8, 12]], R.paper, { grad: true });
    t.poly([[26, 15], [30, 10], [24, 12]], R.paper, { grad: true });
    t.cap(8, 20, 24, 20, 2, R.navy);
    t.cap(22, 20, 24, 25, 2, R.navy);
  }
  if (back) {
    t.vline(16, 10, 7, R.blue, 2);
    t.ell(16, 25, 3, 2, R.paper);
    return;
  }
  eyeSleepy(t, 10, 10); eyeSleepy(t, 19, 10);
  t.ell(16, 16, 4, 2.5, R.paper);
  nose(t, 15, 15); smile(t, 14, 17, 5);
  for (const x of [10, 22]) t.hline(x, 16, 3, R.blue, 2);
};
const chillip = paintChill(false), chillossus = paintChill(true);

const paintCrumb = (grown: boolean): Paint => (t, back) => {
  paws(t, 16, 5, 2.5, R.orange);
  if (grown) t.poly([[9, 13], [2, 26], [16, 24], [29, 26], [23, 13]], R.gold, { grad: true });
  t.ell(16, 21, grown ? 9 : 7, 7, R.slate);
  t.ell(9, 21, 3, 5, R.navy); t.ell(23, 21, 3, 5, R.navy);
  t.ell(16, 11, grown ? 7 : 6, 6, R.slate);
  if (grown) {
    t.ell(16, 5, 7, 3, R.cream);
    for (const x of [12, 16, 20]) t.px(x, 4, R.gold, 2);
  }
  if (back) {
    t.cap(16, 20, 16, 27, 3, R.navy);
    for (const x of [10, 22]) t.vline(x, 20, 4, R.slate, 3);
    return;
  }
  eyeBead(t, 11, 9); eyeBead(t, 19, 9);
  t.poly([[13, 13], [19, 13], [16, 16]], R.orange, { grad: true });
  t.ell(16, 22, 6, 4, R.gold);
  t.ell(16, 22, 2, 1.5, R.navy, { spec: false });
  for (const [x, y] of [[12,21],[16,19],[20,21],[18,24],[13,24]]) t.px(x!, y!, R.paper, 0);
};
const crumbit = paintCrumb(false), breadwinner = paintCrumb(true);

const paintPuck = (grown: boolean): Paint => (t, back) => {
  for (const x of [7, 25]) {
    t.cap(x, 22, x < 16 ? 3 : 29, 27, 2, R.slate);
    t.cap(x, 24, x < 16 ? 6 : 26, 29, 1.5, R.slate);
  }
  t.ell(16, 23, grown ? 11 : 9, 6, R.navy);
  t.cap(7, 21, 5, 14, 2, R.navy); t.cap(25, 21, 27, 14, 2, R.navy);
  t.ell(5, 12, grown ? 4 : 3, 4, R.red);
  t.ell(27, 12, grown ? 4 : 3, 4, R.red);
  t.vline(5, 8, 3, R.navy, 3); t.vline(27, 8, 3, R.navy, 3);
  t.ell(16, 16, grown ? 8 : 6, 6, R.slate);
  if (grown) {
    t.ell(16, 10, 8, 4, R.red);
    t.hline(9, 11, 14, R.paper, 1);
  }
  t.hline(9, 24, 14, R.paper, 1);
  if (back) {
    t.rect(14, 15, 4, 4, R.paper, 1);
    t.vline(16, 20, 5, R.slate, 3);
    return;
  }
  eyeBead(t, 11, 14); eyeBead(t, 19, 14);
  smile(t, 14, 19, 5);
  if (grown) t.hline(11, 17, 10, R.ice, 1);
};
const puckup = paintPuck(false), clapback = paintPuck(true);

const paintTab = (grown: boolean): Paint => (t, back) => {
  paws(t, 16, 5, 3, R.cream);
  if (grown) {
    t.poly([[7, 16], [2, 22], [8, 27], [13, 18]], R.blue, { grad: true });
    t.poly([[24, 15], [30, 21], [26, 27], [19, 19]], R.gold, { grad: true });
  }
  t.ell(16, 22, grown ? 9 : 7, 7, R.paper);
  t.poly([[8, 12], [7, 2], [13, 3], [14, 13]], R.paper, { grad: true });
  t.poly([[18, 12], [21, 1], [26, 3], [24, 14]], R.paper, { grad: true });
  t.rect(8, 3, 4, 3, R.blue, 1); t.rect(21, 2, 4, 3, R.gold, 1);
  t.ell(16, 14, 7.5, 6, R.cream);
  if (back) {
    t.ell(16, 25, 3, 2.5, R.gold);
    t.hline(12, 11, 8, R.cream, 2);
    return;
  }
  eyeBig(t, 10, 11); eyeBig(t, 18, 11);
  nose(t, 15, 17);
  t.rect(11, 21, 10, 5, R.navy, 2);
  for (const x of [12, 15, 18]) t.rect(x, 20, 2, 2, R.blue, 0);
  t.hline(12, 23, 7, R.paper, 1);
};
const tabbit = paintTab(false), tabalanche = paintTab(true);

const PAINT: Record<SpeciesId, Paint> = {
  ...(FRONTIER_PAINT as Record<FrontierId, Paint>),
  ...UNDERNET_PAINT,
  ...(SIDECHAIN_PAINT as Record<SidechainId, Paint>),
  bogfiend, shibonk, wojakle, gigachomp, laserpup, whalord, apeling, rugfox, moonling, chopperdog,
  shibonkx, bogbaron, wojaked, laserhound, rugwolf, apemajor, moonlord, chopperalpha,
  cloutling, cloutking, hopiumite, copiumax, chartgob, candlewyrm, bagsnail, diamondshell,
  fomoling, fomobeast, gasgob, slippeel, mintmoth, airdropqueen, yieldyeti,
  yapling, yapstorm, lurkit, threadmaw, ratiofin, ratioking, scrollmite, doomwyrm,
  finekit, kilnmutt, npcat, lorelord,
  gatekit, gatewyrm, glazeling, glazewyrm, capling, nocapra, grassnudge, lawnlord,
  seethtoad, maldmaw, deluling, delulord,
  vibeling, vibewyrm, draftkit, postwyrm, spoilpup, leakmaw,
  queueling, linebaron, receiptling, auditlord, clipfinch, cliplord,
  toquelin, tuquelord, poutkit, gravylord, ghostkit, ghostwyrm,
  crashpup, crashmaw, sauceling, saucelord, pingling, pinglord,
  slayling, slaylord, cookling, cookwyrm, midling, midbaron,
  lockkit, locklord, depkit, depbaron,
  ehcorn, ehvergreen, snoozlet, napocalypse, coneling, detourant,
  syrupup, sapreme, bufferret, lagomorph,
  honklet, honkference, chillip, chillossus, crumbit, breadwinner,
  puckup, clapback, tabbit, tabalanche,
};

const cache = new Map<string, ImageData>();

/**
 * Creature frame. `scale` 1 is the 32×32 design grid (icons, compact lists); 2 and 3 render the
 * same painter supersampled at 64×64 / 96×96 for battle, files and the title stage.
 */
export function creatureSprite(id: SpeciesId, pose: Pose, scale = 1, shiny = false): ImageData {
  if (shiny) return shinyOf(`${id}:${pose}:${scale}`, id, creatureSprite(id, pose, scale));
  const key = `${id}:${pose}:${scale}`;
  const hit = cache.get(key);
  if (hit) return hit;
  const t = new Toy(scale);
  const paint = PAINT[id];
  if (paint) paint(t, pose === "back");
  const img = t.toImage();
  cache.set(key, img);
  return img;
}

const INK_KEY = "26,20,48";
const WHITE_KEY = "255,255,255";

/**
 * 16×16 party / index icon: each 2×2 block keeps the outline where the silhouette edge runs,
 * keeps ink where the block is all ink (eyes, nose), and otherwise takes the fill colour
 * closest to the block's mean so faces and materials survive the downsample.
 *
 * With one exception, and it is the whole gloss: nearest-to-the-mean is by construction a
 * de-contrasting operator, so a block holding the volume's single specular against three body
 * pixels always threw the specular away — every icon in the party list arrived matte. A block
 * with no ink in it that owns a white pixel now keeps it. The white is only ever the one
 * specular the rig allows per volume, so this cannot smear: it restores exactly one hot pixel
 * per volume and nothing else. Blocks that *do* contain ink are untouched, which keeps teeth
 * and eye catches from blowing out — an eye's catch sits on its ink pupil, so the pupil still
 * wins the block and the eye still reads dark at 16px.
 */
export function creatureIcon(id: SpeciesId, shiny = false): ImageData {
  if (shiny) return shinyOf(`${id}:icon`, id, creatureIcon(id));
  const key = `${id}:icon`;
  const hit = cache.get(key);
  if (hit) return hit;
  const full = creatureSprite(id, "front");
  const img = new ImageData(16, 16);
  for (let y = 0; y < 16; y++) {
    for (let x = 0; x < 16; x++) {
      const samples: number[] = [];
      let inks = 0;
      for (let oy = 0; oy < 2; oy++) for (let ox = 0; ox < 2; ox++) {
        const o = ((y * 2 + oy) * 32 + (x * 2 + ox)) * 4;
        if (full.data[o + 3]! < 8) continue;
        samples.push(o);
        if (`${full.data[o]},${full.data[o + 1]},${full.data[o + 2]}` === INK_KEY) inks++;
      }
      if (!samples.length) continue;
      const d = (y * 16 + x) * 4;
      let pick: number;
      if (inks === samples.length || (samples.length <= 2 && inks > 0)) {
        pick = samples.find((o) => `${full.data[o]},${full.data[o + 1]},${full.data[o + 2]}` === INK_KEY)!;
      } else if (inks === 0 && samples.some((o) => `${full.data[o]},${full.data[o + 1]},${full.data[o + 2]}` === WHITE_KEY)) {
        pick = samples.find((o) => `${full.data[o]},${full.data[o + 1]},${full.data[o + 2]}` === WHITE_KEY)!;
      } else {
        const fills = samples.filter((o) => `${full.data[o]},${full.data[o + 1]},${full.data[o + 2]}` !== INK_KEY);
        const mean = [0, 0, 0];
        for (const o of fills) { mean[0]! += full.data[o]!; mean[1]! += full.data[o + 1]!; mean[2]! += full.data[o + 2]!; }
        let best = fills[0]!, bestD = Infinity;
        for (const o of fills) {
          const dd = (full.data[o]! * fills.length - mean[0]!) ** 2 + (full.data[o + 1]! * fills.length - mean[1]!) ** 2 + (full.data[o + 2]! * fills.length - mean[2]!) ** 2;
          if (dd < bestD) { bestD = dd; best = o; }
        }
        pick = best;
      }
      img.data[d] = full.data[pick]!;
      img.data[d + 1] = full.data[pick + 1]!;
      img.data[d + 2] = full.data[pick + 2]!;
      img.data[d + 3] = 255;
    }
  }
  cache.set(key, img);
  return img;
}

// ------------------------------------------------------------------------------------------------
// Shiny variants
// ------------------------------------------------------------------------------------------------

/** Each species shifts to its own alternate hue; ink outlines, eye catches and whites stay put. */
export function shinyShift(id: SpeciesId): number {
  let h = 0;
  for (let i = 0; i < id.length; i++) h = (h * 31 + id.charCodeAt(i)) >>> 0;
  return [150, 180, 210, 120, 240][h % 5]!;
}

function shinyOf(key: string, id: SpeciesId, base: ImageData): ImageData {
  const k = `${key}:shiny`;
  const hit = cache.get(k);
  if (hit) return hit;
  const img = new ImageData(base.width, base.height);
  img.data.set(base.data);
  const d = img.data;
  const shift = shinyShift(id);
  for (let o = 0; o < d.length; o += 4) {
    if (!d[o + 3]) continue;
    const r = d[o]! / 255, g = d[o + 1]! / 255, b = d[o + 2]! / 255;
    const max = Math.max(r, g, b), min = Math.min(r, g, b);
    const l = (max + min) / 2;
    if (l < 0.14 || (max - min < 0.06 && l > 0.88)) continue;
    const dl = max - min;
    let s = dl === 0 ? 0 : l > 0.5 ? dl / (2 - max - min) : dl / (max + min);
    let h = dl === 0 ? 0 : max === r ? (g - b) / dl + (g < b ? 6 : 0) : max === g ? (b - r) / dl + 2 : (r - g) / dl + 4;
    h = (h * 60 + shift) % 360;
    s = Math.min(1, s * 1.12 + 0.06);
    const c = (1 - Math.abs(2 * l - 1)) * s, x = c * (1 - Math.abs(((h / 60) % 2) - 1)), m = l - c / 2;
    const [rr, gg, bb] = h < 60 ? [c, x, 0] : h < 120 ? [x, c, 0] : h < 180 ? [0, c, x] : h < 240 ? [0, x, c] : h < 300 ? [x, 0, c] : [c, 0, x];
    d[o] = Math.round((rr + m) * 255);
    d[o + 1] = Math.round((gg + m) * 255);
    d[o + 2] = Math.round((bb + m) * 255);
  }
  cache.set(k, img);
  return img;
}
